import React, { useState, useEffect, useMemo, Fragment } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { precoUnitarioB2B } from './precoCliente'
import { useAuth } from './AuthContext'
import { LOGNOW_MORADA, SLOTS_TRANSITO} from './constants'
import { CopyButton } from './CopyButton'
import { EdicaoMassaPrecos } from './EdicaoMassaPrecos'
import {
  ShoppingCart, Package, Search, X, CheckCircle,
  AlertCircle, ChevronRight, Loader, Tag, Hash,
  Plus, User, Edit2, Eye, EyeOff, Save, Link, Upload,
  Clock, CreditCard, Truck, Info, Sparkles, Droplets, Trash2, Copy as CopyIcon, Heart, ArrowDownUp, Send,
  AlertTriangle, Download, Globe2,
} from 'lucide-react'

import { C } from './theme'
import { PORTES_COBRADOS_FALLBACK, PORTES_CUSTO_FALLBACK } from './portes'
import { guardarCarrinho } from './carrinhoStorage'


const IVA = 0.23
const fmt2 = (n) => Number(n || 0).toFixed(2)

// Formato/tamanho do produto ("... Eau de Parfum 100ml" → "100 ml").
// O tamanho vem quase sempre no FIM do nome — que é exactamente o que o corte
// do card come primeiro — por isso extraímos e mostramos numa etiqueta própria.
// Lookahead (não \b) para não apanhar "5 lâminas" como "5 L" — acentos não são \w
const RE_FORMATO = /(?:(\d+)\s*[x×]\s*)?(\d+(?:[.,]\d+)?)\s*(ml|mls|cl|lt|litros?|l|gr|gramas?|g|kg|oz)(?![\p{L}\p{N}])/giu
function extrairFormato(nome) {
  const s = String(nome || '')
  RE_FORMATO.lastIndex = 0
  let m
  const achados = []
  while ((m = RE_FORMATO.exec(s)) !== null) achados.push(m)
  if (!achados.length) return null
  // Última ocorrência, mas em volume quando existe ("100ml/3.4oz" → 100 ml)
  const volumes = achados.filter(a => /^(ml|mls|cl|lt|l|litros?)$/i.test(a[3]))
  const ultimo = (volumes.length ? volumes : achados).slice(-1)[0]
  const [, pack, qtd, unidadeRaw] = ultimo
  const u = unidadeRaw.toLowerCase()
  const unidade =
    u === 'mls' ? 'ml' :
    (u === 'l' || u === 'lt' || u.startsWith('litro')) ? 'L' :
    (u === 'gr' || u.startsWith('grama')) ? 'g' :
    u
  const valor = qtd.replace(',', '.').replace(/\.0+$/, '')
  return `${pack ? `${pack}×` : ''}${valor} ${unidade}`
}

// REGRA DEFINITIVA — formula unica (desde 20/07/2026), sobre o preço FORNECEDOR BRUTO:
//   PVP = fornecedor_bruto × 1.20 + 1€ portes. Se preço termina em .00 → +0.05€.
// Substituiu os escalões antigos (+5€ fixo / +20% / +15%) — esses já não se aplicam.
// O desconto 10% do fornecedor afecta apenas o LUCRO REAL interno — NAO entra no preco.
// Aplica-se a produtos AUTO. Manuais: preco inserido + 1€ portes.
// Quantos dias um artigo continua a aparecer no separador "Novos" depois de ter
// sido anunciado ao cliente.
// Quem nunca ca esteve nao tem "ultima visita" para comparar. Sem um limite,
// o catalogo inteiro aparecia marcado como novo no primeiro dia — 800
// distintivos nao sao um aviso, sao papel de parede. Ve o que quem chega veria:
// os ultimos dias.
const DIAS_NOVO_SEM_HISTORICO = 3
// E quem esteve tres meses fora tambem nao precisa de ver o catalogo todo
// marcado. Trinta dias e o que ainda se percebe como "o que me escapou".
const DIAS_NOVO_MAX = 30
const PORTES_UN_FALLBACK = PORTES_COBRADOS_FALLBACK
const precoVendaComPortes = (fornecedorNum, portesUn) => {
  const f = Number(fornecedorNum || 0)
  const portes = Number(portesUn ?? PORTES_UN_FALLBACK)
  if (f <= 0) return 0
  let preco = f * 1.20 + portes
  preco = Math.round(preco * 100) / 100
  const cents = Math.round((preco - Math.floor(preco)) * 100)
  if (cents === 0) preco = Math.round((preco + 0.05) * 100) / 100
  return preco
}
// Inverso: dado um preco final, calcula o preco fornecedor.
const fornecedorEstimadoDoPreco = (preco, portesUn) => {
  const p = parseFloat(preco)
  if (isNaN(p) || p <= 0) return 0
  const portes = Number(portesUn ?? PORTES_UN_FALLBACK)
  // Tenta com e sem o arredondamento estetico +0.05
  const forn = Math.round(((p - portes) / 1.20) * 100) / 100
  if (forn > 0) return forn
  const fornArr = Math.round(((p - 0.05 - portes) / 1.20) * 100) / 100
  return Math.max(0, fornArr)
}
// Helper de display: descreve o lucro/markup aplicado a um dado fornecedor
// Descreve a regra de preco aplicada. Recebe os portes cobrados porque nem todos
// os fornecedores os tem — no Naypes sao zero, e escrever "+1€ portes" seria
// descrever uma conta que nao foi feita.
// Escapa um campo para CSV. Mesma convencao do resto do projecto (Analise de
// Compras, Analise de Factura): separador ';', que e o que o Excel portugues
// espera sem perguntar nada ao utilizador.
const csvEscape = (v) => {
  const t = String(v ?? '')
  return /[;"\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t
}

const descreverLucro = (portes) => (portes > 0 ? `+20% + ${portes.toFixed(2)}€ portes` : '+20%')

// Icone por categoria. As duas antigas mantem o que sempre tiveram; as novas
// levam um generico. Uma categoria criada na BD aparece na mesma, so sem icone
// proprio — nao ha nada a fazer no codigo para ela funcionar.
const ICONE_CATEGORIA = {
  perfumes:   <Sparkles size={13} />,
  cosmeticos: <Droplets size={13} />,
}

const EMPTY_PROD = {
  Nome: '', EAN: '', preco_base: '', preco: '', preco_iva: '',
  preco_manual: false, categoria: '',
  Stock_Atual: 0, visivel_catalogo: true, sinal_flexivel: false, Email_Cliente: '', foto_url: '',
  Vendas_Mensais: 0, vendas_mensais_manual: false,
  Lead_Time: 25, Objetivo_Cobertura: 30,
  ...Object.fromEntries(SLOTS_TRANSITO.flatMap(n => [[`Transito${n}_Qtd`, 0], [`Transito${n}_Data`, '']])),
  Data_Contagem: new Date().toISOString().split('T')[0],
  qtd_minima_compra: 10,
  preco_mercado: '',
  preco_custo: '',
  notas_admin: '', desconto_supplier_pct: '',
}

export default function Catalogo({ viewAsEmail: viewAsEmailProp } = {}) {
  const { user, perfil, isAdmin, encomendarComoEmail, setEncomendarComoEmail } = useAuth()
  // Quando admin activa "encomendar como cliente", override do view_as da URL.
  // Toda a logica existente que reage a viewAsEmail (precos, descontos, favoritos, etc.) passa a comportar-se como esse cliente.
  const viewAsEmail = encomendarComoEmail || viewAsEmailProp
  const [produtos, setProdutos] = useState([])
  // Categorias vindas da BD (tabela `categorias`), nao cravadas no codigo. Os
  // separadores do catalogo nascem daqui cruzado com o que existe nos produtos.
  const [categorias, setCategorias] = useState([])
  // Fornecedores: prazo de entrega e nome. O prazo deixa de estar cravado em 25
  // dias e passa a vir daqui — o Plus Skill demora 25, o Naypes 2.
  const [fornecedores, setFornecedores] = useState([])
  // ─── Portes que PAGAMOS, por unidade, no fornecedor deste artigo ────────────
  // Era uma constante global de 0,75 €, como se todos os fornecedores fossem o
  // Plus Skill. No Naypes os portes são grátis acima de 300 € (e as encomendas
  // são sempre acima), portanto somar-lhe 0,75 € inventava um custo que não
  // existe — e fazia um artigo com 20% de margem aparecer como prejuízo.
  // Sem fornecedor conhecido mantém-se o valor de sempre.
  // Portes COBRADOS (embutidos no preco de venda) do fornecedor deste artigo.
  // No Naypes sao zero: o preco e custo x 1.20 e mais nada. Sem isto os textos
  // do formulario explicavam uma conta com "+1,00 de portes" que nao bate com o
  // preco ao lado — e quem le fica sem saber em qual acreditar.
  // ─── Quantidade minima de encomenda ─────────────────────────────────────────
  // O minimo do perfil do cliente e uma politica comercial nossa — 10 unidades
  // no Plus Skill. Mas ha fornecedores em que o minimo e uma restricao real
  // deles, que vem no ficheiro artigo a artigo: uns vendem a unidade, outros so
  // em caixas de 6. Nesses, impor as 10 do perfil obriga o cliente a levar dez
  // de uma coisa que se vende a uma.
  //
  // O fornecedor diz se a politica do cliente se aplica. Quando nao se aplica,
  // manda o minimo do artigo, que e o unico que o fornecedor consegue cumprir.
  const qtdMinimaDoProduto = (p) => {
    const f = fornecedores.find(x => x.id === p?.fornecedor_id)
    const doArtigo = Number(p?.qtd_minima_compra) || 0
    if (f && f.aplica_minimo_do_cliente === false) {
      // Piso do fornecedor: um minimo nosso, por baixo do dele. Serve para nao
      // se venderem unidades soltas quando o fornecedor as permite — mas nunca
      // por baixo do que ele exige, senao a encomenda era recusada na compra.
      const piso = Number(f.qtd_min_artigo) || 1
      return Math.max(doArtigo || 1, piso)
    }
    return Number(perfilEfectivo?.qtd_minima_compra) || doArtigo || 10
  }
  const portesCobradosDoProduto = (p) => {
    const f = fornecedores.find(x => x.id === p?.fornecedor_id)
    return f?.portes_cobrados_por_unidade != null
      ? Number(f.portes_cobrados_por_unidade)
      : portesUn
  }
  const portesCustoDoProduto = (p) => {
    const f = fornecedores.find(x => x.id === p?.fornecedor_id)
    return f?.portes_custo_por_unidade != null
      ? Number(f.portes_custo_por_unidade)
      : PORTES_CUSTO_FALLBACK
  }
  // Filtro de 2.o nivel dentro de uma categoria (ex: Perfumes > Mulher).
  const [subFiltro, setSubFiltro] = useState('')
  // Quantos cartoes se desenham. Buscamos todos os produtos (os filtros e a
  // pesquisa precisam do conjunto inteiro), mas desenhar 2.652 cartoes de uma
  // vez punha o browser de rastos — sobretudo em telemovel, que e onde os
  // clientes estao. Mostra-se um lote e ha um botao para mais.
  const LOTE_CARTOES = 60
  const [quantosMostrar, setQuantosMostrar] = useState(LOTE_CARTOES)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [abaCategoria, setAbaCategoria] = useState('todos')
  // PRAZO DE ENTREGA — um eixo diferente do da categoria.
  //
  // Pedido por um cliente: "para poder olhar de formas diferentes para produtos
  // que demoram 25 dias e para produtos que demoram 2 dias a chegar". Sao duas
  // decisoes de compra distintas — repor stock que esta a acabar, ou planear com
  // um mes de antecedencia — e ate aqui viam-se misturadas na mesma grelha.
  //
  // Os prazos saem dos artigos e nao de uma lista escrita a mao: hoje sao 2, 3 e
  // 25 dias, e se um fornecedor mudar o prazo na ficha os botoes acompanham.
  //
  // Escolher um prazo tambem garante um so fornecedor, que e regra do sistema
  // para o carrinho. O cliente nunca ve nomes de fornecedores — ve prazos, que e
  // como pensa no assunto.
  const [prazoFiltro, setPrazoFiltro] = useState(null)
  const [favoritos, setFavoritos] = useState(new Set()) // produto_id's favoritados pelo cliente
  // SEGUIR PRECOS A PARTIR DO CATALOGO (pedido do Ruben, 30/09/2026).
  // "ou um botao dentro de cada produto no catalogo '+pedir para seguir'".
  // Tinha razao: obrigar a ir ao separador e repetir a pesquisa do artigo que
  // ja esta a olhar e trabalho a mais por nada.
  const [seguidos, setSeguidos] = useState(new Set())   // produto_id -> ja na lista
  const [podeSeguir, setPodeSeguir] = useState(false)
  const [aSeguir, setASeguir] = useState(null)          // produto_id em curso
  const [frequenciaCompra, setFrequenciaCompra] = useState(new Map()) // produto_nome → count
  const [ordenacao, setOrdenacao] = useState('frequencia') // 'frequencia' | 'nome' (cliente)
  const [nib, setNib] = useState('')
  const [iban, setIban] = useState('')
  const [bancoNome, setBancoNome] = useState('')
  const [bancoTitular, setBancoTitular] = useState('')
  const [portesUn, setPortesUn] = useState(PORTES_UN_FALLBACK)
  // Incentivo Volume: 5% se Standard (desconto_percentagem=0) e total c/IVA >= 1500
  const [incentivoConfig, setIncentivoConfig] = useState({ ativo: false, threshold: 1500, pct: 5 })
  // Perfil efectivo: quando admin esta em "ver como cliente", carrega o perfil do cliente alvo
  // para que qtd_minima, desconto, portes e tipo_cliente reflictam o do cliente, nao o do admin.
  const [perfilCliente, setPerfilCliente] = useState(null)
  const perfilEfectivo = perfilCliente || perfil
  useEffect(() => {
    if (!viewAsEmail) { setPerfilCliente(null); return }
    let cancelado = false
    ;(async () => {
      const { data } = await supabase.from('perfis').select('*').eq('email', viewAsEmail).maybeSingle()
      if (!cancelado) setPerfilCliente(data || null)
    })()
    return () => { cancelado = true }
  }, [viewAsEmail])
  // Diferencial de portes do cliente vs default embutido nos preços guardados.
  // Clientes premium (0.75) têm diferencial 0; standard (1.00) têm +0.25/un.
  // Admin vê preço base (sem diferencial). Em modo "ver como cliente", aplica o do cliente.
  const portesDoCliente = perfilEfectivo?.portes_por_unidade != null ? Number(perfilEfectivo.portes_por_unidade) : 1.00
  const portesDiff = (isAdmin && !viewAsEmail) ? 0 : Math.max(0, portesDoCliente - portesUn)  // diferencial s/IVA por unidade
  // Modo "encomendar como cliente interno" (ex: Catia = mesma pessoa que admin).
  // Catalogo mostra preco_custo + portes (consumo interno, sem auto-venda ficticia).
  // Sem desconto cliente. Bloqueia adicionar se preco_custo nulo.
  const modoEncomendarInterno = isAdmin && !!encomendarComoEmail && !!perfilCliente?.cliente_interno
  // Cliente particular (B2C) vê preços com markup + portes embebido; oculta detalhe fiscal
  const isParticular = perfilEfectivo?.tipo_cliente === 'particular'
  const [error, setError] = useState('')

  // Modal criar / editar produto (admin) — unificado
  const [clientes, setClientes] = useState([])

  const [prodModal, setProdModal] = useState(null)   // null = fechado, {} = novo, {id,...} = editar
  const [prodForm, setProdForm] = useState(EMPTY_PROD)
  const [prodErro, setProdErro] = useState('')
  const [avisoEmail, setAvisoEmail] = useState(null)   // { tipo:'ok'|'erro', texto } após associar produto
  const [guardando, setGuardando] = useState(false)
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 })

  // Mini-modal para cliente/admin editar só Vendas_Mensais (sem expor outros campos sensíveis)
  const [vendasMiniProd, setVendasMiniProd] = useState(null)
  const [vendasMiniValor, setVendasMiniValor] = useState('')
  const [vendasMiniManual, setVendasMiniManual] = useState(false)
  const [vendasMiniSaving, setVendasMiniSaving] = useState(false)
  const [vendasMiniErro, setVendasMiniErro] = useState('')

  const abrirVendasMini = (p) => {
    setVendasMiniProd(p)
    setVendasMiniValor(String(p.Vendas_Mensais ?? 0))
    setVendasMiniManual(p.vendas_mensais_manual ?? false)
    setVendasMiniErro('')
  }

  const guardarVendasMini = async () => {
    if (!vendasMiniProd) return
    const num = Number(vendasMiniValor)
    if (Number.isNaN(num) || num < 0) { setVendasMiniErro('Valor inválido.'); return }
    setVendasMiniSaving(true)
    setVendasMiniErro('')
    try {
      const { error } = await supabase.from('Produtos')
        .update({ Vendas_Mensais: num, vendas_mensais_manual: !!vendasMiniManual })
        .eq('id', vendasMiniProd.id)
      if (error) throw error
      await loadCatalogo()
      setVendasMiniProd(null)
    } catch (e) {
      setVendasMiniErro('Erro ao guardar: ' + (e?.message || e))
    } finally {
      setVendasMiniSaving(false)
    }
  }

  const startDrag = (e) => {
    if (e.button !== 0) return
    const startX = e.clientX - modalPos.x
    const startY = e.clientY - modalPos.y
    const onMove = (ev) => setModalPos({ x: ev.clientX - startX, y: ev.clientY - startY })
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // Modal encomenda (cliente)
  const [notas, setNotas] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultMsg, setResultMsg] = useState({ tipo: '', texto: '' })
  const EMPTY_ENTREGA = { nome: '', morada: '', cod_postal: '', pais: 'Portugal', telefone: '', nif: '' }
  const [entrega, setEntrega] = useState(EMPTY_ENTREGA)
  const updEntrega = (k, v) => setEntrega(prev => ({ ...prev, [k]: v }))
  const [tentouSubmeter, setTentouSubmeter] = useState(false)

  // Moradas guardadas pelo cliente + escolha (default LOGNOW)
  const [moradasGuardadas, setMoradasGuardadas] = useState([])
  // moradaEscolhida: 'lognow' | 'manual' | <id da morada guardada>
  const [moradaEscolhida, setMoradaEscolhida] = useState('lognow')
  const [encomendaFinalizada, setEncomendaFinalizada] = useState(null) // { id, total, metade, tipoPagamento }
  const [tipoPagamento, setTipoPagamento] = useState('50') // '50' = 1a prestação | '100' = total upfront
  // Sinal flexível (acordo especial): percentagem que o cliente escreve. O que
  // vale é o valor em euros calculado a partir dela — ver sinalValor.
  const [sinalPct, setSinalPct] = useState('')
  // Marca o lote fixo do acordo com o fornecedor. Existe porque o pagamento nao
  // chega para distinguir: numa conta interna as duas prestacoes entram pagas e
  // o sinal_escolhido fica NULL, portanto duas encomendas do MESMO artigo, do
  // mesmo cliente, ficavam iguais — e uma delas e o compromisso mensal.
  // Só admin: o RPC ignora este campo se quem chama nao for admin.
  const [acordoFixo, setAcordoFixo] = useState(false)
  const [comprovativo, setComprovativo] = useState(null) // File
  const [uploadando, setUploadando] = useState(false)
  const [uploadMsg, setUploadMsg] = useState({ tipo: '', texto: '' })

  // Carrinho de compras
  const [carrinho, setCarrinho] = useState([])
  const [showCarrinho, setShowCarrinho] = useState(false)
  const [showEdicaoMassa, setShowEdicaoMassa] = useState(false)
  const [modalQuantidade, setModalQuantidade] = useState(null) // { produto, qtdMin }
  const [quantidadeSelecionada, setQuantidadeSelecionada] = useState(10)

  // Funções do carrinho
  const abrirModalQuantidade = (produto) => {
    const qtdMin = qtdMinimaDoProduto(produto)
    setQuantidadeSelecionada(qtdMin)
    setModalQuantidade({ produto, qtdMin })
  }

  const adicionarAoCarrinho = (produto, qtd) => {
    const qtdMin = qtdMinimaDoProduto(produto)
    const qtdFinal = Math.max(qtdMin, qtd)
    // Modo interno: bloquear se nao tiver preco_custo definido
    if (modoEncomendarInterno && (produto.preco_custo == null || Number(produto.preco_custo) <= 0)) {
      alert(`"${produto.Nome}" não tem preço de custo definido. Define-o em "Editar produto" antes de o adicionar em modo consumo interno.`)
      return
    }
    // ── Um fornecedor por encomenda ─────────────────────────────────────────
    // Cada fornecedor tem o seu prazo de entrega, a sua forma de pagamento e o
    // seu minimo. Uma encomenda com artigos de dois nao teria prazo nem
    // condicoes que se pudessem escrever — e do lado da compra terias de a
    // partir em duas na mesma. Melhor impedi-lo aqui, com uma explicacao, do
    // que deixar avancar e falhar mais a frente.
    const fornNovo = produto.fornecedor_id || null
    const fornCarrinho = carrinho.find(i => i.fornecedor_id)?.fornecedor_id || null
    if (fornNovo && fornCarrinho && fornNovo !== fornCarrinho) {
      // Descrito pelo PRAZO, nunca pelo nome. Este aviso dizia ao cliente
      // "é de X e o teu carrinho já tem artigos de Y" — entregava-lhe de
      // bandeja quem nos fornece, que e a unica coisa que ele nao pode saber.
      const prazoDe = (id) => {
        const d = Number(fornecedores.find(f => f.id === id)?.prazo_envio_dias) || 0
        return d > 0 ? `entrega em ${d} ${d === 1 ? 'dia útil' : 'dias úteis'}` : 'outro prazo de entrega'
      }
      alert(
        `"${produto.Nome}" é um artigo de ${prazoDe(fornNovo)}, e o teu carrinho já tem artigos de ${prazoDe(fornCarrinho)}.\n\n` +
        `Artigos com prazos diferentes têm condições de pagamento diferentes, ` +
        `por isso não podem ir na mesma encomenda.\n\n` +
        `Finaliza esta encomenda primeiro, ou esvazia o carrinho.`
      )
      return
    }

    // Preço base por unidade:
    // - Modo interno: preco_custo (já tem desc fornecedor) + portes AO CUSTO
    // - Particular: preco_particular (custo + markup + portes 5€)
    // - B2B: preco + diferencial portes do tier
    //
    // O interno compra ao custo, portanto os portes também vão ao custo — o que
    // pagamos ao transportador, não o euro que cobramos aos clientes. O ecrã
    // mostrava 1,00 € e a encomenda era criada com 0,75 €: vinte e cinco
    // cêntimos por unidade de diferença entre o que se via e o que se pagava.
    // Num fornecedor sem portes (Naypes) não se soma nada, porque não existem.
    const precoBase = modoEncomendarInterno
      ? Number(produto.preco_custo) + portesCustoDoProduto(produto)
      : isParticular
        ? Number(produto.preco_particular || 0)
        : Number(produto.preco) + portesDiff
    const precoComIva = precoBase * (1 + IVA)

    setCarrinho(prev => {
      const existente = prev.find(item => item.Nome === produto.Nome)
      let novoCarrinho
      if (existente) {
        novoCarrinho = prev.map(item =>
          item.Nome === produto.Nome
            ? {
                ...item,
                quantidade: item.quantidade + qtdFinal,
                preco_unitario: precoBase,
                total_sem_iva: precoBase * (item.quantidade + qtdFinal),
                total_com_iva: precoComIva * (item.quantidade + qtdFinal),
                preco_com_iva: precoComIva
              }
            : item
        )
      } else {
        novoCarrinho = [...prev, {
          ...produto,
          quantidade: qtdFinal,
          preco_unitario: precoBase,
          total_sem_iva: precoBase * qtdFinal,
          total_com_iva: precoComIva * qtdFinal,
          preco_com_iva: precoComIva,
          foto_url: produto.foto_url,
          fornecedor_id: produto.fornecedor_id || null,
        }]
      }
      return guardarCarrinho(novoCarrinho)
    })
  }

  const removerDoCarrinho = (nomeProduto) => {
    setCarrinho(prev => {
      const novoCarrinho = prev.filter(item => item.Nome !== nomeProduto)
      return guardarCarrinho(novoCarrinho)
    })
  }

  const atualizarQuantidadeCarrinho = (nomeProduto, novaQtd) => {
    const item = carrinho.find(i => i.Nome === nomeProduto)
    if (!item) return
    const qtdMin = qtdMinimaDoProduto(item)
    const qtdFinal = Math.max(qtdMin, novaQtd)
    
    setCarrinho(prev => {
      const novoCarrinho = prev.map(item =>
        item.Nome === nomeProduto
          ? {
              ...item,
              quantidade: qtdFinal,
              total_sem_iva: item.preco_unitario * qtdFinal,
              total_com_iva: item.preco_com_iva * qtdFinal
            }
          : item
      )
      return guardarCarrinho(novoCarrinho)
    })
  }

  const limparCarrinho = () => {
    setCarrinho([])
    guardarCarrinho([])
    // A marca do acordo nao pode sobreviver ao carrinho: senao a encomenda
    // seguinte, avulsa, saia marcada como sendo do lote fixo.
    setAcordoFixo(false)
  }

  const carrinhoTotalSemIva = carrinho.reduce((sum, item) => sum + item.total_sem_iva, 0)
  const carrinhoTotal = carrinho.reduce((sum, item) => sum + item.total_com_iva, 0)  // bruto c/IVA (sem desconto)
  const descontoPerfil = perfilEfectivo?.desconto_percentagem || 0

  // ─── Exportar o catálogo ────────────────────────────────────────────────────
  // Três colunas e mais nada: EAN, nome, preço. É o que o software de análise
  // do cliente precisa, e cada coluna a mais é uma que ele tem de decidir
  // ignorar.
  //
  // Exporta o que está no ecrã, não o catálogo inteiro — assim os filtros e a
  // pesquisa passam a servir para alguma coisa, e ninguém recebe 500 linhas
  // quando queria vinte.
  const fornecedorDoProduto = (p) => fornecedores.find(f => f.id === p?.fornecedor_id) || null

  // O preço que ESTE cliente paga por unidade, já com o desconto do perfil dele
  // aplicado onde se aplica. O desconto não incide sobre os portes embutidos no
  // preço — mesma regra do carrinho e da factura.
  const precoDoClienteParaExport = (p) => {
    // Os dois casos especiais ficam ca: so este ecra os tem. O caso B2B saiu
    // para `precoCliente.js` a 30/09/2026, quando o separador de precos de
    // mercado passou a precisar dele — a mesma conta em dois ficheiros e como
    // o preco de particular acabou a alternar de hora a hora.
    if (modoEncomendarInterno) return Number(p.preco_custo || 0) + portesCustoDoProduto(p)
    if (isParticular) return Number(p.preco_particular || 0)
    return precoUnitarioB2B({
      produto: p, fornecedor: fornecedorDoProduto(p), portesUn,
      // `portesDiff` ja resolve o caso do admin (vale 0) e o do cliente; passa-se
      // somado para a funcao partilhada chegar exactamente ao mesmo numero.
      perfil: { desconto_percentagem: descontoPerfil, portes_por_unidade: portesUn + portesDiff },
    })
  }

  const exportarCatalogo = () => {
    // ── Travão: o ficheiro do admin não é o ficheiro do cliente ─────────────
    // O cliente só recebe da base de dados o que é dele e o que está publicado,
    // por isso o ficheiro DELE é seguro por construção. O do admin não: a vista
    // de admin traz o catálogo inteiro, incluindo o que ainda não foi lançado e
    // os arquivados. Um ficheiro exportado daqui e reencaminhado a um cliente
    // entrega-lhe milhares de artigos por publicar, com preços.
    //
    // Não se proíbe — há razões legítimas para exportar tudo. Diz-se quantos
    // são e o que acontece se o ficheiro sair daqui.
    if (isAdmin && !viewAsEmail) {
      const porPublicar = produtosFiltrados.filter(p => !p.visivel_catalogo).length
      if (porPublicar > 0 && !window.confirm(
        `Este ficheiro leva ${produtosFiltrados.length} artigos, dos quais ${porPublicar} NÃO ESTÃO PUBLICADOS.\n\n` +
        `Se o enviares a um cliente, ele fica com o catálogo por lançar e com os preços.\n\n` +
        `Para exportar só o que está publicado, filtra primeiro por "Publicado".\n\n` +
        `Continuar mesmo assim?`
      )) return
    }
    const linhaDoProduto = (p) => [
      p.EAN ? String(p.EAN) : '',
      p.Nome || '',
      fmt2(precoDoClienteParaExport(p)).replace('.', ','),
    ].map(csvEscape).join(';')
    const linhas = produtosFiltrados.map(linhaDoProduto)

    // ── SEM NOTAS, E PARTIDO AOS 499 ──────────────────────────────────────
    //
    // O ficheiro levava ate cinco linhas de texto no fim — para quem era o
    // preco, o desconto do perfil, o de quantidade. Eram para uma pessoa ler, e
    // o ficheiro nao e lido por pessoas: vai para um software de analise que le
    // linha a linha e espera tres colunas. Ao chegar a "Precos para Diogo
    // Azevedo" encontra uma linha com um campo so, e ou recusa o ficheiro ou
    // importa lixo.
    //
    // A informacao nao se perde: o preco no ficheiro ja e o do cliente, com o
    // desconto dele aplicado, e o desconto por quantidade e sempre feito no
    // carrinho. Quem le o ficheiro so precisa do preco certo, e tem-no.
    //
    // O software tambem nao aguenta mais de 500 linhas por ficheiro. Como o
    // cabecalho ocupa uma, sao 499 produtos em cada — e o cabecalho repete-se em
    // todos, senao os ficheiros a partir do segundo chegam sem nomes de coluna.
    const CABECALHO = 'EAN;DESCRICAO;PRECO'
    const POR_FICHEIRO = 499
    const bom = '﻿'
    const dia = new Date().toISOString().slice(0, 10)
    const partes = []
    for (let i = 0; i < linhas.length; i += POR_FICHEIRO) {
      partes.push(linhas.slice(i, i + POR_FICHEIRO))
    }
    if (partes.length === 0) partes.push([])

    // ── OS ARTIGOS NOVOS, EM FICHEIRO PROPRIO ─────────────────────────────
    //
    // O cliente quer saber o que mudou desde a ultima vez. Nao da para o dizer
    // dentro do ficheiro: ele tem tres colunas porque vai para um software que
    // le linha a linha, e uma quarta coluna ou um "[NOVO]" no nome ou parte a
    // importacao ou suja os dados dele para sempre.
    //
    // Entao vai a parte. Mesmas tres colunas, mesmo formato — o software dele
    // importa-o igual — e tudo o que la esta e novo, sem precisar de marca.
    //
    // NOVO E O MESMO QUE O ECRA DIZ: o que lhe foi anunciado desde a ultima
    // visita num dia anterior. Se fosse "desde o ultimo download", o site
    // mostrava um numero e o ficheiro outro, e nenhum estaria errado — que e o
    // pior tipo de confusao para quem recebe.
    //
    // Sem novidades nao se gera nada. A ausencia do ficheiro ja e a mensagem.
    const novos = produtosFiltrados.filter(p => novosParaFicheiro.has(p.id))
    // A etiqueta do ficheiro diz DESDE QUANDO, e nao a data de hoje. E a unica
    // coisa que ele nao consegue descobrir sozinho a olhar para dentro.
    const desde = ultimoDownload ? new Date(ultimoDownload) : null
    const desdeDia = desde ? desde.toISOString().slice(0, 10) : null
    const desdeCurto = desde ? desde.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }) : null
    const partesNovas = []
    for (let i = 0; i < novos.length; i += POR_FICHEIRO) {
      partesNovas.push(novos.slice(i, i + POR_FICHEIRO).map(linhaDoProduto))
    }

    // O aviso conta os ficheiros TODOS. Contava so os do catalogo, e com o dos
    // novos a juntar-se podiam sair dois downloads sem ninguem ter avisado —
    // e o browser bloqueia o segundo em silencio quando isso o apanha de
    // surpresa.
    const totalFicheiros = partes.length + partesNovas.length
    if (totalFicheiros > 1) {
      // O AVISO ENUMERA OS FICHEIROS, UM A UM.
      //
      // Dizia "9 artigos novos, num ficheiro a parte" e logo a seguir "vao ser
      // descarregados 2 ficheiros". Um cliente leu isso a 14/09/2026 e concluiu
      // que os nove artigos tinham sido partidos em dois — e queixou-se de um bug
      // que nao existe. Qualquer pessoa leria assim: sao dois numeros seguidos, e
      // nada dizia que o segundo ficheiro era o catalogo.
      //
      // Enumerados, nao ha o que interpretar: ve-se o que ha dentro de cada um.
      const linhasAviso = [`Vao ser descarregados ${totalFicheiros} ficheiros:`, '']
      let n = 0
      if (partes.length === 1) {
        linhasAviso.push(`${++n}. Catalogo completo — ${linhas.length} artigos`)
      } else {
        for (let i = 0; i < partes.length; i++) {
          linhasAviso.push(`${++n}. Catalogo, parte ${i + 1} de ${partes.length} — ${partes[i].length} artigos`)
        }
      }
      if (partesNovas.length > 0) {
        const desdeQuando = desdeCurto ? `desde o teu ultimo descarregamento (${desdeCurto})` : 'desde a tua ultima visita'
        if (partesNovas.length === 1) {
          linhasAviso.push(`${++n}. So os artigos novos ${desdeQuando} — ${novos.length} artigo${novos.length === 1 ? '' : 's'}`)
        } else {
          for (let i = 0; i < partesNovas.length; i++) {
            linhasAviso.push(`${++n}. Artigos novos ${desdeQuando}, parte ${i + 1} de ${partesNovas.length} — ${partesNovas[i].length} artigos`)
          }
        }
      }
      linhasAviso.push('')
      linhasAviso.push('O browser pede autorizacao para varios downloads — carrega em permitir.')
      if (!window.confirm(linhasAviso.join('\n'))) return
    }

    // A escrita passa a servir dois conjuntos: o catalogo todo e, quando ha,
    // so os artigos novos. O contador de atraso e partilhado para os downloads
    // nao sairem todos no mesmo instante — ver o comentario la dentro.
    let ordem = 0
    const escrever = (grupos, base, dataNoNome = dia) => {
      grupos.forEach((parte, idx) => {
        const nome = grupos.length === 1
          ? `${base}-${dataNoNome}.csv`
          : `${base}-${dataNoNome}_${idx + 1}.csv`
        const conteudo = bom + [CABECALHO, ...parte].join('\n')
        const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = nome
        document.body.appendChild(a); a.click(); a.remove()
        // Um respiro entre downloads: disparados todos no mesmo instante, o
        // browser deixa cair os ultimos sem dizer nada.
        const n = ordem++
        setTimeout(() => URL.revokeObjectURL(url), 1000 + n * 300)
      })
    }

    escrever(partes, 'catalogo')

    if (partesNovas.length > 0) {
      // catalogo-NOVOS-desde-2026-09-05.csv — se um dia faltarem artigos, ve-se
      // logo no nome que intervalo e que o ficheiro cobriu.
      if (desdeDia) escrever(partesNovas, 'catalogo-NOVOS-desde', desdeDia)
      else escrever(partesNovas, 'catalogo-NOVOS')
    }

    // A DATA SO AVANCA NUM DESCARREGAMENTO SEM FILTROS.
    //
    // O ficheiro leva o que esta no ecra. Se ele tivesse filtrado por uma marca
    // e a data avancasse na mesma, as novidades de todas as outras marcas
    // desapareciam para sempre — nunca mais apareciam em ficheiro nenhum, e
    // ninguem daria por isso. Com filtros ligados o ficheiro sai na mesma; e a
    // marca que fica para tras, que erra sempre para o lado de mostrar demais.
    const semFiltros = !search.trim() && abaCategoria === 'todos' && !subFiltro && prazoFiltro == null
    if (semFiltros) {
      const agora = new Date().toISOString()
      supabase.rpc('registar_download_catalogo')
        .then(({ error }) => { if (!error) setUltimoDownload(agora) })
        .catch(() => {})
    }
  }
  // Incentivo Volume: aplica se cliente Standard (sem desconto base), B2B, nao-interno, e total c/IVA >= threshold.
  // Premium (desconto > 0) NUNCA acede a este incentivo — fix double-protected aqui e no RPC server-side.
  // ─── Fornecedores sem desconto ──────────────────────────────────────────────
  // No Naypes vende-se ao preço de tabela: custo mais margem, e nada por cima.
  // Sem isto, os dois ou três clientes que têm 10% no perfil levavam-nos também
  // aqui — e 10% sobre uma margem de 16,7% leva-lhe mais de metade. O incentivo
  // de volume é pior ainda porque é automático: bastava a encomenda passar dos
  // 1.500 € para dar 5% que ninguém autorizou.
  //
  // É do fornecedor e não do cliente, de propósito: no dia em que quiseres dar
  // desconto de Naypes a alguém, acrescenta-se uma coluna no cliente para isso,
  // em vez de abrir a excepção para toda a gente de uma vez.
  // Uma encomenda só pode ter um fornecedor (a BD recusa o contrário), portanto
  // o primeiro artigo do carrinho identifica-o sem ambiguidade.
  const fornecedorDoCarrinho = carrinho.find(i => i.fornecedor_id)?.fornecedor_id || null
  const fornCarrinho = fornecedores.find(f => f.id === fornecedorDoCarrinho) || null
  const fornecedorSemDesconto = fornCarrinho?.permite_desconto === false
  // O desconto do perfil (os 10%) não se aplica em fornecedores que vendem a
  // tabela. Zerá-lo aqui é o que abre a porta ao incentivo de volume: quem não
  // tem desconto próprio entra na regra geral.
  const descontoBaseCliente = fornecedorSemDesconto ? 0 : descontoPerfil
  // Incentivo de volume: o do fornecedor quando ele tem valores proprios (Naypes:
  // 5% acima de 500 €, prémio de abertura), senão o global (Plus Skill: 5% acima
  // de 1.500 €). Ter valores próprios dispensa a chave global.
  const incentivoPct = fornCarrinho?.incentivo_pct != null
    ? Number(fornCarrinho.incentivo_pct) : incentivoConfig.pct
  const incentivoThreshold = fornCarrinho?.incentivo_threshold != null
    ? Number(fornCarrinho.incentivo_threshold) : incentivoConfig.threshold
  const incentivoDisponivel = fornCarrinho?.incentivo_pct != null
    ? Number(fornCarrinho.incentivo_pct) > 0 : incentivoConfig.ativo
  // Não acumula: só chega aqui quem não tem desconto próprio. No Naypes isso
  // inclui os quatro que têm 10% no Plus Skill — ali esse desconto não se
  // aplica, ficam sem nenhum, e entram na regra geral como toda a gente.
  const incentivoElegivel = (
    incentivoDisponivel
    && descontoBaseCliente === 0
    && !perfilEfectivo?.cliente_interno
    && perfilEfectivo?.tipo_cliente !== 'particular'
  )
  // SEM IVA, como o mínimo de encomenda e como tudo o que decide descontos.
  // O imposto passa por nós e vai para o Estado; não é valor de compra e não
  // pode empurrar um cliente para um desconto que a mercadoria dele não paga.
  // Com IVA, os 500 € do Naypes disparavam com 407 € de artigos.
  const incentivoAtivo = incentivoElegivel && carrinhoTotalSemIva >= incentivoThreshold


  // Modo interno (Cátia = revenda a custo): preços já são custo+portes, sem desconto adicional.
  // Também IVA 0% (reverse charge — conta interna).
  const descontoCliente = modoEncomendarInterno
    ? 0
    : (incentivoAtivo ? incentivoPct : descontoBaseCliente)
  // Calculo fiscal correcto (CIVA art.16):
  // 1) Desconto aplica-se sobre s/IVA dos PRODUTOS (excluindo portes embutidos)
  // 2) Base tributavel = subtotal s/IVA - desconto
  // 3) IVA 23% sobre base tributavel (0% para modo interno — reverse charge)
  // 4) Total = base + IVA
  const qtdTotalCarrinho = carrinho.reduce((s, it) => s + Number(it.quantidade || 0), 0)
  const portesSemIvaCarrinho = qtdTotalCarrinho * portesUn
  const baseDescontoSemIva = Math.max(0, carrinhoTotalSemIva - portesSemIvaCarrinho)
  const valorDescontoSemIva = descontoCliente > 0 ? baseDescontoSemIva * (descontoCliente / 100) : 0
  const baseTributavel = carrinhoTotalSemIva - valorDescontoSemIva
  const IVA_APLICAVEL = modoEncomendarInterno ? 0 : IVA

  // ── O incentivo, num sitio so ────────────────────────────────────────────
  //
  // Isto era calculado dentro do banner do carrinho e so aparecia a partir dos
  // 80% do limiar. Com o limiar do Plus Skill a subir para 2.500, isso queria
  // dizer que o cliente so descobria que havia desconto aos 2.000 — e quem nao
  // sabe que existe nao vai a caminho dele.
  //
  // Passa a ser calculado uma vez e mostrado em varios lados: no cabecalho
  // enquanto navega, no carrinho, e ao pe do botao de finalizar. Um sitio so a
  // decidir, para os tres nao poderem discordar entre si.
  //
  // `proximidade` e o que decide o peso de cada aviso. Um cliente com 150 EUR a
  // ler "faltam 2.350" num quadro grande sente que e inalcancavel — o aviso
  // desmotiva em vez de puxar. Ate meio caminho e uma linha discreta; dai para
  // cima ganha barra e cor.
  const incentivoInfo = React.useMemo(() => {
    if (!incentivoElegivel || carrinho.length === 0 || !(incentivoThreshold > 0)) return null
    const falta = Math.max(0, incentivoThreshold - carrinhoTotalSemIva)
    const progresso = Math.min(100, (carrinhoTotalSemIva / incentivoThreshold) * 100)
    return {
      pct: incentivoPct,
      limiar: incentivoThreshold,
      falta,
      progresso,
      atingido: falta <= 0,
      // Quase la: vale a pena interromper antes de ele submeter e perder o
      // desconto por uma diferenca que resolvia com mais um artigo.
      quaseLa: !(falta <= 0) && progresso >= 90,
      discreto: progresso < 50,
      // O que poupa, ou pouparia, ja com IVA — que e o numero que ele ve na
      // transferencia.
      poupanca: incentivoAtivo
        ? valorDescontoSemIva * (1 + IVA)
        : (carrinhoTotalSemIva >= incentivoThreshold ? 0 : incentivoThreshold * incentivoPct / 100 * (1 + IVA)),
    }
  }, [incentivoElegivel, carrinho.length, incentivoThreshold, incentivoPct, carrinhoTotalSemIva, incentivoAtivo, valorDescontoSemIva])
  const ivaValor = baseTributavel * IVA_APLICAVEL
  const carrinhoFinal = baseTributavel + ivaValor
  const carrinhoMetade = carrinhoFinal * 0.5
  // Compat: valorDesconto convertido para c/IVA equivalente (para apresentacao legacy se necessario)
  const valorDesconto = valorDescontoSemIva

  // ─── Sinal flexível (acordo especial com o fornecedor: 20% do custo à cabeça) ──
  // Só para clientes marcados no perfil. Internos e particulares ficam de fora:
  // os primeiros são auto-pagos, os segundos pagam sempre 100%.
  // cliente_interno do PERFIL (e nao modoEncomendarInterno): senao a Catia a
  // entrar na conta dela via o campo, escrevia 7% e a RPC descartava — o ecra
  // prometia uma coisa e a encomenda saia outra.
  // Minimo obrigatorio por cliente. Sem minimo definido a condicao NAO se aplica
  // — falhar para o lado seguro: um cliente marcado por engano, sem minimo
  // configurado, nao pode acabar a pagar 1%. A BD faz a mesma verificacao.
  const fmtPctSinal = (n) => String(Math.round(Number(n) * 10) / 10).replace('.', ',')
  const sinalMinimoPct = Number(perfilEfectivo?.sinal_minimo_pct)
  const temMinimoValido = Number.isFinite(sinalMinimoPct) && sinalMinimoPct > 0 && sinalMinimoPct < 100
  const clienteTemAcordo = !!perfilEfectivo?.sinal_flexivel && !isParticular
    && !perfilEfectivo?.cliente_interno && !modoEncomendarInterno && temMinimoValido
  // TODOS os artigos tem de estar marcados. Se bastasse um, o cliente metia 1
  // unidade elegivel + 10 de outra coisa e pagava 8% sobre o carrinho inteiro —
  // e o fornecedor continua a querer 50% a cabeca nos que nao estao no acordo.
  // Carrinhos gravados antes desta versao nao trazem o campo => cai em 50/50.
  // A elegibilidade sai do CATALOGO ACTUAL, nao do retrato guardado no carrinho.
  // O carrinho vive no localStorage e guarda uma copia do produto no momento em
  // que foi adicionado (`...produto`). Um artigo metido no carrinho antes de o
  // produto ser marcado ficava com o retrato antigo — e o cliente perdia os 8%
  // sem nada que o explicasse. A marca e uma propriedade viva do produto, nao
  // do momento da adicao. Se o produto nao estiver no catalogo carregado, cai
  // no retrato (o comportamento anterior).
  const flexPorNome = useMemo(() => {
    const m = new Map()
    produtos.forEach(p => { if (p?.Nome) m.set(p.Nome, p.sinal_flexivel === true) })
    return m
  }, [produtos])
  const itemElegivel = (i) => {
    const vivo = flexPorNome.get(i?.Nome)
    return vivo !== undefined ? vivo : i?.sinal_flexivel === true
  }
  const carrinhoTodoElegivel = carrinho.length > 0 && carrinho.every(itemElegivel)
  const artigosForaDoAcordo = carrinho.filter(i => !itemElegivel(i))
  // Prazo de entrega do carrinho: vem do fornecedor dos artigos, nao de um 25
  // cravado. Se o carrinho estiver vazio ou o fornecedor nao tiver prazo
  // definido, mantem-se o 25 de sempre.
  const prazoEntregaDias = Number(
    fornecedores.find(f => f.id === fornecedorDoCarrinho)?.prazo_envio_dias
  ) || 25

  // ─── Fornecedores que tem de ser pagos por inteiro ──────────────────────────
  // Ha fornecedores a quem pagamos 100% a cabeca (o Naypes, por exemplo, porque
  // sao valores baixos). Se o cliente pudesse pagar metade, a diferenca saia do
  // nosso bolso ate a segunda prestacao entrar — numa encomenda de 5.000 EUR sao
  // 2.500 EUR a financiar sem o termos decidido.
  //
  // E propriedade do fornecedor e nao do cliente: como uma encomenda so pode ter
  // um fornecedor, a condicao aplica-se a encomenda inteira sem ambiguidade.
  // Aqui so se decide o que MOSTRAR; quem manda e a RPC, que volta a verificar.
  const fornecedorExigeTotal = !!fornecedores
    .find(f => f.id === fornecedorDoCarrinho)?.exige_pagamento_total
  // Valor derivado, e nao um setState: se fosse estado, ficava a hipotese de o
  // radio dos 50% estar escondido e a escolha antiga continuar la por baixo.
  const tipoPagamentoEfectivo = fornecedorExigeTotal ? '100' : tipoPagamento

  // ─── Minimo de encomenda do fornecedor ──────────────────────────────────────
  // Uma encomenda de 50 € da exactamente o mesmo trabalho que uma de 5.000:
  // encomendar, receber, conferir, facturar, acompanhar o pagamento e expedir.
  // E rende oito euros. O minimo nao existe por causa dos portes — existe porque
  // abaixo dele o trabalho custa mais do que a margem paga.
  //
  // Nao se aplica a contas internas (compram ao custo, para consumo proprio) nem
  // a particulares, que hoje nem chegam a ver estes artigos e amanha terao regra
  // propria. Compara-se com o total ANTES de desconto — o mesmo numero que o
  // cliente ve a subir no carrinho, e o mesmo que decide o incentivo de volume.
  // SEM IVA, e nao com. O mínimo é sobre mercadoria — o IVA é imposto que passa
  // por nós e vai para o Estado, não é valor de encomenda. Comparava-se com o
  // total c/IVA, e assim 122 € de artigos mais 28 € de imposto cumpriam um
  // mínimo de 150 €: o cliente chegava lá com um quarto de nada.
  const minEncomenda = (!modoEncomendarInterno && !isParticular && !perfilEfectivo?.cliente_interno)
    ? Number(fornCarrinho?.min_encomenda_b2b) || 0
    : 0
  const abaixoDoMinimo = minEncomenda > 0 && carrinho.length > 0 && carrinhoTotalSemIva < minEncomenda

  const sinalFlexivel = clienteTemAcordo && carrinhoTodoElegivel

  // ─── [UNIDADES] Sinal em artigos inteiros, para quem tem de o facturar ───────
  // Uma factura nao pode ser de 8,5 unidades. Nestes clientes o sinal deixa de
  // ser uma percentagem livre e passa a ser o preco de um numero INTEIRO de
  // artigos: por cada linha, ARREDONDA PARA CIMA (quantidade x pct).
  //
  // Isto ESPELHA o calculo do RPC (ver criar_encomenda_sinal_flexivel.sql,
  // zona [UNIDADES]). Quem manda e o servidor — isto aqui e so para o admin ver
  // o que vai facturar. Se as duas formulas divergirem, o ecra mostra um numero
  // e a encomenda vale outro, por isso qualquer mudanca tem de ser feita nos
  // dois sitios.
  const sinalPorUnidades = sinalFlexivel && !!perfilEfectivo?.sinal_por_unidades
  const precoUnEfectivoComIva = (it) => {
    const base = Number(it.preco_unitario || 0)
    // O desconto nao incide sobre o 1 EUR/un de portes embutido no preco.
    const desc = descontoCliente > 0 ? Math.max(0, base - portesUn) * (descontoCliente / 100) : 0
    return (base - desc) * (1 + IVA_APLICAVEL)
  }
  const linhasSinalUnidades = sinalPorUnidades
    ? carrinho.map(it => {
        const qtd = Number(it.quantidade || 0)
        const un = Math.ceil(qtd * sinalMinimoPct / 100)
        return { nome: it.Nome, unidades: un, restantes: qtd - un, valor: un * precoUnEfectivoComIva(it) }
      })
    : []
  const sinalUnidadesTotal = linhasSinalUnidades.reduce((s, l) => s + l.valor, 0)
  const sinalUnidadesPct = carrinhoFinal > 0 ? (sinalUnidadesTotal / carrinhoFinal) * 100 : 0
  const sinalPctNum = Number(String(sinalPct).replace(',', '.'))
  // Regra: pode pagar MAIS a cabeca, nunca menos que o minimo do acordo.
  // Tolerancia de 0,001 para o caso de 8 vir a dar 7,999999 em virgula flutuante.
  const sinalAbaixoDoMinimo = Number.isFinite(sinalPctNum) && sinalPctNum > 0
    && temMinimoValido && sinalPctNum < sinalMinimoPct - 0.001
  const sinalPctValida = Number.isFinite(sinalPctNum) && sinalPctNum > 0 && sinalPctNum < 100
    && !sinalAbaixoDoMinimo
  // O cliente escreve a %, mas o que vale é o EURO: arredondado uma vez, aqui, e
  // é este número que vai para a BD, para o email e para a transferência. Assim
  // nunca há discussão sobre se 7% de 979,82 são 68,58 ou 68,59.
  // (carrinhoFinal * pct/100) arredondado a 2 casas == Math.round(total*pct)/100
  const sinalValor = sinalPctValida ? Math.round(carrinhoFinal * sinalPctNum) / 100 : 0
  // A 2ª prestação é sempre o resto exacto — nunca uma segunda percentagem.
  const sinalSegunda = Math.round((carrinhoFinal - sinalValor) * 100) / 100

  // Pré-preenche a percentagem sugerida do perfil. Só enquanto o campo estiver
  // vazio — se o cliente já escreveu algo, não lhe mexemos no que ele pôs.
  useEffect(() => {
    if (!sinalFlexivel) return
    const min = perfilEfectivo?.sinal_minimo_pct
    if (min != null && sinalPct === '') setSinalPct(String(min).replace(/\.00$/, ''))
  }, [sinalFlexivel, perfilEfectivo?.sinal_minimo_pct])

  useEffect(() => { loadCatalogo(); loadConfig(); }, [isAdmin, user?.email, viewAsEmail, modoEncomendarInterno])
  useEffect(() => { if (!isAdmin && user) { loadFavoritos(); loadFrequencia() } }, [isAdmin, user?.id, viewAsEmail])

  const loadSeguidos = async () => {
    if (!user || isAdmin) { setPodeSeguir(false); return }
    const { data: perf } = await supabase.from('perfis').select('precos_mercado').eq('id', user.id).maybeSingle()
    if (!perf?.precos_mercado) { setPodeSeguir(false); return }
    setPodeSeguir(true)
    const { data } = await supabase.from('vigia_mercado')
      .select('produto_id').eq('cliente_id', user.id).neq('estado', 'removido')
    setSeguidos(new Set((data || []).map(r => r.produto_id)))
  }

  // O TECTO DOS 15 TEM DE SE VER NO BOTAO, NAO SO QUANDO FALHA.
  // Com um botao em cada cartao, os lugares enchem-se sem se dar por isso — e
  // quem paga sao os 15 precos que alguem escreve a mao de semana a semana.
  const toggleSeguir = async (produtoId) => {
    if (!user || !podeSeguir || aSeguir) return
    setASeguir(produtoId)
    try {
      if (seguidos.has(produtoId)) {
        await supabase.from('vigia_mercado').update({ estado: 'removido' })
          .eq('cliente_id', user.id).eq('produto_id', produtoId).neq('estado', 'removido')
        setSeguidos(prev => { const n = new Set(prev); n.delete(produtoId); return n })
      } else {
        if (seguidos.size >= 15) {
          setAvisoEmail({ tipo: 'erro', texto: 'Já estás a seguir 15 artigos. Tira um em Preços de Mercado para acrescentares outro.' })
          return
        }
        const { error } = await supabase.from('vigia_mercado')
          .insert({ cliente_id: user.id, produto_id: produtoId, estado: 'proposto', criado_por: 'cliente' })
        if (error) { setAvisoEmail({ tipo: 'erro', texto: error.message }); return }
        setSeguidos(prev => new Set(prev).add(produtoId))
        setAvisoEmail({ tipo: 'ok', texto: `A seguir. Faltam ${14 - seguidos.size} lugares dos 15.` })
      }
    } finally { setASeguir(null) }
  }

  const loadFavoritos = async () => {
    if (!user) return
    const { data } = await supabase.from('cliente_favoritos').select('produto_id').eq('cliente_id', user.id)
    setFavoritos(new Set((data || []).map(r => r.produto_id)))
  }

  const loadFrequencia = async () => {
    if (!user) return
    const targetEmail = viewAsEmail || perfil?.email || user.email
    if (!targetEmail) return
    // Conta quantas vezes cada produto aparece em encomendas deste cliente (qualquer estado excepto cancelado)
    const { data } = await supabase
      .from('encomenda_itens')
      .select('produto_nome, encomendas!inner(cliente_email, estado)')
      .eq('encomendas.cliente_email', targetEmail)
      .neq('encomendas.estado', 'cancelado')
    const map = new Map()
    ;(data || []).forEach(it => {
      const nome = it.produto_nome
      if (nome) map.set(nome, (map.get(nome) || 0) + 1)
    })
    setFrequenciaCompra(map)
  }

  const toggleFavorito = async (produtoId) => {
    if (!user) return
    const isFav = favoritos.has(produtoId)
    if (isFav) {
      await supabase.from('cliente_favoritos').delete().eq('cliente_id', user.id).eq('produto_id', produtoId)
      setFavoritos(prev => { const n = new Set(prev); n.delete(produtoId); return n })
    } else {
      await supabase.from('cliente_favoritos').insert({ cliente_id: user.id, produto_id: produtoId })
      setFavoritos(prev => new Set(prev).add(produtoId))
    }
  }
  useEffect(() => { loadSeguidos() }, [user?.id, isAdmin])
  useEffect(() => { if (isAdmin) loadClientes() }, [isAdmin])
  useEffect(() => {
    const lerCarrinho = () => {
      try {
        const carrinhoSalvo = localStorage.getItem('carrinho')
        if (!carrinhoSalvo) { setCarrinho([]); return }
        const carrinhoParsed = JSON.parse(carrinhoSalvo)
        if (!Array.isArray(carrinhoParsed)) { setCarrinho([]); return }
        const carrinhoCorrigido = carrinhoParsed.map(item => ({
          ...item,
          preco_com_iva: item.preco_com_iva || (item.preco_unitario ? item.preco_unitario * (1 + IVA) : 0)
        }))
        setCarrinho(carrinhoCorrigido)
      } catch { setCarrinho([]) }
    }
    lerCarrinho()
    // Sincronização entre abas: outra aba mexe no localStorage → atualiza esta
    const onStorage = (e) => {
      if (e.key === 'carrinho') lerCarrinho()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Auto-abrir o cart drawer quando se chega vindo do botão "Carrinho" da Gestão de Stock
  // IMPORTANTE: usa abrirCarrinho() (não setShowCarrinho directo) para correr toda a
  // inicialização — moradas LOGNOW auto-preenchidas, moradas guardadas carregadas, etc.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('autoOpenCart') === '1') {
        sessionStorage.removeItem('autoOpenCart')
        // pequeno delay para garantir que o componente está montado e o carrinho carregado
        setTimeout(() => { abrirCarrinho() }, 100)
      }
    } catch { /* silent */ }
  }, [])

  // Auto-abrir modal do produto quando se chega vindo do clique numa notificacao
  // (stock_esgotado/critico/aviso, vendas_auto_iniciadas, etc.)
  useEffect(() => {
    if (!produtos || produtos.length === 0) return
    try {
      const raw = sessionStorage.getItem('notif_open')
      if (!raw) return
      const data = JSON.parse(raw)
      if (data?.table !== 'Produtos' || !data?.id) return
      sessionStorage.removeItem('notif_open')
      const prod = produtos.find(p => p.id === data.id)
      if (prod) setTimeout(() => abrirEditarProduto(prod), 80)
    } catch { /* silent */ }
  }, [produtos])

  const loadClientes = async () => {
    const { data } = await supabase.from('perfis').select('id, nome, email, desconto_percentagem').order('nome')
    setClientes(data || [])
  }

  const loadConfig = async () => {
    const { data } = await supabase.from('config_sistema').select('chave, valor')
    if (data) {
      data.forEach(r => {
        if (r.chave === 'nib') setNib(r.valor || '')
        if (r.chave === 'iban') setIban(r.valor || '')
        if (r.chave === 'banco_nome') setBancoNome(r.valor || '')
        if (r.chave === 'banco_titular') setBancoTitular(r.valor || '')
        if (r.chave === 'portes_por_unidade') {
          const v = parseFloat(String(r.valor || '').replace(',', '.'))
          if (!isNaN(v) && v >= 0) setPortesUn(v)
        }
      })
      // Carrega config do incentivo de volume (3 chaves)
      const cfg = {
        ativo: data.find(r => r.chave === 'incentivo_volume_ativo')?.valor === 'true',
        threshold: parseFloat(data.find(r => r.chave === 'incentivo_volume_threshold_eur')?.valor || '1500') || 1500,
        pct: parseFloat(data.find(r => r.chave === 'incentivo_volume_desconto_pct')?.valor || '5') || 5,
      }
      setIncentivoConfig(cfg)
    }
  }

  const loadCatalogo = async () => {
    setLoading(true)
    setError('')
    try {
      // notas_admin e desconto_supplier_pct só para admin — caso contrário ficam no state React e são
      // inspeccionáveis em DevTools mesmo que a UI as esconda.
      const colsBase = 'id, Nome, EAN, preco, preco_particular, preco_manual, categoria, Stock_Atual, visivel_catalogo, sinal_flexivel, subcategoria, fornecedor_id, Email_Cliente, notificado_cliente_em, foto_url, qtd_minima_compra, preco_mercado, created_at, "Vendas_Mensais", vendas_mensais_manual, vendas_mensais_calculadas_em, vendas_mensais_dias_dados, vendas_mensais_dias_activos, vendas_mensais_capturadas, "Lead_Time", "Objetivo_Cobertura", "Transito1_Qtd", "Transito1_Data", "Transito2_Qtd", "Transito2_Data", "Transito3_Qtd", "Transito3_Data", "Transito4_Qtd", "Transito4_Data", "Transito5_Qtd", "Transito5_Data", "Data_Contagem"'
      const colsAdmin = colsBase + ', notas_admin, desconto_supplier_pct, zerado_desde, ex_dono_email, aviso_libertacao_em'
      // Particular vê o catálogo todo (decisão de negócio: 1 unidade, não faz concorrência),
      // mas não tem de receber o Email_Cliente — é o email de outro cliente e ia no payload,
      // visível em DevTools mesmo sem aparecer no ecrã.
      const colsParticular = colsBase.replace(', Email_Cliente', '')
      let query = supabase
        .from('Produtos')
        .select(isAdmin ? colsAdmin : (isParticular ? colsParticular : colsBase))
        .order('Nome')
      // Cliente B2B (ou admin em modo "ver como cliente"): produtos visíveis e seus/públicos
      // Cliente B2C particular: vê TODOS os produtos visiveis (separação total — vê catálogo completo)
      // Admin sem viewAs: vê tudo (incluindo arquivados)
      const emailFiltro = viewAsEmail || (!isAdmin ? user?.email : null)
      if (emailFiltro) {
        // ─── O que o fornecedor nao tem, nao se vende ──────────────────────
        // A sincronizacao marca `fornecedor_sem_stock` todas as noites — hoje
        // marcou 36 artigos — mas ninguem lia essa marca. Um artigo publicado
        // que o fornecedor deixasse de ter continuava a venda, e a encomenda so
        // rebentava do lado da compra, dias depois, com o cliente ja a espera.
        //
        // So se aplica ao cliente: o admin continua a ver tudo, senao os
        // artigos desapareciam-lhe do ecra sem explicacao e ele nao percebia
        // porque e que o catalogo encolhia sozinho.
        //
        // Nao e preciso republicar quando o stock volta: a marca e desfeita pela
        // sincronizacao e o artigo reaparece na manha seguinte, sozinho.
        query = query.eq('fornecedor_sem_stock', false)
        if (isParticular) {
          // ─── Sem preco de particular, nao se mostra ao particular ──────────
          // O preco_particular e definido a mao, artigo a artigo. Os 2.509 do
          // Naypes entraram sem ele — a sincronizacao nao o calcula, porque o
          // markup de B2C e uma decisao comercial e nao uma formula.
          //
          // Sem este filtro, o particular via-os a 0,00 EUR (o ecra faz
          // `preco_particular || 0`), metia-os no carrinho a zero, e so no
          // checkout e que a base de dados recusava. Ver um preco de zero e
          // levar com um erro a seguir e pior do que nao ver o artigo.
          //
          // `gt` exclui os nulos por definicao em SQL, portanto cobre os dois
          // casos: sem preco e com preco a zero.
          query = query.gt('preco_particular', 0)
          // B2C: query ampla — RLS já filtra a visibilidade. NÃO usar filtro Email_Cliente.
          query = query.eq('visivel_catalogo', true)
        } else {
          // B2B: o cliente vê produtos
          //   (a) seus privados (Email_Cliente = email) — independentemente de visivel_catalogo
          //   (b) públicos visíveis (Email_Cliente IS NULL AND visivel_catalogo = true)
          // O visivel_catalogo é um controlo do admin para esconder produtos PÚBLICOS — não
          // deve esconder produtos privados ao cliente dono.
          query = query.or(`Email_Cliente.eq.${emailFiltro},and(Email_Cliente.is.null,visivel_catalogo.eq.true)`)
        }
      }
      // O Supabase devolve no maximo 1000 linhas por consulta. Com 2.652 produtos
      // o catalogo so via os primeiros mil — e os custos, carregados a seguir,
      // eram cortados no mesmo sitio, o que fazia aparecer produtos "sem custo"
      // que na verdade tinham custo. Buscar por blocos ate nao vir mais nada.
      const PAGINA = 1000
      let data = [], error = null
      for (let de = 0; ; de += PAGINA) {
        const r = await query.range(de, de + PAGINA - 1)
        if (r.error) { error = r.error; break }
        data = data.concat(r.data || [])
        if (!r.data || r.data.length < PAGINA) break
      }
      if (error) throw error

      // Admin: carregar dados internos (preco_custo etc) em separado e fundir.
      // Carrega:
      //   (a) admin sem viewAs (vista propria) - para mostrar margens etc
      //   (b) admin em modo "encomendar como cliente interno" - para usar preco_custo no catalogo
      // Nao carrega em URL view_as (admin a ver como cliente standard - vista do cliente).
      let produtosFinal = data || []
      const queroDadosInternos = isAdmin && (!viewAsEmailProp || modoEncomendarInterno)
      if (queroDadosInternos && produtosFinal.length > 0) {
        let internos = []
        for (let de = 0; ; de += 1000) {
          const r = await supabase.from('produtos_internos')
            .select('produto_nome, preco_fornecedor, preco_custo')
            .range(de, de + 999)
          if (r.error) break
          internos = internos.concat(r.data || [])
          if (!r.data || r.data.length < 1000) break
        }
        const mapaInternos = new Map((internos || []).map(i => [i.produto_nome, i]))
        produtosFinal = produtosFinal.map(p => {
          const i = mapaInternos.get(p.Nome)
          return i ? { ...p, preco_fornecedor: i.preco_fornecedor, preco_custo: i.preco_custo } : p
        })
      }
      // Categorias: nomes e ordem dos separadores. Se a tabela ainda nao existir
      // (migracao por correr), fica vazio e o catalogo mostra so "Todos" — nao
      // rebenta.
      try {
        // A tabela `fornecedores` esta protegida por RLS e so o admin a le. O
        // cliente recebia lista vazia — sem erro, porque uma leitura bloqueada
        // devolve zero linhas — e o catalogo caia nos valores por omissao: 25
        // dias, 50/50, e o incentivo global. Um artigo do Naypes prometia-lhe
        // condicoes que a base de dados depois recusava.
        //
        // O admin continua a ler a tabela, porque precisa dos custos para o
        // painel de margem. O cliente le a vista, que nao os traz.
        const colsAdminForn = 'id, nome, prazo_envio_dias, exige_pagamento_total, portes_custo_por_unidade, portes_cobrados_por_unidade, permite_desconto, incentivo_pct, incentivo_threshold, min_encomenda_b2b, aplica_minimo_do_cliente, qtd_min_artigo'
        // SEM `nome`. O cliente recebe o fornecedor_id de cada artigo na mesma
        // resposta do catalogo; bastava-lhe cruzar as duas listas no DevTools
        // para saber a quem compramos cada produto. Nada no ecra dele precisa
        // do nome — precisa do prazo, do pagamento e do minimo.
        const colsClienteForn = 'id, prazo_envio_dias, exige_pagamento_total, permite_desconto, incentivo_pct, incentivo_threshold, min_encomenda_b2b, aplica_minimo_do_cliente, qtd_min_artigo'
        const { data: forns } = isAdmin
          ? await supabase.from('fornecedores').select(colsAdminForn).eq('ativo', true)
          : await supabase.from('fornecedores_publicos').select(colsClienteForn).eq('ativo', true)
        setFornecedores(forns || [])
      } catch { setFornecedores([]) }

      try {
        const { data: cats } = await supabase.from('categorias')
          .select('slug, nome, ordem, ativo').eq('ativo', true).order('ordem')
        setCategorias(cats || [])
      } catch { setCategorias([]) }

      // Carregar precos Qogita (por EAN) — visivel a todos para comparacao
      try {
        let qog = []
        for (let de = 0; ; de += 1000) {
          const r = await supabase.from('qogita_precos')
            .select('ean, preco, in_stock, mais_pedido_score').range(de, de + 999)
          if (r.error) break
          qog = qog.concat(r.data || [])
          if (!r.data || r.data.length < 1000) break
        }
        const mapaQog = new Map((qog || []).map(q => [String(q.ean).trim(), q]))
        produtosFinal = produtosFinal.map(p => {
          const ean = p.EAN ? String(p.EAN).trim() : null
          const q = ean ? mapaQog.get(ean) : null
          return q ? { ...p, qogita_preco: q.preco, qogita_in_stock: q.in_stock, mais_pedido_score: q.mais_pedido_score } : p
        })
      } catch (e) { console.warn('Qogita precos load falhou:', e) }
      setProdutos(produtosFinal)
    } catch (err) {
      setError('Erro ao carregar catálogo: ' + err.message)
      setProdutos([])
    } finally {
      setLoading(false)
    }
  }

  // ── Helpers modal produto ──────────────────────────────────────────────────
  const abrirNovoProduto = () => {
    setProdForm(EMPTY_PROD)
    setProdErro('')
    setProdModal({})
    setModalPos({ x: 0, y: 0 })
  }

  const abrirEditarProduto = (p) => {
    setModalPos({ x: 0, y: 0 })
    setProdForm({
      Nome: p.Nome || '',
      EAN: p.EAN ? String(p.EAN) : '',
      // preco na BD é já o preço final (com lucro fixo + portes embutidos se auto)
      // preco_base = preço de custo que o admin vê — invertido por escalão para auto, ou preco se manual
      preco_manual: p.preco_manual ?? false,
      categoria: p.categoria || '',
      // preco_base = preco_fornecedor (preço cheio do fornecedor, sem desconto).
      // Fallback: preco_custo (legacy) ou inversa da margem se nada existir
      preco_base: p.preco_fornecedor != null
        ? fmt2(Number(p.preco_fornecedor))
        : (p.preco_custo != null
          ? fmt2(Number(p.preco_custo))
          : (p.preco !== null && p.preco !== undefined
            ? (p.preco_manual ? fmt2(Number(p.preco)) : fmt2(fornecedorEstimadoDoPreco(Number(p.preco), portesUn)))
            : '')),
      preco: p.preco !== null && p.preco !== undefined ? String(p.preco) : '',
      preco_iva: p.preco !== null && p.preco !== undefined
        ? fmt2(Number(p.preco) * (1 + IVA)) : '',

      Stock_Atual: p.Stock_Atual ?? 0,
      visivel_catalogo: p.visivel_catalogo ?? true,
      sinal_flexivel: p.sinal_flexivel === true,
      Email_Cliente: p.Email_Cliente || '',
      foto_url: p.foto_url || '',
      qtd_minima_compra: p.qtd_minima_compra ?? 10,
      Vendas_Mensais: p.Vendas_Mensais ?? 0,
      vendas_mensais_manual: p.vendas_mensais_manual ?? false,
      Lead_Time: p.Lead_Time ?? 25,
      Objetivo_Cobertura: p.Objetivo_Cobertura ?? 30,
      ...Object.fromEntries(SLOTS_TRANSITO.flatMap(n => [
        [`Transito${n}_Qtd`, p[`Transito${n}_Qtd`] ?? 0],
        [`Transito${n}_Data`, p[`Transito${n}_Data`] || ''],
      ])),
      Data_Contagem: p.Data_Contagem || new Date().toISOString().split('T')[0],
      preco_mercado: p.preco_mercado != null ? String(p.preco_mercado) : '',
      // Referencia de mercado, so para o admin ver enquanto decide o preco. Nao
      // e editavel nem e gravada — vem da recolha da Qogita, e e de la que se
      // actualiza. Anda aqui porque o sitio onde se decide o preco tem de ser o
      // mesmo onde se ve contra o que se esta a decidir.
      qogita_preco: p.qogita_preco ?? null,
      // Sem isto, o painel de margem nao sabe de que fornecedor e o artigo e cai
      // nos portes por omissao (0,75 EUR). Num artigo do Naypes, onde os portes
      // sao zero, isso mostrava 0,06 EUR de lucro onde ha 0,81.
      fornecedor_id: p.fornecedor_id ?? null,
      preco_custo: p.preco_custo != null ? String(p.preco_custo) : '',
      notas_admin: p.notas_admin || '',
      desconto_supplier_pct: p.desconto_supplier_pct != null ? String(p.desconto_supplier_pct) : '',
    })
    setProdErro('')
    setProdModal(p)
  }

  // Duplicar produto: copia tudo, limpa EAN (UNIQUE) e adiciona "(Cópia)" ao nome,
  // depois abre o modal em modo "novo" pré-preenchido para o admin ajustar antes de gravar.
  const duplicarProduto = (p) => {
    setModalPos({ x: 0, y: 0 })
    const baseNome = p.Nome || 'Produto'
    setProdForm({
      Nome: baseNome.endsWith('(Cópia)') ? baseNome : `${baseNome} (Cópia)`,
      EAN: '',
      preco_manual: p.preco_manual ?? false,
      categoria: p.categoria || '',
      preco_base: p.preco_fornecedor != null
        ? fmt2(Number(p.preco_fornecedor))
        : (p.preco !== null && p.preco !== undefined
          ? (p.preco_manual ? fmt2(Number(p.preco)) : fmt2(fornecedorEstimadoDoPreco(Number(p.preco), portesUn)))
          : ''),
      preco: p.preco !== null && p.preco !== undefined ? String(p.preco) : '',
      preco_iva: p.preco !== null && p.preco !== undefined
        ? fmt2(Number(p.preco) * (1 + IVA)) : '',
      Stock_Atual: 0, // não copiar stock — produto novo começa em 0
      visivel_catalogo: p.visivel_catalogo ?? true,
      sinal_flexivel: p.sinal_flexivel === true,
      Email_Cliente: p.Email_Cliente || '',
      foto_url: p.foto_url || '',
      qtd_minima_compra: p.qtd_minima_compra ?? 10,
      Vendas_Mensais: 0, // histórico é do original, não do novo
      Lead_Time: p.Lead_Time ?? 25,
      Objetivo_Cobertura: p.Objetivo_Cobertura ?? 30,
      ...Object.fromEntries(SLOTS_TRANSITO.flatMap(n => [[`Transito${n}_Qtd`, 0], [`Transito${n}_Data`, '']])),
      Data_Contagem: new Date().toISOString().split('T')[0],
      preco_mercado: p.preco_mercado != null ? String(p.preco_mercado) : '',
      // Referencia de mercado, so para o admin ver enquanto decide o preco. Nao
      // e editavel nem e gravada — vem da recolha da Qogita, e e de la que se
      // actualiza. Anda aqui porque o sitio onde se decide o preco tem de ser o
      // mesmo onde se ve contra o que se esta a decidir.
      qogita_preco: p.qogita_preco ?? null,
      // Sem isto, o painel de margem nao sabe de que fornecedor e o artigo e cai
      // nos portes por omissao (0,75 EUR). Num artigo do Naypes, onde os portes
      // sao zero, isso mostrava 0,06 EUR de lucro onde ha 0,81.
      fornecedor_id: p.fornecedor_id ?? null,
      preco_custo: p.preco_custo != null ? String(p.preco_custo) : '',
    })
    setProdErro('')
    setProdModal({}) // {} sem id = modo "criar novo"
  }

  const handleProdChange = (key, val) => {
    setProdForm(prev => {
      const next = { ...prev, [key]: val }
      const isAuto = key === 'preco_manual' ? !val : !prev.preco_manual

      // Admin digita o preço BASE (custo) em preco_base
      // preco final calculado por precoVendaComPortes(fornecedor, portes):
      //   PVP = fornecedor × 1.20 + 1€ portes. Se preço termina em .00 → +0.05€.
      if (key === 'preco_base') {
        const base = parseFloat(val)
        if (!isNaN(base)) {
          const final = isAuto ? fmt2(precoVendaComPortes(base, portesUn)) : fmt2(base)
          next.preco = final
          next.preco_iva = fmt2(Number(final) * (1 + IVA))
        } else {
          next.preco = ''
          next.preco_iva = ''
        }
      }
      // Preço c/ IVA editado directamente -> recalcula preco (final s/IVA) e preco_base
      if (key === 'preco_iva') {
        const n = parseFloat(val)
        if (!isNaN(n)) {
          const final = fmt2(n / (1 + IVA))
          next.preco = final
          next.preco_base = isAuto ? fmt2(fornecedorEstimadoDoPreco(Number(final), portesUn)) : final
        }
      }
      // Toggle modo manual/auto: recalcula preco final a partir do preco_base actual
      if (key === 'preco_manual') {
        const base = parseFloat(prev.preco_base)
        if (!isNaN(base) && base > 0) {
          const final = !val ? fmt2(precoVendaComPortes(base, portesUn)) : fmt2(base)
          next.preco = final
          next.preco_iva = fmt2(Number(final) * (1 + IVA))
        }
      }
      // Preço final s/IVA editado directamente (modo manual) -> recalcula preco_iva
      if (key === 'preco' && prev.preco_manual) {
        const raw = String(val).replace(',', '.')
        const n = parseFloat(raw)
        if (!isNaN(n)) {
          next.preco_iva = fmt2(n * (1 + IVA))
        } else {
          next.preco_iva = ''
        }
      }
      // visivel_catalogo é gerido manualmente pelo admin (toggle no modal)
      return next
    })
  }

  const guardarProduto = async () => {
    if (!prodForm.Nome.trim()) { setProdErro('Nome é obrigatório.'); return }

    // Proteccao: aviso se preco no campo diverge do preco em BD >= 0.50 EUR
    // (evita perder aumentos em massa quando modal AUTO recalcula pela formula ao abrir)
    if (prodModal?.id) {
      const precoBd = prodModal.preco != null ? Number(prodModal.preco) : null
      const precoForm = prodForm.preco !== '' ? Number(String(prodForm.preco).replace(',', '.')) : null
      if (precoBd != null && precoForm != null && Math.abs(precoForm - precoBd) >= 0.50) {
        const modoTxt = prodForm.preco_manual ? 'MANUAL' : 'AUTO'
        const explicacao = !prodForm.preco_manual
          ? '\nO produto e AUTO — o campo foi recalculado pela formula ao abrir a modal. Se o valor em BD veio de um aumento em massa (ex: +2%), esse ajuste perde-se ao guardar o valor recalculado.\n'
          : '\n'
        const ok = window.confirm(
          `Confirmar alteracao de preco\n\n` +
          `Preco actual em BD: ${precoBd.toFixed(2)} EUR\n` +
          `Preco novo (campo): ${precoForm.toFixed(2)} EUR\n` +
          `Diferenca: ${(precoForm - precoBd) > 0 ? '+' : ''}${(precoForm - precoBd).toFixed(2)} EUR (${((precoForm - precoBd) / precoBd * 100).toFixed(1)}%)\n\n` +
          `Modo: ${modoTxt}${explicacao}\n` +
          `Guardar mesmo assim?\n` +
          `(Cancelar para voltar a modal e ajustar manualmente para ${precoBd.toFixed(2)} EUR se necessario)`
        )
        if (!ok) return
      }
    }

    setGuardando(true)
    setProdErro('')

    // Validar EAN duplicado
    const eanNovo = prodForm.EAN.trim()
    if (eanNovo) {
      let q = supabase.from('Produtos').select('id, Nome').eq('EAN', eanNovo)
      if (prodModal?.id) q = q.neq('id', prodModal.id) // ignorar o próprio produto ao editar
      const { data: dup } = await q
      if (dup && dup.length > 0) {
        setProdErro(`EAN "${eanNovo}" já existe no produto "${dup[0].Nome}". Não é possível criar duplicados.`)
        setGuardando(false)
        return
      }
    }

    const emailAnterior = prodModal?.Email_Cliente || ''
    const emailNovo     = prodForm.Email_Cliente.trim()
    const desassociou   = prodModal?.id && emailAnterior !== '' && emailNovo === ''
    // visivel_catalogo gerido manualmente pelo admin via toggle (default true)
    const visivelCalc   = prodForm.visivel_catalogo !== false

    // Gravar histórico se desassociou
    //
    // DOIS SÍTIOS, DE PROPÓSITO. O `produto_historico_clientes` é a relação
    // produto-cliente, e é o que o Dashboard lê. O
    // `historico_libertacao_produtos` é o registo de libertações, e até hoje só
    // a libertação automática lá escrevia — as manuais não deixavam rasto
    // nenhum ali.
    //
    // Isso custou uma busca de dez minutos por um La Mer que o Marco tinha
    // libertado à mão e de que não se lembrava. Pior: com um cliente a dizer
    // "este artigo era meu", não havia como confirmar nem desmentir num sítio
    // só. Agora as duas formas de libertar aparecem na mesma lista, distintas
    // pela coluna `origem`.
    if (desassociou) {
      const agoraIso = new Date().toISOString()
      const { data: perf } = await supabase.from('perfis').select('nome').eq('email', emailAnterior).single()
      await supabase.from('produto_historico_clientes').insert({
        produto_nome:    prodForm.Nome.trim(),
        cliente_email:   emailAnterior,
        cliente_nome:    perf?.nome || emailAnterior,
        desassociado_em: agoraIso,
      })
      // O EAN vai junto: é por ele que se procura um artigo meses depois, e a
      // outra tabela já o guardava. Sem ele, procurar pelo nome obriga a
      // adivinhar como o fornecedor o escreveu — "LA MER", "La Mer", "Lamer".
      await supabase.from('historico_libertacao_produtos').insert({
        produto_id:    prodModal.id,
        produto_nome:  prodForm.Nome.trim(),
        produto_ean:   prodForm.EAN?.trim() || null,
        ex_dono_email: emailAnterior,
        ex_dono_nome:  perf?.nome || emailAnterior,
        libertado_em:  agoraIso,
        origem:        'manual',
        libertado_por: user?.email || null,
      })
    }

    const payload = {
      Nome: prodForm.Nome.trim(),
      EAN: prodForm.EAN.trim() || null,
      preco: prodForm.preco !== '' ? Number(String(prodForm.preco).replace(',', '.')) : null,  // preço final com margem
      preco_manual: prodForm.preco_manual,
      categoria: prodForm.categoria || null,
      visivel_catalogo: visivelCalc,
      sinal_flexivel: prodForm.sinal_flexivel === true,
      Email_Cliente: emailNovo || null,
      // Só se escreve ao libertar. Fora disso nem entra no payload, senão uma
      // edição qualquer — mudar a foto, corrigir o nome — apagava o ex-dono de
      // um artigo que nunca foi libertado. É este campo que faz o artigo voltar
      // sozinho para o mesmo cliente se ele o comprar outra vez.
      ...(desassociou ? { ex_dono_email: emailAnterior } : {}),
      foto_url: prodForm.foto_url.trim() || null,
      Vendas_Mensais: Number(prodForm.Vendas_Mensais) || 0,
      vendas_mensais_manual: !!prodForm.vendas_mensais_manual,
      Lead_Time: Number(prodForm.Lead_Time) || 25,
      Objetivo_Cobertura: Number(prodForm.Objetivo_Cobertura) || 30,
      ...Object.fromEntries(SLOTS_TRANSITO.flatMap(n => [
        [`Transito${n}_Qtd`, Number(prodForm[`Transito${n}_Qtd`]) || 0],
        [`Transito${n}_Data`, prodForm[`Transito${n}_Data`] || null],
      ])),
      Data_Contagem: prodForm.Data_Contagem || null,
      qtd_minima_compra: Math.max(1, Number(prodForm.qtd_minima_compra) || 10),
      preco_mercado: prodForm.preco_mercado !== '' && prodForm.preco_mercado != null
        ? Number(String(prodForm.preco_mercado).replace(',', '.'))
        : null,
      notas_admin: (prodForm.notas_admin || '').trim() || null,
      desconto_supplier_pct: prodForm.desconto_supplier_pct !== '' && prodForm.desconto_supplier_pct != null
        ? Number(String(prodForm.desconto_supplier_pct).replace(',', '.'))
        : null,
      // NOTA: preco_fornecedor e preco_custo NÃO ficam aqui — vão para `produtos_internos` (admin only)
    }
    // Dados sensíveis (admin only — tabela isolada)
    const internosPayload = {
      produto_nome: prodForm.Nome.trim(),
      preco_fornecedor: prodForm.preco_base !== '' && prodForm.preco_base != null
        ? Number(String(prodForm.preco_base).replace(',', '.'))
        : null,
      preco_custo: prodForm.preco_custo !== '' && prodForm.preco_custo != null
        ? Number(String(prodForm.preco_custo).replace(',', '.'))
        : null, // se vazio, trigger BD calcula auto
    }
    let error
    if (prodModal?.id) {
      // Editar — Stock_Atual não é tocado (gere-se em Gestão de Stock)
      ;({ error } = await supabase.from('Produtos').update(payload).eq('id', prodModal.id))
    } else {
      // Criar — produto novo começa com stock 0 (atualizado depois via sync LOGNOW ou Gestão de Stock)
      ;({ error } = await supabase.from('Produtos').insert({ ...payload, Stock_Atual: 0 }))
    }
    if (error) { setProdErro('Erro: ' + error.message); setGuardando(false); return }

    // Gravar dados sensíveis na tabela isolada (admin only — RLS bloqueia cliente)
    if (isAdmin && (internosPayload.preco_fornecedor != null || internosPayload.preco_custo != null)) {
      const { error: errInternos } = await supabase
        .from('produtos_internos')
        .upsert(internosPayload, { onConflict: 'produto_nome' })
      if (errInternos) {
        setProdErro('Produto guardado, mas falhou guardar dados internos: ' + errInternos.message)
        setGuardando(false)
        return
      }
    }

    // Associou a um cliente novo. O email de exclusividade NÃO é enviado daqui —
    // quem o dispara é o trigger notificar_exclusividade_trg na base de dados, que
    // cobre tanto esta associação manual como a automática quando o cliente compra.
    // Enviar também daqui punha o cliente a receber dois emails iguais.
    if (emailNovo && emailNovo.toLowerCase() !== emailAnterior.toLowerCase()) {
      setAvisoEmail({
        tipo: 'ok',
        texto: `Produto associado a ${emailNovo}. O email de exclusividade sai automaticamente.`,
      })
    }

    setProdModal(null)
    await loadCatalogo()
    setGuardando(false)
  }

  // ── Modal carrinho ────────────────────────────────────────────────────────
  const aplicarMoradaEscolha = (escolha, moradas) => {
    if (escolha === 'lognow') {
      setEntrega({
        nome: LOGNOW_MORADA.nome,
        morada: LOGNOW_MORADA.morada,
        cod_postal: LOGNOW_MORADA.cod_postal,
        pais: LOGNOW_MORADA.pais,
        telefone: LOGNOW_MORADA.telefone || '',
        nif: LOGNOW_MORADA.nif || '',
      })
      return
    }
    if (escolha === 'manual') {
      // Pré-preenche com dados do perfil para conveniência
      setEntrega({
        nome: perfil?.nome || '',
        morada: perfil?.morada || '',
        cod_postal: perfil?.cod_postal || '',
        pais: 'Portugal',
        telefone: perfil?.telefone || '',
        nif: perfil?.nif || '',
      })
      return
    }
    // Morada guardada
    const m = (moradas || moradasGuardadas).find(x => x.id === escolha)
    if (m) {
      setEntrega({
        nome: m.nome || '',
        morada: m.morada || '',
        cod_postal: m.cod_postal || '',
        pais: m.pais || 'Portugal',
        telefone: m.telefone || '',
        nif: m.nif || '',
      })
    }
  }

  const escolherMorada = (escolha) => {
    setMoradaEscolhida(escolha)
    aplicarMoradaEscolha(escolha)
  }

  const abrirCarrinho = async () => {
    setShowCarrinho(true)
    setResultMsg({ tipo: '', texto: '' })
    setEncomendaFinalizada(null)
    setComprovativo(null)
    setUploadMsg({ tipo: '', texto: '' })
    setMoradaEscolhida('lognow')
    setTentouSubmeter(false)
    aplicarMoradaEscolha('lognow')
    // Carregar moradas guardadas do cliente
    if (user) {
      const { data } = await supabase.from('cliente_moradas')
        .select('*').eq('cliente_id', user.id).order('created_at', { ascending: true })
      setMoradasGuardadas(data || [])
    }
  }

  const fecharCarrinho = () => { setShowCarrinho(false); setResultMsg({ tipo: '', texto: '' }); setEncomendaFinalizada(null); setTentouSubmeter(false) }

  const submeterEncomenda = async () => {
    if (carrinho.length === 0) return
    if (enviando) return // Anti duplo-clique
    const isLognow = moradaEscolhida === 'lognow'
    // Para LOGNOW, NIF e telefone não são obrigatórios; para outras moradas mantém-se a validação completa
    const camposEmFalta = !entrega.nome.trim() || !entrega.morada.trim() || !entrega.cod_postal.trim() || !entrega.pais.trim()
      || (!isLognow && (!entrega.telefone.trim() || !entrega.nif.trim()))
    if (camposEmFalta) {
      setTentouSubmeter(true) // ativa indicadores visuais nos inputs
      setResultMsg({ tipo: 'erro', texto: isLognow
        ? 'Dados de entrega LOGNOW incompletos. Por favor reabra o carrinho.'
        : 'Preencha os campos destacados a vermelho.' })
      return
    }
    setEnviando(true)
    setResultMsg({ tipo: '', texto: '' })
    try {
      const totalSemIva = carrinho.reduce((sum, item) => sum + item.total_sem_iva, 0)
      const totalComIva = carrinho.reduce((sum, item) => sum + item.total_com_iva, 0)
      // Modo interno: sem desconto (precos ja sao custo + portes reais) e IVA 0% (reverse charge)
      // Fornecedor sem desconto (Naypes): preco de tabela, e nada por cima.
      // Desconto do perfil apenas. O incentivo de volume e aplicado pela RPC,
      // que o recalcula sobre o total autoritativo dela.
      const descontoCliente = (modoEncomendarInterno || fornecedorSemDesconto)
        ? 0 : (perfilEfectivo?.desconto_percentagem || 0)
      const IVA_SUBMIT = modoEncomendarInterno ? 0 : IVA
      // Calculo fiscal (CIVA art.16): desconto aplicado sobre s/IVA dos produtos (portes nao levam desc).
      // Base tributavel = subtotal s/IVA - desconto. IVA 23% (ou 0% se interno) sobre base. Total = base + IVA.
      const qtdTotalSubmit = carrinho.reduce((s, it) => s + Number(it.quantidade || 0), 0)
      const portesSemIvaSubmit = qtdTotalSubmit * portesUn
      const baseDescontoSubmitSemIva = Math.max(0, totalSemIva - portesSemIvaSubmit)
      const valorDesconto = descontoCliente > 0 ? baseDescontoSubmitSemIva * (descontoCliente / 100) : 0  // valor s/IVA
      const baseTributavelSubmit = totalSemIva - valorDesconto
      const totalFinal = baseTributavelSubmit * (1 + IVA_SUBMIT)
      // numero_encomenda gerado pela RPC criar_encomenda_com_itens (sequencial global por ano, à prova de race condition)

      // Adicionar desconto às notas se existir
      let notasComDesconto = notas.trim() || ''
      if (descontoCliente > 0) {
        const descontoInfo = `Desconto aplicado: ${descontoCliente}% (-${fmt2(valorDesconto)} €)`
        notasComDesconto = notasComDesconto ? `${notasComDesconto}\n${descontoInfo}` : descontoInfo
      }

      // Criar encomenda + itens atomicamente (transação única no servidor)
      // Para B2C: origem='b2c_fornecedor_directo' → admin pede directo ao fornecedor,
      // não afecta stock B2B, não associa produtos ao cliente B2C
      //
      // Modo "encomendar como cliente": admin a criar encomenda em nome de outro
      // (ex: Catia). Usa-se perfilEfectivo (carregado via viewAsEmail) em vez do
      // perfil do admin. Audit trail vai para notas.
      const aEncomendarComoCliente = !!encomendarComoEmail && !!perfilCliente
      const clienteId    = aEncomendarComoCliente ? perfilCliente.id    : user.id
      const clienteEmail = aEncomendarComoCliente ? perfilCliente.email : user.email
      const clienteNome  = aEncomendarComoCliente ? (perfilCliente.nome || perfilCliente.email) : (perfil?.nome || user.email)
      if (aEncomendarComoCliente) {
        const auditNota = `Criada por admin (${perfil?.nome || user.email}) em modo "encomendar como"`
        notasComDesconto = notasComDesconto ? `${notasComDesconto}\n${auditNota}` : auditNota
      }
      const payloadEncomenda = {
        cliente_id:           clienteId,
        cliente_email:        clienteEmail,
        cliente_nome:         clienteNome,
        total_itens:          carrinho.length,
        total_sem_iva:        totalSemIva,
        total_com_iva:        totalComIva,
        desconto_percentagem: descontoCliente > 0 ? descontoCliente : null,
        desconto_valor:       valorDesconto > 0 ? valorDesconto : null,
        total_final:          totalFinal,
        notas:                notasComDesconto || null,
        origem:               isParticular ? 'b2c_fornecedor_directo' : 'b2b',
        acordo_fixo:          isAdmin && acordoFixo,
        // numero_encomenda gerado pela RPC (ignora o que viesse aqui)
        tempo_entrega_dias:   prazoEntregaDias,
        entrega_nome:         entrega.nome.trim(),
        entrega_morada:       entrega.morada.trim(),
        entrega_cod_postal:   entrega.cod_postal.trim(),
        entrega_pais:         entrega.pais.trim(),
        entrega_telefone:     entrega.telefone.trim(),
        entrega_nif:          entrega.nif.trim() || null,
        // Sinal flexível: vai o VALOR em euros, não a percentagem. A RPC volta a
        // validar (perfil + limites) — isto aqui só decide o que se mostra.
        // Em pagamento 100% não vai nada: a encomenda fica paga por inteiro.
        // Calculado sobre o totalFinal DESTA submissão (e não sobre o total do
        // ecrã) para não haver hipótese de divergirem por um cêntimo.
        // A encomenda passa a registar COMO ia ser paga. Ate aqui nao ficava
        // registo nenhum: a escolha vivia so no ecra, e depois de submetida nao
        // havia como saber o que tinha sido combinado.
        tipo_pagamento:       isParticular ? '100' : tipoPagamentoEfectivo,
        sinal_escolhido:      (sinalFlexivel && tipoPagamentoEfectivo !== '100' && sinalPctValida)
                                ? Math.round(totalFinal * sinalPctNum) / 100
                                : null,
      }
      const itensPayload = carrinho.map(item => ({
        produto_nome:   item.Nome,
        produto_ean:    item.EAN ? String(item.EAN) : null,
        preco_unitario: item.preco_unitario,
        preco_iva:      item.preco_com_iva,
        quantidade:     item.quantidade,
        total_sem_iva:  item.total_sem_iva,
        total_com_iva:  item.total_com_iva,
      }))

      const { data: enc, error: errRpc } = await supabase.rpc('criar_encomenda_com_itens', {
        p_encomenda: payloadEncomenda,
        p_itens: itensPayload,
      })
      if (errRpc) {
        // As nossas validacoes sao escritas para o cliente ler: falta de stock,
        // minimo de encomenda, unidades, pagamento. Essas passam tal e qual.
        //
        // As do Postgres nao. Na primeira encomenda do Naypes o cliente leu
        // "function min(uuid) does not exist" no carrinho — uma mensagem que
        // nao lhe diz nada, que nao lhe da nada para fazer, e que lhe mostra as
        // entranhas do sistema. O erro real vai para a consola, para o admin o
        // ver; ao cliente diz-se o que interessa: que nao passou e que ja
        // sabemos.
        const msg = String(errRpc.message || '')
        const nossa = /^(O artigo|O fornecedor|Encomendas deste fornecedor|Produto|Uma encomenda|Os artigos desta encomenda|Nao autenticado|So pode criar|Encomenda tem de ter)/.test(msg)
        if (!nossa) console.error('[encomenda] erro nao tratado:', errRpc)
        throw new Error(nossa ? msg : 'Não foi possível criar a encomenda. Já fomos avisados — tenta daqui a pouco ou fala connosco.')
      }
      if (!enc?.id) throw new Error('Erro ao criar encomenda — resposta sem id')

      // Email "Encomenda confirmada" é disparado automaticamente pelo trigger BD
      // nova_encomenda_email_trg (AFTER INSERT em encomendas). Garantido — não depende
      // do frontend ficar aberto ou da rede.

      // A RPC devolve a linha completa de `encomendas`, por isso o numero_encomenda
      // vem aqui — é o que o cliente precisa de pôr na descrição da transferência.
      // `metade` = o valor da 1.ª prestação, seja ele metade ou o sinal escolhido.
      // Lê-se de enc.sinal_escolhido (o que a RPC gravou de facto) e não do que o
      // browser calculou — se a RPC tiver recusado o sinal, o ecrã mostra a verdade.
      const primeiraPrestacao = enc.sinal_escolhido != null
        ? Number(enc.sinal_escolhido)
        : totalFinal * 0.5
      setEncomendaFinalizada({ id: enc.id, numero: enc.numero_encomenda || null, total: totalFinal, metade: primeiraPrestacao, sinalPct: enc.sinal_escolhido != null ? sinalPctNum : null, tipoPagamento: isParticular ? '100' : tipoPagamentoEfectivo, foiEncomendarComo: aEncomendarComoCliente, nomeClienteImpersonado: clienteNome })
      setResultMsg({ tipo: 'ok', texto: '' })
      limparCarrinho()
      // Auto-revert: ao finalizar com sucesso, sai do modo "encomendar como"
      if (aEncomendarComoCliente) setEncomendarComoEmail(null)
      await loadCatalogo()
    } catch (e) {
      setResultMsg({ tipo: 'erro', texto: 'Erro: ' + e.message })
    }
    setEnviando(false)
  }

  // Detectar última "leva" de produtos: produtos cujo created_at é
  // contíguo (gap < 1h) com o mais recente. Quando admin adiciona novos,
  // a leva avança e os antigos perdem o badge NOVO automaticamente.
  // ─── O que e "novo" ─────────────────────────────────────────────────────────
  // Era a ultima leva CRIADA: ordenava por created_at e agrupava o que estivesse
  // a menos de uma hora do artigo mais recente. Isso e a data em que o artigo
  // entrou na base de dados — que nao tem nada a ver com o dia em que o cliente
  // passou a poder ve-lo. Os 499 artigos publicados hoje tinham sido criados ha
  // tres dias no import: para aquele criterio nao eram novos, e o cliente que
  // viesse do email encontrava o separador vazio.
  //
  // Novo passa a ser o que lhe foi ANUNCIADO nos ultimos dias. E o carimbo que o
  // proprio email de novidades poe, portanto anunciar um artigo torna-o novo —
  // que e exactamente o que a palavra quer dizer para quem a le.
  // A ultima vez que este cliente ca esteve, NUM DIA ANTERIOR a hoje. Vem de uma
  // funcao e nao de uma leitura a `visitas`: essa tabela tem ip, browser e o
  // email de toda a gente, e um cliente nao a pode ler. A funcao devolve-lhe um
  // instante e so o dele. Ver supabase/sql/ultima_visita_cliente.sql.
  const [ultimaVisita, setUltimaVisita] = React.useState(undefined)   // undefined = ainda a carregar
  React.useEffect(() => {
    let vivo = true
    supabase.rpc('minha_ultima_visita')
      .then(({ data, error }) => { if (vivo) setUltimaVisita(error ? null : (data ?? null)) })
      .catch(() => { if (vivo) setUltimaVisita(null) })
    return () => { vivo = false }
  }, [])

  // A DATA DO ULTIMO DESCARREGAMENTO DESTE CLIENTE.
  //
  // Le-se a entrada e guarda-se, em vez de se ir buscar no momento do clique. O
  // browser so deixa disparar um download se ele nascer do gesto do utilizador,
  // e um `await` pelo meio quebra essa ligacao — os ficheiros eram bloqueados
  // sem aviso. Aqui, ao carregar no botao, a data ja ca esta.
  //
  // undefined = ainda a carregar; null = nunca descarregou nada.
  const [ultimoDownload, setUltimoDownload] = React.useState(undefined)
  React.useEffect(() => {
    let vivo = true
    supabase.rpc('meu_ultimo_download')
      .then(({ data, error }) => { if (vivo) setUltimoDownload(error ? null : (data ?? null)) })
      .catch(() => { if (vivo) setUltimoDownload(null) })
    return () => { vivo = false }
  }, [])

  const novoIds = React.useMemo(() => {
    const ids = new Set()
    if (!produtos || produtos.length === 0) return ids
    // Enquanto nao se souber da ultima visita nao se marca nada. Marcar tudo e
    // depois desmarcar dava um piscar de 800 distintivos a cada carregamento.
    if (ultimaVisita === undefined) return ids

    const agora = Date.now()
    const dias = (n) => agora - n * 24 * 60 * 60 * 1000
    // Sem historico, ve o que quem chega veria. Com historico antigo demais,
    // trava-se nos 30 dias — senao "novo" passava a ser o catalogo inteiro.
    const limite = ultimaVisita
      ? Math.max(new Date(ultimaVisita).getTime(), dias(DIAS_NOVO_MAX))
      : dias(DIAS_NOVO_SEM_HISTORICO)

    for (const p of produtos) {
      // PUBLICADO MAS AINDA POR ANUNCIAR TAMBEM E NOVO.
      //
      // O carimbo e posto pelo email de novidades, que passou a sair uma vez por
      // semana. Sem esta linha, um artigo publicado na quarta ficava sem marca
      // ate terca seguinte: o cliente entrava, tinha uma centena de artigos
      // novos a frente e nenhum assinalado.
      //
      // Nao ha risco de marcar o catalogo inteiro: as sincronizacoes inserem os
      // artigos ja carimbados e invisiveis, portanto sem carimbo E visivel so
      // acontece a quem acabou de ser publicado.
      if (!p.notificado_cliente_em) {
        if (p.visivel_catalogo) ids.add(p.id)
        continue
      }
      if (new Date(p.notificado_cliente_em).getTime() >= limite) ids.add(p.id)
    }
    return ids
  }, [produtos, ultimaVisita])

  // O QUE VAI NO FICHEIRO DOS NOVOS — conta desde o ultimo DESCARREGAMENTO.
  //
  // E de proposito que nao e o mesmo que o distintivo verde do ecra. Sao duas
  // perguntas diferentes: o ecra responde a "o que apareceu desde que ca estive"
  // e o ficheiro a "o que ainda nao entrou no meu sistema". Um cliente que
  // espreita todos os dias e descarrega a segunda tem as duas respostas muito
  // distantes — e a do ficheiro e a que lhe interessa.
  //
  // Sem descarregamento anterior (conta nova, ou a primeira vez), vale a regra
  // do ecra: assim ninguem recebe o catalogo inteiro como se fosse novidade.
  // ── A MODAL DAS NOVIDADES ────────────────────────────────────────────────
  //
  // Substitui o email para quem ja ca esta. O email das 10h deixou de sair a
  // quem entrou depois da publicacao das 9h (ver notificar-produtos-novos), e
  // sem nada no lugar essa pessoa perdia o empurrao: o distintivo verde e
  // discreto de proposito e nao se ve de um ecra cheio de artigos.
  //
  // UMA VEZ POR SEPARADOR, guardado no sessionStorage e nao no localStorage:
  // uma modal que volta a cada F5 irrita mais do que o email que veio substituir,
  // e uma que so aparece uma vez por computador nunca mais aparecia no telemovel.
  // Fechar o separador e comecar de novo e o comportamento certo.
  //
  // O sessionStorage pode nao existir (janela anonima, definicoes fechadas) e
  // nesse caso rebenta a leitura — por isso vai em try, e sem ele a modal
  // aparece uma vez por carregamento, que e o mal menor.
  const [novidadesAbertas, setNovidadesAbertas] = useState(false)
  React.useEffect(() => {
    if (isAdmin && !viewAsEmail) return          // o admin tem o distintivo e mais nada
    if (ultimaVisita === undefined) return       // ainda nao se sabe o que e novo
    if (novoIds.size === 0) return
    const chave = 'novidades-vistas'
    try { if (sessionStorage.getItem(chave)) return } catch { /* sem sessionStorage */ }
    const t = setTimeout(() => {
      setNovidadesAbertas(true)
      try { sessionStorage.setItem(chave, '1') } catch { /* idem */ }
    }, 700)
    return () => clearTimeout(t)
  }, [isAdmin, viewAsEmail, ultimaVisita, novoIds])

  const novosParaFicheiro = React.useMemo(() => {
    if (!ultimoDownload) return novoIds
    const limite = new Date(ultimoDownload).getTime()
    const ids = new Set()
    for (const p of (produtos || [])) {
      // Publicado e ainda por anunciar tambem conta — o carimbo so e posto pelo
      // email das 10h, e o lote sai as 9h. Sem isto, quem descarregasse entre as
      // duas horas levava o ficheiro sem os artigos desse dia.
      if (!p.notificado_cliente_em) { if (p.visivel_catalogo) ids.add(p.id); continue }
      if (new Date(p.notificado_cliente_em).getTime() > limite) ids.add(p.id)
    }
    return ids
  }, [produtos, ultimoDownload, novoIds])


  // Os prazos que existem de facto, com quantos artigos cada um tem. Sai dos
  // produtos ja filtrados por tudo o resto menos o proprio prazo, para as
  // contagens dizerem o que se vai ver ao carregar e nao o total do catalogo.
  const prazosDisponiveis = React.useMemo(() => {
    const m = new Map()
    for (const p of (produtos || [])) {
      const d = Number(p.Lead_Time)
      if (!(d > 0)) continue
      m.set(d, (m.get(d) || 0) + 1)
    }
    return [...m.entries()].map(([dias, n]) => ({ dias, n })).sort((a, b) => a.dias - b.dias)
  }, [produtos])

  // Email do cliente atual (para o filtro "Os meus produtos")
  const emailMe = (viewAsEmail || user?.email || '').toLowerCase()

  const produtosFiltrados = (() => {
    let list = produtos.filter(p => {
      const matchSearch = (p.Nome || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.EAN && String(p.EAN).includes(search))
      const matchCat = ['todos','novos','favoritos','sem_custo','meus','risco'].includes(abaCategoria) || p.categoria === abaCategoria
      // Subcategoria: so filtra quando ha uma escolhida. Vazio = a categoria toda.
      const matchSub = !subFiltro || p.subcategoria === subFiltro
      const matchNovo = abaCategoria !== 'novos' || novoIds.has(p.id)
      const matchFav = abaCategoria !== 'favoritos' || favoritos.has(p.id)
      const matchSemCusto = abaCategoria !== 'sem_custo' || p.preco_custo == null
      const matchMeus = abaCategoria !== 'meus' || (p.Email_Cliente && String(p.Email_Cliente).toLowerCase() === emailMe)
      const matchRisco = abaCategoria !== 'risco' || !!p.zerado_desde
      const matchPrazo = prazoFiltro == null || Number(p.Lead_Time) === prazoFiltro
      return matchSearch && matchCat && matchSub && matchNovo && matchFav && matchSemCusto && matchMeus && matchRisco && matchPrazo
    })
    // Ordenação: cliente pode escolher 'frequencia' (default) ou 'nome'.
    // Admin fica sempre por nome (ordem alfabética).
    if (!isAdmin && ordenacao === 'frequencia' && frequenciaCompra.size > 0) {
      list = [...list].sort((a, b) => {
        const fa = frequenciaCompra.get(a.Nome) || 0
        const fb = frequenciaCompra.get(b.Nome) || 0
        if (fb !== fa) return fb - fa
        return (a.Nome || '').localeCompare(b.Nome || '')
      })
    }
    return list
  })()

  // helpers de estilo reutilizável
  const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }
  const inputStyle = { width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Resultado do email de exclusividade — fica até ser fechado, para não passar despercebido */}
      {avisoEmail && (
        <div style={{
          position: 'fixed', top: '70px', right: '24px', zIndex: 3000, maxWidth: '360px',
          background: avisoEmail.tipo === 'ok' ? C.successSoft : C.dangerSoft,
          border: `1px solid ${avisoEmail.tipo === 'ok' ? C.success : C.danger}`,
          color: avisoEmail.tipo === 'ok' ? C.success : C.danger,
          borderRadius: '6px', padding: '10px 12px', fontSize: '12px', fontWeight: '700',
          display: 'flex', alignItems: 'flex-start', gap: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
        }}>
          {avisoEmail.tipo === 'ok' ? <CheckCircle size={15} style={{ flexShrink: 0, marginTop: '1px' }} /> : <AlertCircle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />}
          <span style={{ flex: 1 }}>{avisoEmail.texto}</span>
          <button onClick={() => setAvisoEmail(null)} title="Fechar"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '3px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: 0 }}>Catálogo de Produtos</h1>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', background: C.successSoft, color: C.success, border: `1px solid ${C.success}55`, borderRadius: '14px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Truck size={11} /> Portes Grátis
              </span>
            </div>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
              {loading ? '...' : `${produtosFiltrados.length} produto(s) disponível(eis)`}
              {isAdmin && <span style={{ marginLeft: '8px', fontSize: '11px', padding: '1px 7px', background: C.surfaceAlt, color: C.primary, borderRadius: '10px', border: `1px solid ${C.primary}33` }}>Vista Admin — todos os produtos</span>}
              {isAdmin && !loading && (() => {
                const totalVisivel = produtos.filter(p => p.visivel_catalogo).length
                const comMercado = produtos.filter(p => p.visivel_catalogo && p.preco_mercado != null).length
                if (totalVisivel === 0) return null
                const completo = comMercado === totalVisivel
                return (
                  <span style={{ marginLeft: '6px', fontSize: '11px', padding: '1px 7px', background: completo ? C.successSoft : C.warningSoft, color: completo ? C.success : C.warning, borderRadius: '10px', border: `1px solid ${completo ? C.success : C.warning}33`, fontWeight: '600' }}>
                    Preço mercado: {comMercado}/{totalVisivel}
                  </span>
                )
              })()}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Admin nao ve carrinho por defeito (encomenda em nome de clientes).
                Mas se ja la tiver posto alguma coisa, tem de conseguir abri-lo —
                senao o carrinho fica preso sem saida. A RPC ja permite ao admin
                encomendar em nome proprio; era so a interface que escondia. */}
            {(!isAdmin || encomendarComoEmail || carrinho.length > 0) && (
              <button onClick={abrirCarrinho}
                style={{ height: '34px', padding: '0 14px', background: carrinho.length > 0 ? C.primary : C.surface, border: `1px solid ${C.border}`, borderRadius: '2px', color: carrinho.length > 0 ? '#fff' : C.text, fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ShoppingCart size={14} />
                {carrinho.length > 0 && <span style={{ background: C.danger, color: '#fff', borderRadius: '10px', padding: '0 6px', fontSize: '11px', fontWeight: '700' }}>{carrinho.length}</span>}
              </button>
            )}
            {/* O carrinho passa a maior parte do tempo fechado. Esta pastilha
                mantem o objectivo a vista enquanto ele navega, que e quando
                ainda da para acrescentar artigos. */}
            {incentivoInfo && (
              <div title={incentivoInfo.atingido
                    ? `Desconto de ${incentivoInfo.pct}% activo nesta encomenda`
                    : `A partir de ${fmt2(incentivoInfo.limiar)} € s/IVA tens ${incentivoInfo.pct}% de desconto`}
                style={{
                  height: '34px', padding: '0 12px', borderRadius: '2px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3px',
                  background: incentivoInfo.atingido ? C.successSoft : C.surface,
                  border: `1px solid ${incentivoInfo.atingido ? C.success : C.border}`,
                  minWidth: '150px',
                }}>
                <span style={{ fontSize: '11px', fontWeight: '700', lineHeight: 1,
                  color: incentivoInfo.atingido ? C.success : C.textMuted }}>
                  {incentivoInfo.atingido
                    ? `${incentivoInfo.pct}% de desconto activo`
                    : `Faltam ${fmt2(incentivoInfo.falta)} € para ${incentivoInfo.pct}%`}
                </span>
                <div style={{ background: C.border, borderRadius: '2px', height: '3px', overflow: 'hidden' }}>
                  <div style={{ background: incentivoInfo.atingido ? C.success : C.warning,
                    height: '100%', width: `${incentivoInfo.progresso}%`, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            {isAdmin && (
              <>
                <button onClick={() => setShowEdicaoMassa(true)} title="Edição em massa de preços"
                  style={{ height: '34px', padding: '0 14px', background: C.surface, border: `1px solid ${C.primary}`, borderRadius: '2px', color: C.primary, fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tag size={14} /> Edição em massa
                </button>
                <button onClick={abrirNovoProduto}
                  style={{ height: '34px', padding: '0 14px', background: C.primary, border: 'none', borderRadius: '2px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Plus size={14} /> Adicionar Produto
                </button>
              </>
            )}
          </div>
        </div>
        {/* Selector: admin "encomendar como cliente" — fica registado em nome desse cliente */}
        {isAdmin && !viewAsEmailProp && (
          <div style={{ marginBottom: '14px', padding: '10px 14px', background: encomendarComoEmail ? '#fff4ce' : C.surfaceAlt, border: `1px solid ${encomendarComoEmail ? C.warning : C.border}`, borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: encomendarComoEmail ? '#92400e' : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              A encomendar como:
            </span>
            <select
              value={encomendarComoEmail || ''}
              onChange={e => {
                const novo = e.target.value || null
                if (novo === (encomendarComoEmail || null)) return
                // Limpar carrinho ao trocar de cliente (preços/descontos podem ser diferentes)
                if (carrinho.length > 0) {
                  if (!confirm('Mudar o cliente vai limpar o carrinho actual. Continuar?')) {
                    e.target.value = encomendarComoEmail || ''
                    return
                  }
                  limparCarrinho()
                }
                setEncomendarComoEmail(novo)
              }}
              style={{ flex: '0 1 280px', height: '30px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff', cursor: 'pointer', fontWeight: '600', color: C.text }}
            >
              <option value="">👤 Admin (eu)</option>
              {clientes.filter(c => c.email !== user?.email).map(c => (
                <option key={c.id} value={c.email}>{c.nome || c.email}</option>
              ))}
            </select>
            {encomendarComoEmail && (
              <button
                onClick={() => { if (carrinho.length === 0 || confirm('Sair do modo "encomendar como" vai limpar o carrinho. Continuar?')) { limparCarrinho(); setEncomendarComoEmail(null) } }}
                style={{ height: '26px', padding: '0 10px', background: 'transparent', border: `1px solid ${C.warning}`, borderRadius: '4px', color: '#92400e', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
              >
                Voltar a admin
              </button>
            )}
            {encomendarComoEmail && perfilCliente && (
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#92400e', fontWeight: '600' }}>
                ⚠ Encomenda será gravada em nome de <strong>{perfilCliente.nome || perfilCliente.email}</strong>
              </span>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', borderBottom: `2px solid ${C.border}`, paddingBottom: '0' }}>
          {(() => {
            const meusCount = emailMe ? produtos.filter(p => p.Email_Cliente && String(p.Email_Cliente).toLowerCase() === emailMe).length : 0
            const riscoCount = produtos.filter(p => !!p.zerado_desde).length
            return [
              { key: 'todos', label: 'Todos', icon: <Package size={13} /> },
              // Categorias vindas da BD, e SO as que tem artigos nesta vista.
              // Um cliente com 3 artigos de perfumaria ve um separador, nao
              // seis — o ecra adapta-se a quem esta a olhar. E acrescentar uma
              // categoria nova e uma linha na tabela `categorias`, sem deploy.
              ...categorias
                .filter(c => produtos.some(p => p.categoria === c.slug))
                .map(c => ({
                  key: c.slug,
                  label: c.nome,
                  icon: ICONE_CATEGORIA[c.slug] || <Tag size={13} />,
                  count: produtos.filter(p => p.categoria === c.slug).length,
                })),
              ...((!isAdmin || viewAsEmail) ? [{ key: 'meus', label: 'Os meus produtos', icon: <User size={13} />, count: meusCount }] : []),
              { key: 'novos', label: 'Novos',
                // Diz de onde vem a conta. "Novos" sozinho nao explica porque e
                // que hoje sao 18 e ontem eram 40 — e a resposta ("desde que ca
                // estiveste") e tambem a razao para voltar amanha.
                titulo: ultimaVisita
                  ? `Anunciados desde a tua ultima visita, a ${new Date(ultimaVisita).toLocaleDateString('pt-PT')}`
                  : 'Anunciados nos ultimos dias',
                icon: <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: C.success }} />, count: novoIds.size },
              ...((!isAdmin || viewAsEmail) ? [{ key: 'favoritos', label: 'Favoritos', icon: <Heart size={13} fill={favoritos.size > 0 ? C.danger : 'none'} color={C.danger} />, count: favoritos.size }] : []),
              ...(isAdmin && !viewAsEmail ? [{ key: 'sem_custo', label: 'Sem custo', icon: <AlertCircle size={13} color={C.warning} />, count: produtos.filter(p => p.preco_custo == null).length }] : []),
              ...(isAdmin && !viewAsEmail ? [{ key: 'risco', label: 'Risco de libertação', icon: <span style={{ fontSize: '13px' }}>⏳</span>, count: riscoCount }] : []),
            ]
          })().map(tab => {
            const ativa = abaCategoria === tab.key
            const isNovos = tab.key === 'novos'
            const isFavoritos = tab.key === 'favoritos'
            const isSemCusto = tab.key === 'sem_custo'
            const isMeus = tab.key === 'meus'
            const isRisco = tab.key === 'risco'
            const corAtiva = isNovos ? C.success : isFavoritos ? C.danger : isSemCusto ? C.warning : isMeus ? C.purple : isRisco ? C.danger : C.primary
            const corBadge = corAtiva
            // "Sem custo" com fundo amarelado quando há produtos por preencher (chama atenção mesmo inativa)
            const semCustoAttention = isSemCusto && tab.count > 0 && !ativa
            // "Risco" com fundo vermelho suave quando ha produtos elegiveis (chama atencao mesmo inativa)
            const riscoAttention = isRisco && tab.count > 0 && !ativa
            const bgAttention = semCustoAttention ? C.warningSoft : riscoAttention ? C.dangerSoft : (ativa && isSemCusto ? C.warningSoft : (ativa && isRisco ? C.dangerSoft : 'none'))
            const colorAttention = semCustoAttention ? C.warning : riscoAttention ? C.danger : null
            return (
              <button key={tab.key} title={tab.titulo || undefined} onClick={() => { setAbaCategoria(tab.key); setSubFiltro(''); setQuantosMostrar(LOTE_CARTOES) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 16px', border: 'none',
                  background: bgAttention,
                  cursor: 'pointer', fontSize: '13px',
                  fontWeight: ativa || semCustoAttention || riscoAttention ? '700' : '500',
                  color: ativa ? corAtiva : (colorAttention || C.textMuted),
                  borderBottom: ativa ? `2px solid ${corAtiva}` : '2px solid transparent',
                  marginBottom: '-2px', borderRadius: '0', transition: 'color .15s',
                }}>
                {tab.icon} {tab.label}
                {(isNovos || isFavoritos || isSemCusto || isRisco) && tab.count > 0 && (
                  <span style={{ background: corBadge, color: '#fff', borderRadius: '10px', padding: '0 6px', fontSize: '10px', fontWeight: '800', minWidth: '16px', textAlign: 'center', marginLeft: '2px' }}>
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
          {/* Seletor de ordenação (só cliente) */}
          {!isAdmin && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ArrowDownUp size={12} color={C.textMuted} />
              <select value={ordenacao} onChange={e => setOrdenacao(e.target.value)}
                style={{ height: '28px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '12px', background: C.surface, cursor: 'pointer', color: C.text }}>
                <option value="frequencia">Mais comprados por mim</option>
                <option value="nome">Nome (A-Z)</option>
              </select>
            </div>
          )}
        </div>

        {/* PRAZO DE ENTREGA — linha propria, de proposito.
            Categoria e prazo sao perguntas diferentes e combinam-se: "perfumes
            que chegam em 2 dias". Na mesma fila das categorias, o desenho dizia
            que eram alternativas — carregar num perdia o outro. */}
        {prazosDisponiveis.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', margin: '10px 0 0' }}>
            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', marginRight: '2px' }}>Chega em</span>
            {[{ dias: null, n: null }, ...prazosDisponiveis].map(op => {
              const activa = prazoFiltro === op.dias
              return (
                <button key={op.dias ?? 'todos'}
                  onClick={() => { setPrazoFiltro(op.dias); setQuantosMostrar(LOTE_CARTOES) }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '4px 11px', borderRadius: '13px', cursor: 'pointer',
                    border: `1px solid ${activa ? C.primary : C.border}`,
                    background: activa ? C.primary : C.surface,
                    color: activa ? '#fff' : C.text,
                    fontSize: '12px', fontWeight: activa ? '700' : '500',
                  }}>
                  {op.dias == null ? 'qualquer prazo' : `${op.dias} dias`}
                  {op.n != null && (
                    <span style={{ fontSize: '10px', fontWeight: '700', opacity: activa ? 0.85 : 0.55 }}>{op.n}</span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', marginTop: '14px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: '380px' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setQuantosMostrar(LOTE_CARTOES) }} placeholder="Pesquisar por nome ou EAN…"
              style={{ width: '100%', height: '34px', padding: '0 10px 0 32px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {/* Exporta o que esta no ecra. O numero no botao diz quantas linhas
              saem, para nao haver surpresa ao abrir o ficheiro. */}
          {produtosFiltrados.length > 0 && (
            <button onClick={exportarCatalogo} title="Descarregar em CSV — abre no Excel"
              style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.surface, color: C.text, cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              <Download size={13} /> Exportar {produtosFiltrados.length}
            </button>
          )}
        </div>
      </div>

      {/* ── Subcategorias: 2.o nivel dentro da categoria escolhida ──
          Sai dos produtos, nao de uma lista fixa: mostra so as subcategorias
          que existem naquela categoria e para aquele utilizador. So aparece
          quando ha mais do que uma — com uma so, seria um botao inutil. */}
      {(() => {
        if (!categorias.some(c => c.slug === abaCategoria)) return null
        const subs = [...new Set(
          produtos.filter(p => p.categoria === abaCategoria && p.subcategoria)
                  .map(p => p.subcategoria)
        )].sort((a, b) => a.localeCompare(b, 'pt'))
        if (subs.length < 2) return null
        const chip = (activo) => ({
          height: '26px', padding: '0 11px', borderRadius: '13px', cursor: 'pointer',
          fontSize: '11.5px', fontWeight: activo ? '700' : '500',
          border: `1px solid ${activo ? C.primary : C.border}`,
          background: activo ? C.primary : C.surface,
          color: activo ? '#fff' : C.text, whiteSpace: 'nowrap',
        })
        return (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', padding: '0 28px', marginBottom: '14px', marginTop: '-6px' }}>
            <button onClick={() => { setSubFiltro(''); setQuantosMostrar(LOTE_CARTOES) }} style={chip(!subFiltro)}>Todas</button>
            {subs.map(sc => (
              <button key={sc} onClick={() => { setSubFiltro(sc === subFiltro ? '' : sc); setQuantosMostrar(LOTE_CARTOES) }} style={chip(subFiltro === sc)}>
                {sc}
              </button>
            ))}
          </div>
        )
      })()}

      {/* ── Grelha de cartões ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 28px 28px' }}>
        {error && (
          <div style={{ padding: '16px', backgroundColor: C.dangerSoft, border: `1px solid ${C.danger}`, borderRadius: '4px', color: C.danger, fontSize: '13px', marginBottom: '16px' }}>
            <AlertCircle size={14} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {error}
          </div>
        )}
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '10px', color: C.textMuted }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> A carregar...
          </div>
        ) : produtosFiltrados.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '12px', color: C.textMuted }}>
            <Package size={48} style={{ opacity: 0.2 }} />
            <p style={{ fontSize: '15px', margin: 0, textAlign: 'center' }}>
              {search
                ? 'Nenhum produto encontrado.'
                : abaCategoria === 'meus'
                  ? 'Ainda não tem produtos associados a si. Quando comprar algum produto, fica aqui automaticamente.'
                  : 'Nenhum produto disponível no catálogo de momento.'}
            </p>
            {isAdmin && !search && (
              <button onClick={abrirNovoProduto}
                style={{ height: '34px', padding: '0 14px', background: C.primary, border: 'none', borderRadius: '2px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={14} /> Adicionar primeiro produto
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '18px' }}>
            {produtosFiltrados.slice(0, quantosMostrar).map(p => {
              // Aplicar diferencial de portes para clientes standard (visivel a este cliente especifico)
              // Particular usa preco_particular (já inclui markup + portes); B2B usa preco + diferencial portes
              // Modo encomendar interno: preco_custo (com desc fornecedor) + portes cobrados (1€) - consumo interno
              const semCustoInterno = modoEncomendarInterno && (p.preco_custo == null || Number(p.preco_custo) <= 0)
              const semIva = modoEncomendarInterno
                ? Number(p.preco_custo || 0) + portesCustoDoProduto(p)
                : isParticular
                  ? Number(p.preco_particular || 0)
                  : Number(p.preco || 0) + portesDiff
              const comIva = semIva * (1 + IVA)
              const associado = p.Email_Cliente
              const associadoAMim = associado && associado === user?.email
              const formato = extrairFormato(p.Nome)
              return (
                <div
                  key={p.id}
                  onMouseEnter={ev => { ev.currentTarget.style.transform = 'translateY(-2px)'; ev.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)' }}
                  onMouseLeave={ev => { ev.currentTarget.style.transform = 'translateY(0)'; ev.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)' }}
                  style={{
                    backgroundColor: C.surface,
                    border: `1px solid ${associadoAMim ? `${C.primary}30` : C.border}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                  }}>

                  {/* Foto */}
                  <div style={{
                    position: 'relative',
                    height: '200px',
                    background: '#f7f7f7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                    borderBottom: `1px solid ${C.border}`,
                  }}>
                    {p.foto_url
                      // 'contain' (não 'cover'): encolhe até caber inteira em vez de cortar
                      // o que sai fora da janela. Padding no <img> — no contentor deslocava
                      // as etiquetas e o botão que estão em position:absolute.
                      ? <img src={p.foto_url} alt={p.Nome}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }}
                          onError={ev => {
                            ev.target.style.display = 'none'
                            const fallback = ev.target.nextElementSibling
                            if (fallback) fallback.style.display = 'flex'
                          }} />
                      : null
                    }
                    {/* Placeholder visivel quando foto_url e' nula ou da' erro de carregamento */}
                    <div style={{
                      display: p.foto_url ? 'none' : 'flex',
                      position: 'absolute', inset: 0,
                      alignItems: 'center', justifyContent: 'center',
                      background: '#f7f7f7',
                    }}>
                      <Package size={48} style={{ color: '#d1d1d1' }} />
                    </div>

                    {/* Status badges (top-left) */}
                    {(isAdmin || associadoAMim || novoIds.has(p.id)) && (
                      <div style={{ position: 'absolute', top: '10px', left: '10px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        {novoIds.has(p.id) && (
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px',
                            fontSize: '10px', fontWeight: '800', letterSpacing: '0.5px',
                            backgroundColor: C.success, color: '#fff',
                            boxShadow: '0 2px 8px rgba(16,124,16,0.35)',
                            textTransform: 'uppercase',
                          }}>
                            ✦ Novo
                          </span>
                        )}
                        {!isAdmin && associadoAMim && (
                          <span style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', backgroundColor: C.primary, color: '#fff', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}>
                            <User size={10} /> Meu produto
                          </span>
                        )}
                        {isAdmin && !p.visivel_catalogo && (
                          <span style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', backgroundColor: '#fff', color: C.danger, border: `1px solid ${C.danger}40` }}>Arquivado</span>
                        )}
                        {isAdmin && p.preco_manual && (
                          <span style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', backgroundColor: '#fff', color: C.warning, border: `1px solid ${C.warning}40` }}>Preço manual</span>
                        )}
                        {isAdmin && p.zerado_desde && (() => {
                          const diasZerado = Math.floor((Date.now() - new Date(p.zerado_desde).getTime()) / (1000 * 60 * 60 * 24))
                          const diasParaLibertar = Math.max(0, 15 - diasZerado)
                          const critico = diasParaLibertar <= 3
                          const avisado = !!p.aviso_libertacao_em
                          const dono = p.Email_Cliente || '?'
                          const zeradoStr = new Date(p.zerado_desde).toLocaleDateString('pt-PT')
                          const tip = avisado
                            ? `Dono: ${dono}\nZerado desde ${zeradoStr} (${diasZerado}d)\nAviso enviado ao cliente em ${new Date(p.aviso_libertacao_em).toLocaleDateString('pt-PT')}\nLiberta em ${diasParaLibertar}d — passa a público e todos os clientes recebem email`
                            : `Dono: ${dono}\nZerado desde ${zeradoStr} (${diasZerado}d)\nSe nada mudar, o cliente recebe aviso em ${Math.max(0, 12 - diasZerado)}d e liberta em ${diasParaLibertar}d (passa a público)`
                          const bg = avisado ? '#0078d4' : (critico ? C.danger : C.warning)
                          const icone = avisado ? '🔔' : '⏳'
                          const label = avisado ? `Avisado · ${diasParaLibertar}d` : `Liberta em ${diasParaLibertar}d`
                          return (
                            <span title={tip}
                              style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '10px', fontWeight: '800', backgroundColor: bg, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {icone} {label}
                            </span>
                          )
                        })()}
                        {isAdmin && p.ex_dono_email && (
                          <span title={`Ex-dono: ${p.ex_dono_email} — se ele voltar a comprar, é auto-reassociado`}
                            style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', backgroundColor: '#fff', color: C.primary, border: `1px solid ${C.primary}55` }}>
                            🔓 Libertado
                          </span>
                        )}
                      </div>
                    )}

                    {/* Edit button (top-right, admin) */}
                    {isAdmin && (
                      <button onClick={() => abrirEditarProduto(p)} title="Editar produto"
                        style={{
                          position: 'absolute', top: '10px', right: '10px',
                          width: '34px', height: '34px',
                          background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                        }}>
                        <Edit2 size={14} />
                      </button>
                    )}

                    {/* Favorito (top-right, cliente) */}
                    {!isAdmin && (() => {
                      const isFav = favoritos.has(p.id)
                      return (
                        <button onClick={(e) => { e.stopPropagation(); toggleFavorito(p.id) }}
                          title={isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                          style={{
                            position: 'absolute', top: '10px', right: '10px',
                            width: '34px', height: '34px',
                            background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: isFav ? C.danger : C.textMuted,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                          }}>
                          <Heart size={15} fill={isFav ? C.danger : 'none'} />
                        </button>
                      )
                    })()}

                    {/* Seguir precos de mercado — so para quem tem o separador.
                        Por baixo do coracao, para nao competir com ele. */}
                    {!isAdmin && podeSeguir && (() => {
                      const seg = seguidos.has(p.id)
                      const cheio = !seg && seguidos.size >= 15
                      return (
                        <button onClick={(e) => { e.stopPropagation(); toggleSeguir(p.id) }}
                          disabled={aSeguir === p.id}
                          title={seg
                            ? 'A seguir os preços de mercado — carrega para deixar de seguir'
                            : cheio
                              ? 'Já estás a seguir 15 artigos'
                              : `Seguir os preços de mercado (restam ${15 - seguidos.size} de 15)`}
                          style={{
                            position: 'absolute', top: '50px', right: '10px',
                            width: '34px', height: '34px',
                            background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%',
                            cursor: aSeguir === p.id ? 'wait' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: seg ? C.primary : (cheio ? C.textFaint : C.textMuted),
                            opacity: cheio ? 0.55 : 1,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                          }}>
                          <Globe2 size={15} />
                        </button>
                      )
                    })()}
                  </div>

                  {/* Body */}
                  <div style={{ padding: '14px 14px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>

                    {/* Categoria + cliente associado (admin) */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '8px', minHeight: '18px' }}>
                      {p.categoria ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '2px 8px', borderRadius: '10px',
                          fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px',
                          background: p.categoria === 'perfumes' ? '#f3e5f5' : '#e0f7fa',
                          color: p.categoria === 'perfumes' ? '#7b1fa2' : '#00838f'
                        }}>
                          {p.categoria === 'perfumes' ? <Sparkles size={10} /> : <Droplets size={10} />}
                          {p.categoria === 'perfumes' ? 'Perfume' : 'Cosmético'}
                        </span>
                      ) : <span />}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        {isAdmin && associado && (
                          <span title={associado}
                            style={{ fontSize: '10px', color: C.success, fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <User size={10} /> {associado.split('@')[0]}
                          </span>
                        )}
                        {/* Formato/tamanho — sempre visível, independente do corte do nome */}
                        {formato && (
                          <span title={`Formato: ${formato}`}
                            style={{
                              padding: '2px 8px', borderRadius: '10px',
                              fontSize: '10.5px', fontWeight: '800', letterSpacing: '0.2px',
                              background: C.text, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0,
                            }}>
                            {formato}
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Nome — SEM corte, de propósito.
                        Havia aqui um limite de 3 linhas. Mas em produtos da mesma marca e
                        formato (ex: La Mer Soft Cream vs Cream, ambos 60ml) a única palavra
                        que os distingue pode estar no fim do nome — e cortá-la leva o cliente
                        a encomendar o artigo errado. O nome inteiro vale mais do que cards
                        todos com a mesma altura. */}
                    <p title={p.Nome} style={{
                      margin: 0,
                      fontSize: '14px', fontWeight: '700',
                      color: C.text, lineHeight: '1.35',
                      minHeight: '38px',
                      overflowWrap: 'anywhere',   // parte nomes colados sem espaços em vez de os deixar sair do card
                    }}>
                      {p.Nome}
                      <CopyButton text={p.Nome} size={11} title="Copiar nome do artigo" />
                    </p>

                    {/* EAN */}
                    {p.EAN && (
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.textMuted, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Hash size={10} /> {p.EAN}
                        <CopyButton text={p.EAN} size={11} title="Copiar EAN" />
                      </p>
                    )}

                    {/* Spacer */}
                    <div style={{ flex: 1, minHeight: '10px' }} />

                    {/* O QUE ELE TEM, PARA SABER QUANTO PEDIR ─────────────────
                        So nos artigos DELE. O stock e os transitos ja vinham no
                        pedido que o cliente faz — sao precisos para outras contas
                        do ecra — apenas nao se mostravam. Nao ha consulta nova nem
                        dado novo a sair daqui.
                        Nos artigos do catalogo geral nao se mostra: ali o stock e
                        o nosso, e encomenda-se ao fornecedor de qualquer maneira.
                        Ver "0 em stock" em tudo assustava sem razao e mostrava o
                        inventario a todos os clientes. */}
                    {(() => {
                      const meu = p.Email_Cliente && String(p.Email_Cliente).toLowerCase() === emailMe
                      if (!meu) return null
                      const stock = Number(p.Stock_Atual || 0)
                      // AGRUPADO POR DATA E NUMA LINHA SO.
                      //
                      // Primeiro mostrava-se o total e por baixo cada lote. Com um
                      // lote so, lia-se "mais 60 a caminho" e logo abaixo "+60 a
                      // 04/10" — parecia cento e vinte. O total diz-se uma vez, e
                      // as datas vao entre parenteses.
                      //
                      // Dois lotes que chegam no mesmo dia sao uma entrada so para
                      // quem recebe: somam-se.
                      const porData = new Map()
                      for (const n of SLOTS_TRANSITO) {
                        const qtd = Number(p[`Transito${n}_Qtd`] || 0)
                        if (qtd <= 0) continue
                        const d = p[`Transito${n}_Data`] ? String(p[`Transito${n}_Data`]).slice(0, 10) : ''
                        porData.set(d, (porData.get(d) || 0) + qtd)
                      }
                      const aCaminho = [...porData.entries()]
                        .map(([data, qtd]) => ({ data, qtd }))
                        .sort((a, b) => String(a.data).localeCompare(String(b.data)))
                      const totalACaminho = aCaminho.reduce((t, x) => t + x.qtd, 0)
                      // Sem stock e sem nada a caminho nao se mostra caixa nenhuma:
                      // um artigo dele que esteja a zero ja se percebe pelo resto.
                      if (stock === 0 && totalACaminho === 0) return null
                      const dia = (d) => {
                        if (!d) return 'sem data'
                        const partes = String(d).slice(0, 10).split('-')
                        return partes.length === 3 ? `${partes[2]}/${partes[1]}` : String(d)
                      }
                      return (
                        <div style={{ marginBottom: '10px', padding: '8px 10px', background: stock > 0 ? C.surfaceAlt : C.warningSoft, border: `1px solid ${stock > 0 ? C.border : C.warning + '55'}`, borderRadius: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                            <Package size={12} style={{ color: stock > 0 ? C.textMuted : C.warning, alignSelf: 'center' }} />
                            <span style={{ fontSize: '11px', color: C.textMuted }}>Tens em stock</span>
                            <strong style={{ fontSize: '14px', fontWeight: '800', color: stock > 0 ? C.text : C.warning }}>{stock} un.</strong>
                            {totalACaminho > 0 && (
                              <span style={{ fontSize: '11px', color: C.textMuted }}>
                                · mais <strong style={{ color: C.text, fontWeight: '700' }}>{totalACaminho} un.</strong> a caminho
                                {aCaminho.length === 1
                                  ? `, chega ${dia(aCaminho[0].data)}`
                                  : ` (${aCaminho.map(t => `${t.qtd} a ${dia(t.data)}`).join(', ')})`}
                              </span>
                            )}
                          </div>

                        </div>
                      )
                    })()}

                    {/* Quantidade mínima de encomenda + Vendas mensais */}
                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: C.textMuted, marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Mín. encomenda: <strong style={{ color: C.text, fontWeight: '700' }}>{qtdMinimaDoProduto(p)} un.</strong>
                      </span>
                      {/* O PRAZO NO CARTAO, e nao so no filtro.
                          Com o filtro sozinho, o cliente teria de confiar que
                          esta a ver o que pediu. Com o prazo a vista, confirma —
                          e ve-o mesmo quando nao filtra nada. */}
                      {Number(p.Lead_Time) > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Chega em <strong style={{ color: C.text, fontWeight: '700' }}>{Number(p.Lead_Time)} dias</strong>
                        </span>
                      )}
                      {(() => {
                        const podeEditar = isAdmin || (user?.email && p.Email_Cliente && p.Email_Cliente.toLowerCase() === String(user.email).toLowerCase())
                        if (!podeEditar) return null
                        const v = p.Vendas_Mensais ?? 0
                        const isManual = !!p.vendas_mensais_manual
                        const diasDados = Number(p.vendas_mensais_dias_dados || 0)
                        const diasActivos = Number(p.vendas_mensais_dias_activos || 0)
                        const capturadas = Number(p.vendas_mensais_capturadas || 0)
                        const calcEm = p.vendas_mensais_calculadas_em
                        const MIN_DIAS = 20
                        const JANELA = 30
                        let estado, icone, cor, bg, border, tip
                        if (isManual) {
                          estado = 'MANUAL'; icone = '🔒'; cor = C.warning; bg = C.warningSoft; border = C.warning
                          tip = `Vendas Mensais: ${v} un/mês\n\n🔒 Valor manual — definido por ti.\nO cron não substitui valores manuais.`
                        } else if (calcEm) {
                          estado = 'AUTO'; icone = '✓'; cor = C.success; bg = C.successSoft; border = C.success
                          const mediaDiaria = diasActivos > 0 ? (capturadas / diasActivos).toFixed(2) : '0.00'
                          const diasEsgotado = Math.max(0, diasDados - diasActivos)
                          const dataCalc = new Date(calcEm).toLocaleDateString('pt-PT')
                          let tipo = `Vendas Mensais: ${v} un/mês\n\nCálculo automático (${dataCalc})\n────────────────────────────\nDias com dados: ${diasDados}/${JANELA}\nDias com stock disponível: ${diasActivos}\nUnidades vendidas (capturadas): ${capturadas} un\nMédia diária: ${mediaDiaria} un/dia\nProjecção mensal: ${v} un (média × 30)`
                          if (diasEsgotado >= 3) {
                            tipo += `\n\n⚠ Produto esgotado em ${diasEsgotado} dias da janela.\nA procura real pode ser superior — quando estiver esgotado não pode vender, esses dias não contam.`
                          }
                          tip = tipo
                        } else if (diasDados > 0) {
                          estado = 'WARMUP'; icone = '⏳'; cor = C.primary; bg = C.surfaceAlt; border = C.primary
                          tip = `Vendas Mensais: ${v} un/mês (estimativa)\n\n⏳ Em recolha de dados: ${diasDados}/${MIN_DIAS} dias.\nO cálculo automático activa-se assim que atingir ${MIN_DIAS} dias.\nFaltam ${MIN_DIAS - diasDados} dias.`
                        } else {
                          estado = 'INICIAL'; icone = '·'; cor = C.textMuted; bg = C.surface; border = C.border
                          tip = `Vendas Mensais: ${v} un/mês (valor inicial)\n\nO cron ainda não tem dados — começa a recolher quando o produto entrar no LOGNOW.`
                        }
                        return (
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); abrirVendasMini(p) }}
                            title={tip}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '2px 8px', border: `1px solid ${border}`,
                              borderRadius: '10px', background: bg,
                              color: cor, fontSize: '10px', fontWeight: '700',
                              cursor: 'pointer'
                            }}>
                            📊 {v}/mês <span style={{ opacity: 0.85 }}>{icone}</span>
                            {estado === 'WARMUP' && <span style={{ marginLeft: '2px', fontSize: '9px', opacity: 0.85 }}>{diasDados}/{MIN_DIAS}d</span>}
                          </button>
                        )
                      })()}
                    </div>

                    {/* Preço */}
                    <div style={{ marginBottom: '12px' }}>
                      {isParticular ? (
                        // Particular (B2C): so preco final c/IVA, sem detalhe fiscal
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '22px', fontWeight: '800', color: C.text, lineHeight: '1', letterSpacing: '-0.3px' }}>
                            {fmt2(comIva)} €
                          </span>
                        </div>
                      ) : (
                        // B2B: s/IVA principal + c/IVA pequeno por baixo (formato fiscal)
                        <>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                            <span style={{ fontSize: '22px', fontWeight: '800', color: C.text, lineHeight: '1', letterSpacing: '-0.3px' }}>
                              {fmt2(semIva)} €
                            </span>
                            <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>s/IVA</span>
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Tag size={10} /> {fmt2(comIva)} € c/ IVA
                          </div>
                        </>
                      )}
                      {/* Preço de mercado — badge POUPA para B2B + admin.
                          NUNCA mostrar a B2C (particulares têm tabela própria, sem cruzamento). */}
                      {!isParticular && p.preco_mercado != null && Number(p.preco_mercado) > semIva ? (
                        (() => {
                          const mercado = Number(p.preco_mercado)
                          const poupanca = mercado - semIva
                          const pct = (poupanca / mercado) * 100
                          return (
                            <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '11px', color: C.textMuted, textDecoration: 'line-through' }}>
                                {fmt2(mercado)} € no mercado
                              </span>
                              <span style={{ fontSize: '10px', fontWeight: '800', color: '#fff', background: C.success, padding: '2px 7px', borderRadius: '10px', letterSpacing: '0.3px' }}>
                                POUPA {fmt2(poupanca)} € · −{pct.toFixed(0)}%
                              </span>
                            </div>
                          )
                        })()
                      ) : isAdmin && !viewAsEmail && p.preco_mercado != null ? (
                        /* Admin: mercado existe mas <= nosso preco → aviso para corrigir */
                        <div style={{ fontSize: '11px', color: C.warning, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                          ⚠ Mercado: {fmt2(p.preco_mercado)} € (≤ nosso)
                        </div>
                      ) : isAdmin && !viewAsEmail ? (
                        /* Admin: sem ref. mercado definida → indicador discreto */
                        <div style={{ fontSize: '11px', color: '#bdbdbd', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontStyle: 'italic' }}>
                          — sem ref. mercado
                        </div>
                      ) : null}
                      {/* B2C: disponibilidade — sempre disponível, encomenda directa ao fornecedor */}
                      {isParticular && (
                        <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 9px', background: C.successSoft, border: `1px solid ${C.success}40`, borderRadius: '10px', width: 'fit-content' }}>
                          <span style={{ fontSize: '11px' }}>📦</span>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: C.success }}>Disponível por encomenda · 25 dias</span>
                        </div>
                      )}
                    </div>

                    {/* Comparacao com Qogita (B2B reference) ou fallback Top de vendas.
                        Particulares (B2C) nao veem comparacao Qogita — apenas Top de vendas. */}
                    {(() => {
                      const qogPreco = Number(p.qogita_preco || 0)
                      const semQogita = p.qogita_preco == null  // produto nao existe na Qogita
                      const maisPedido = Number(p.mais_pedido_score || 0) >= 10
                      // 1) Qogita mais caro que nos -> badge verde "Poupas X" (NUNCA mostrar a particulares)
                      if (!isParticular && qogPreco > 0 && qogPreco > semIva) {
                        const poupanca = qogPreco - semIva
                        const pct = (poupanca / qogPreco) * 100
                        return (
                          <div style={{ marginBottom: '10px', padding: '8px 10px', background: C.successSoft, border: `1px solid ${C.success}55`, borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>💰 Qogita</span>
                              <span style={{ fontSize: '12px', color: C.textMuted, textDecoration: 'line-through' }}>{fmt2(qogPreco)} €</span>
                              <span style={{ fontSize: '11px', fontWeight: '800', color: '#fff', background: C.success, padding: '2px 8px', borderRadius: '10px' }}>
                                POUPAS {fmt2(poupanca)} € · −{pct.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        )
                      }
                      // 1b) Qogita ao nosso nivel ou ABAIXO — so o admin ve.
                      //     O cliente nao ve nada (nao se mostra uma comparacao
                      //     desfavoravel), mas o admin precisa deste sinal: e
                      //     aqui que a margem nao passa no mercado e ha que
                      //     decidir se se baixa o preco ou se se deixa cair.
                      if (isAdmin && !viewAsEmail && qogPreco > 0 && qogPreco <= semIva) {
                        const dif = semIva - qogPreco
                        return (
                          <div style={{ marginBottom: '10px', padding: '8px 10px', background: C.dangerSoft, border: `1px solid ${C.danger}55`, borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.5px' }}>⚠ Qogita</span>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: C.text }}>{fmt2(qogPreco)} €</span>
                              <span style={{ fontSize: '11px', fontWeight: '800', color: '#fff', background: C.danger, padding: '2px 8px', borderRadius: '10px' }}>
                                {dif < 0.005 ? 'IGUAL AO NOSSO' : `NÓS +${fmt2(dif)} €`}
                              </span>
                              <span style={{ fontSize: '10px', color: C.textMuted }}>só admin vê</span>
                            </div>
                          </div>
                        )
                      }
                      // 2) Sem dados Qogita -> sempre Top de vendas (preenche o espaco)
                      //    Qogita mais barato/igual -> Top vendas SO se score >= 10 (manter regra antiga)
                      //    Particular ve sempre top vendas quando aplicavel (sem ver Qogita)
                      if (semQogita || maisPedido) {
                        return (
                          <div style={{ marginBottom: '10px', padding: '6px 10px', background: C.warningSoft, border: `1px solid ${C.warning}55`, borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '13px' }}>🔥</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: C.warning }}>Top de vendas</span>
                          </div>
                        )
                      }
                      return null
                    })()}

                    {/* Botão "Adicionar" - desactivado em modo interno se nao tiver preco_custo */}
                    {semCustoInterno ? (
                      <div style={{ width: '100%', padding: '8px 10px', background: '#fff4ce', border: '1px solid #c07400', borderRadius: '8px', color: '#92400e', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', textAlign: 'center' }}>
                        <AlertCircle size={13} /> Sem preço custo definido
                      </div>
                    ) : (
                      <button onClick={() => abrirModalQuantidade(p)}
                        onMouseEnter={ev => ev.currentTarget.style.backgroundColor = C.sidebarAlt}
                        onMouseLeave={ev => ev.currentTarget.style.backgroundColor = C.primary}
                        style={{
                          width: '100%', height: '40px',
                          border: 'none', borderRadius: '8px',
                          backgroundColor: C.primary, color: '#fff',
                          cursor: 'pointer', fontSize: '13px', fontWeight: '700', letterSpacing: '0.2px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          transition: 'background-color 0.15s'
                        }}>
                        <Plus size={15} /> Adicionar
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Botao "mostrar mais" — so aparece quando ha mais para mostrar.
            Diz sempre quantos ficam por ver, para nao dar a impressao de que
            o catalogo acaba ali. */}
        {!loading && produtosFiltrados.length > quantosMostrar && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '18px 0 4px' }}>
            <button onClick={() => setQuantosMostrar(q => q + LOTE_CARTOES * 2)}
              style={{ height: '40px', padding: '0 22px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
              Mostrar mais — faltam {produtosFiltrados.length - quantosMostrar}
            </button>
          </div>
        )}
      </div>

      {/* ===== MINI-MODAL VENDAS MENSAIS (cliente + admin) ===== */}
      {vendasMiniProd !== null && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
             onClick={() => { if (!vendasMiniSaving) setVendasMiniProd(null) }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: C.surface, borderRadius: '6px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.text }}>📊 Vendas Mensais</h2>
              <button onClick={() => !vendasMiniSaving && setVendasMiniProd(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>✕</button>
            </div>
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: 0, fontSize: '12px', color: C.textMuted, lineHeight: '1.4' }}>
                <strong style={{ color: C.text }}>{vendasMiniProd.Nome}</strong>
              </p>
              {vendasMiniErro && (
                <div style={{ padding: '8px 12px', background: C.dangerSoft, border: `1px solid ${C.danger}`, borderRadius: '4px', fontSize: '12px', color: C.danger }}>{vendasMiniErro}</div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Estimativa de vendas mensais
                  <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700', backgroundColor: vendasMiniManual ? C.warningSoft : C.successSoft, color: vendasMiniManual ? C.warning : C.success }}>
                    {vendasMiniManual ? '🔒 Manual forçado' : '🔄 Auto'}
                  </span>
                </label>
                <input type="number" min="0" value={vendasMiniValor}
                  onChange={e => setVendasMiniValor(e.target.value)}
                  style={{ width: '100%', height: '38px', padding: '0 12px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '15px', fontWeight: '700', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {!vendasMiniManual && (
                <>
                  <p style={{ margin: 0, fontSize: '11px', color: C.textMuted, lineHeight: '1.4' }}>
                    Quando o sistema acumular ≥14 dias de dados reais, este valor será substituído automaticamente pelas vendas detectadas.
                  </p>
                  <button type="button"
                    onClick={() => {
                      const ok = window.confirm(
                        '⚠️ Forçar valor manual?\n\n' +
                        'Mesmo quando o sistema detectar vendas reais a partir das snapshots diárias, o valor manual será MANTIDO e não atualizado automaticamente.\n\n' +
                        'Recomendado deixar em "Auto" — o cálculo automático reflete vendas verdadeiras, não estimativas.\n\n' +
                        'Tem a certeza que quer forçar manual?'
                      )
                      if (ok) setVendasMiniManual(true)
                    }}
                    style={{ alignSelf: 'flex-start', height: '28px', padding: '0 12px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.textMuted, fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                    🔒 Forçar manual
                  </button>
                </>
              )}
              {vendasMiniManual && (
                <>
                  <div style={{ padding: '8px 12px', background: C.warningSoft, border: `1px solid ${C.warning}`, borderRadius: '4px', fontSize: '11px', color: C.warning, lineHeight: '1.4' }}>
                    ⚠️ Manual ativo: dados reais não substituirão este valor.
                  </div>
                  <button type="button"
                    onClick={() => setVendasMiniManual(false)}
                    style={{ alignSelf: 'flex-start', height: '28px', padding: '0 12px', border: `1px solid ${C.success}`, borderRadius: '4px', background: C.surface, color: C.success, fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                    🔄 Voltar a automático
                  </button>
                </>
              )}
            </div>
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" disabled={vendasMiniSaving}
                onClick={() => setVendasMiniProd(null)}
                style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.textMuted, fontSize: '13px', cursor: vendasMiniSaving ? 'not-allowed' : 'pointer' }}>
                Cancelar
              </button>
              <button type="button" disabled={vendasMiniSaving}
                onClick={guardarVendasMini}
                style={{ height: '34px', padding: '0 16px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: vendasMiniSaving ? 'not-allowed' : 'pointer', opacity: vendasMiniSaving ? 0.7 : 1 }}>
                {vendasMiniSaving ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL CRIAR / EDITAR PRODUTO (admin) ===== */}
      {prodModal !== null && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, padding: '20px' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: `translate(-50%, -50%) translate(${modalPos.x}px, ${modalPos.y}px)`, backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '600px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: '92vh', overflowY: 'auto' }}>

            <div onMouseDown={startDrag} style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.sidebar, position: 'sticky', top: 0, zIndex: 1, cursor: 'move' }}>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {prodModal?.id ? <><Edit2 size={15} /> Editar Produto</> : <><Plus size={15} /> Novo Produto</>}
              </h2>
              <button onClick={() => setProdModal(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '2px', cursor: 'pointer', color: '#fff', padding: '6px', display: 'flex' }}><X size={16} /></button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {prodErro && (
                <div style={{ padding: '10px 12px', background: C.dangerSoft, border: `1px solid ${C.danger}44`, borderRadius: '3px', color: C.danger, fontSize: '13px' }}>{prodErro}</div>
              )}

              {/* BANNER NOTA INTERNA — topo do modal, só admin, só se existe nota */}
              {isAdmin && prodForm.notas_admin && prodForm.notas_admin.trim() && (
                <div style={{ padding: '12px 14px', background: '#fef3c7', border: `2px solid ${C.warning}`, borderLeft: `5px solid ${C.warning}`, borderRadius: '4px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>🔒</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Nota interna admin {prodForm.desconto_supplier_pct ? `· ${prodForm.desconto_supplier_pct}% desconto fornecedor` : ''}</div>
                    <div style={{ fontSize: '12.5px', color: '#7c2d12', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{prodForm.notas_admin.trim()}</div>
                  </div>
                </div>
              )}

              {/* Nome */}
              <div>
                <label style={labelStyle}>Nome do Produto *</label>
                <input style={inputStyle} type="text" value={prodForm.Nome} placeholder="Ex: Dior Sauvage EDP 100ml"
                  onChange={e => handleProdChange('Nome', e.target.value)} />
              </div>

              {/* EAN */}
              <div>
                <label style={labelStyle}>EAN / Referência</label>
                <input style={inputStyle} type="text" value={prodForm.EAN} placeholder="Ex: 3348901250030"
                  onChange={e => handleProdChange('EAN', e.target.value)} />
              </div>

              {/* Categoria */}
              <div>
                <label style={labelStyle}>Categoria</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {/* Sai da tabela `categorias`, nao de uma lista aqui. Criar uma
                      categoria nova na BD passa a aparecer aqui sozinha. */}
                  {categorias.map(c => ({ val: c.slug, label: c.nome, icon: ICONE_CATEGORIA[c.slug] || <Tag size={13} /> })).map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => handleProdChange('categoria', prodForm.categoria === opt.val ? '' : opt.val)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '34px', padding: '0 14px', border: `2px solid ${prodForm.categoria === opt.val ? C.primary : C.border}`, borderRadius: '2px', background: prodForm.categoria === opt.val ? C.surfaceAlt : C.surface, color: prodForm.categoria === opt.val ? C.primary : C.textMuted, fontSize: '13px', fontWeight: prodForm.categoria === opt.val ? '700' : '500', cursor: 'pointer', transition: 'all .15s' }}>
                      {opt.icon} {opt.label}
                    </button>
                  ))}
                  {prodForm.categoria && (
                    <button type="button" onClick={() => handleProdChange('categoria', '')}
                      style={{ height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.bg, color: C.textMuted, fontSize: '12px', cursor: 'pointer' }}>
                      <X size={12} /> Sem categoria
                    </button>
                  )}
                </div>
              </div>

              {/* Preços — com toggle +15% auto */}
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preços</p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '12px', color: prodForm.preco_manual ? C.warning : C.success, fontWeight: '600' }}>
                    <div onClick={() => handleProdChange('preco_manual', !prodForm.preco_manual)}
                      style={{ width: '34px', height: '18px', borderRadius: '9px', background: prodForm.preco_manual ? C.warning : C.success, position: 'relative', transition: 'background .2s', cursor: 'pointer' }}>
                      <div style={{ position: 'absolute', top: '2px', left: prodForm.preco_manual ? '18px' : '2px', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                    </div>
                    {prodForm.preco_manual ? 'Preço Manual' : `Auto ${descreverLucro(portesCobradosDoProduto(prodForm))}`}
                  </label>
                </div>
                {prodForm.preco_manual && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: C.warningSoft, border: `1px solid ${C.warning}66`, borderRadius: '3px', fontSize: '11px', color: C.warning, fontWeight: '600' }}>
                    <AlertCircle size={12} /> Preço manual activo — lucro automático NÃO é aplicado. Insere o preço final s/IVA <strong>já com o 1€/un de portes incluído</strong>.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  {/* Coluna 1: Custo (só visível em modo auto) */}
                  {!prodForm.preco_manual ? (
                    <div>
                      <label style={{ ...labelStyle, color: C.textMuted }}>Custo s/ IVA (€)</label>
                      <input style={inputStyle} type="number" min="0" step="0.01" value={prodForm.preco_base} placeholder="0.00"
                        onChange={e => handleProdChange('preco_base', e.target.value)} />
                      <span style={{ fontSize: '10px', color: C.textMuted }}>Preço de custo</span>
                    </div>
                  ) : (
                    <div style={{ gridColumn: 'span 1' }} />
                  )}
                  {/* Coluna 2: Preço de Venda s/IVA (auto respeita o escalão do custo, ou campo livre se manual) */}
                  <div style={{ gridColumn: prodForm.preco_manual ? 'span 2' : 'span 1' }}>
                    <label style={{ ...labelStyle, color: C.success, fontWeight: '800' }}>
                      {prodForm.preco_manual
                        ? 'Preço Venda s/ IVA (€)'
                        : `Preço Venda s/ IVA (€) = Custo ${descreverLucro(portesCobradosDoProduto(prodForm))}`}
                    </label>
                    {prodForm.preco_manual ? (
                      <input
                        style={{ ...inputStyle, border: `2px solid ${C.success}`, background: C.surface, fontWeight: '700', color: C.success, fontSize: '15px', letterSpacing: '0.3px' }}
                        type="text"
                        inputMode="decimal"
                        value={prodForm.preco}
                        placeholder="ex: 44,90 ou 44.90"
                        onChange={e => handleProdChange('preco', e.target.value.replace(/[^0-9.,]/g, ''))}
                        onBlur={e => {
                          const raw = e.target.value.replace(',', '.')
                          const num = parseFloat(raw)
                          if (!isNaN(num)) handleProdChange('preco', String(num))
                        }}
                        onFocus={e => e.target.select()}
                      />
                    ) : (
                      <input
                        style={{ ...inputStyle, border: `2px solid ${C.success}`, background: C.successSoft, fontWeight: '700', color: C.success, fontSize: '14px' }}
                        type="number" min="0" step="0.01"
                        value={prodForm.preco}
                        readOnly
                        placeholder="0.00"
                      />
                    )}
                    {!prodForm.preco_manual && prodForm.preco_base !== '' && Number(prodForm.preco_base) > 0 && (
                      <span style={{ fontSize: '10px', color: C.success, fontWeight: '600' }}>
                        {/* A formula tem de dizer a que foi mesmo aplicada. Num
                            fornecedor sem portes, escrever "+1,00 (portes)" e
                            uma conta que nao bate com o numero ao lado — e quem
                            a le fica sem saber em qual acreditar. */}
                        Fornec. {fmt2(prodForm.preco_base)}€ × 1.20
                        {portesCobradosDoProduto(prodForm) > 0 ? ` + ${fmt2(portesCobradosDoProduto(prodForm))} (portes)` : ''}
                        {' '}= {fmt2(prodForm.preco)} €
                      </span>
                    )}
                  </div>
                  {/* Coluna 3: Preço c/ IVA */}
                  {!prodForm.preco_manual && (
                    <div>
                      <label style={{ ...labelStyle, color: C.primary }}>Preço c/ IVA 23% (€)</label>
                      <input style={{ ...inputStyle, borderColor: C.primary + '66', background: C.surfaceAlt, fontWeight: '600', color: C.primary }} type="number" min="0" step="0.01" value={prodForm.preco_iva} placeholder="0.00"
                        onChange={e => handleProdChange('preco_iva', e.target.value)} />
                      <span style={{ fontSize: '10px', color: C.primary }}>{fmt2(prodForm.preco)} × 1.23</span>
                    </div>
                  )}
                </div>
                {/* Resumo visual */}
                {prodForm.preco !== '' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: prodForm.preco_manual ? C.warningSoft : C.successSoft, borderRadius: '3px', border: `1px solid ${prodForm.preco_manual ? C.warning : C.success}44` }}>
                    {!prodForm.preco_manual ? (
                      <>
                        <span style={{ fontSize: '11px', color: C.textMuted }}>Custo:</span>
                        <strong style={{ fontSize: '12px' }}>{fmt2(prodForm.preco_base)} €</strong>
                        <span style={{ fontSize: '11px', color: C.success, fontWeight: '700' }}>
                          → ×1.20{portesCobradosDoProduto(prodForm) > 0 ? ` +${fmt2(portesCobradosDoProduto(prodForm))}€ portes` : ''} →
                        </span>
                        <strong style={{ fontSize: '13px', color: C.success }}>Venda s/IVA: {fmt2(prodForm.preco)} €</strong>
                        <span style={{ fontSize: '11px', color: C.primary, fontWeight: '700' }}>→ +IVA →</span>
                        <strong style={{ fontSize: '13px', color: C.primary }}>c/IVA: {fmt2(Number(prodForm.preco) * (1 + IVA))} €</strong>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={12} style={{ color: C.warning }} />
                        <strong style={{ fontSize: '13px', color: C.success }}>Venda s/IVA: {fmt2(prodForm.preco)} €</strong>
                        <span style={{ fontSize: '11px', color: C.primary, fontWeight: '700' }}>→ +IVA →</span>
                        <strong style={{ fontSize: '13px', color: C.primary }}>c/IVA: {fmt2(Number(prodForm.preco) * (1 + IVA))} €</strong>
                      </>
                    )}
                  </div>
                )}

                {/* Preço de custo (interno, para cálculo de lucro) */}
                <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: '10px', marginTop: '4px' }}>
                  <label style={labelStyle}>
                    Preço de custo (€ s/IVA) <span style={{ fontWeight: 400, color: C.textMuted, textTransform: 'none' }}>— interno, para cálculo de lucro</span>
                  </label>
                  <input
                    style={{ ...inputStyle, maxWidth: '180px' }}
                    type="number" min="0" step="0.01"
                    value={prodForm.preco_custo}
                    onChange={e => handleProdChange('preco_custo', e.target.value)}
                    placeholder={!prodForm.preco_manual && prodForm.preco_base !== '' ? `Auto: ${fmt2(prodForm.preco_base)} €` : 'Ex: 35.00'}
                  />
                  {!prodForm.preco_manual && prodForm.preco_base !== '' && prodForm.preco_custo === '' && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.textMuted }}>
                      💡 Vazio assume o preço-base como custo ({fmt2(prodForm.preco_base)} €).
                    </p>
                  )}
                  {prodForm.preco_manual && prodForm.preco_custo === '' && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.warning, fontWeight: '600' }}>
                      ⚠️ Sem custo, este produto não conta para o lucro.
                    </p>
                  )}
                  {/* Painel de margem — SO em modo AUTO. Em manual o admin assume controlo total e nao recebe alertas. */}
                  {!prodForm.preco_manual && prodForm.preco_custo !== '' && prodForm.preco !== '' && Number(prodForm.preco_custo) > 0 && Number(prodForm.preco) > 0 && (() => {
                    const custo = Number(prodForm.preco_custo)
                    const venda = Number(prodForm.preco)
                    // Dois valores distintos: COBRADOS entram na receita e ficam fora do
                    // desconto; CUSTO é o que pagamos ao transportador. Ver src/portes.js.
                    const portesCobrados = portesUn
                    // Do fornecedor deste artigo, e não uma constante: no Naypes
                    // não pagamos transporte, e somar-lhe 0,75 € fazia um artigo
                    // de 3 € de custo — que dá 60 cêntimos de lucro — aparecer
                    // aqui como 15 cêntimos de prejuízo.
                    const portesCusto = portesCustoDoProduto(prodForm)
                    // Markup sobre custo (bruto, antes de desconto de cliente)
                    const lucroBruto = venda - portesCusto - custo
                    const markupBruto = (lucroBruto / custo) * 100
                    // Descobrir desconto do cliente associado (se houver)
                    const emailAssoc = (prodForm.Email_Cliente || '').trim().toLowerCase()
                    const clienteAssoc = emailAssoc ? clientes.find(c => (c.email || '').toLowerCase() === emailAssoc) : null
                    const descPct = Number(clienteAssoc?.desconto_percentagem) || 0
                    // Lucro REAL: o desconto não incide sobre os portes cobrados, que voltam
                    // a somar-se à receita; o custo leva os portes que nós pagamos.
                    const vendaAposDesc = (venda - portesCobrados) * (1 - descPct / 100) + portesCobrados
                    const lucroReal = vendaAposDesc - custo - portesCusto
                    const margemRealPct = vendaAposDesc > 0 ? (lucroReal / vendaAposDesc) * 100 : 0
                    const markupRealPct = custo > 0 ? (lucroReal / custo) * 100 : 0
                    // Limiar DINAMICO: queremos 15% REAL apos desconto.
                    //   bruto_min = 15 / (1 - desconto)  → ex: desc 10% → 16.67% bruto
                    //   ATENCAO: aqui aplicamos sobre VENDA, nao sobre custo.
                    //   Markup sobre custo: M = (1.15 / (1 - desc)) - 1  → desc 10% → 27.78%; desc 0% → 15%
                    const limiarMin = descPct > 0 ? Number((((1.15 / (1 - descPct/100)) - 1) * 100).toFixed(2)) : 15
                    const abaixoLimiar = markupBruto < limiarMin
                    const cor = lucroReal <= 0 ? C.danger : (abaixoLimiar ? C.warning : C.success)
                    return (
                      <div style={{ marginTop: '6px', padding: '8px 10px', background: abaixoLimiar ? C.warningSoft : C.successSoft, border: `1px solid ${cor}66`, borderRadius: '3px' }}>
                        <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: cor, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {abaixoLimiar ? <AlertCircle size={12} /> : '📈'}
                          {descPct > 0 ? `Cliente "${clienteAssoc?.nome || emailAssoc}" tem desconto ${descPct}% — markup minimo: ${limiarMin}% sobre custo (garante 15% REAL apos desconto)` : `Markup bruto: ${markupBruto.toFixed(1)}% sobre custo`}
                        </p>
                        <div style={{ marginTop: '4px', fontSize: '11px', color: C.text, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                          <span><strong>Markup bruto:</strong> {markupBruto.toFixed(1)}% (alvo &ge; {limiarMin}%)</span>
                          <span><strong>Lucro bruto/un:</strong> {fmt2(lucroBruto)} &euro;</span>
                          {/* ─── Referência de mercado ────────────────────────
                              O sítio onde se decide o preço tem de ser o mesmo
                              onde se vê contra o que se está a decidir. Até aqui
                              o preço da Qogita só aparecia no cartão, o que
                              obrigava a fechar o modal, olhar, e voltar a abrir.
                              Não é editável: vem da recolha e é dela que se
                              actualiza. */}
                          {(() => {
                            const qog = Number(prodForm.qogita_preco || 0)
                            if (!(qog > 0)) {
                              return (
                                <span style={{ gridColumn: '1 / -1', color: C.textMuted }}>
                                  <strong>Qogita:</strong> sem referência — ou não existe lá, ou a recolha ainda não passou por este artigo
                                </span>
                              )
                            }
                            // Positivo = estamos acima do mercado. O sinal e a cor
                            // dizem o mesmo, para se ler de relance sem fazer contas.
                            const dif = ((venda - qog) / qog) * 100
                            const acima = dif > 0.5
                            const abaixo = dif < -0.5
                            const corQog = acima ? C.danger : (abaixo ? C.success : C.textMuted)
                            const texto = acima ? `${dif.toFixed(0)}% acima`
                              : abaixo ? `${Math.abs(dif).toFixed(0)}% abaixo`
                              : 'ao nível do mercado'
                            return (
                              <span style={{ gridColumn: '1 / -1', color: corQog, fontWeight: '600' }}>
                                <strong style={{ color: C.text }}>Qogita:</strong> {fmt2(qog)} &euro; &middot; estás <strong>{texto}</strong>
                              </span>
                            )
                          })()}
                          {descPct > 0 && (
                            <>
                              <span><strong>Apos desconto {descPct}%:</strong> {fmt2(vendaAposDesc)} &euro;/un</span>
                              <span style={{ color: lucroReal > 0 ? C.success : C.danger, fontWeight: '700' }}>
                                <strong>Lucro REAL/un:</strong> {fmt2(lucroReal)} &euro; ({markupRealPct.toFixed(1)}% markup, {margemRealPct.toFixed(1)}% margem)
                              </span>
                            </>
                          )}
                        </div>
                        {descPct > 0 && abaixoLimiar && (() => {
                          // Preco sugerido: custo × (1 + limiarMin/100) + portes — garante 15% REAL apos desconto
                          const sugerido = custo * (1 + limiarMin / 100) + portesCobrados
                          return (
                            <p style={{ margin: '5px 0 0', fontSize: '11px', color: C.warning, fontWeight: '700' }}>
                              &#9888;&#65039; Sugestao: subir o preco para pelo menos <strong>{fmt2(sugerido)} &euro;</strong> (custo &times;{(1 + limiarMin / 100).toFixed(4)} + portes cobrados) para garantir 15% REAL apos desconto de {descPct}% do cliente.
                            </p>
                          )
                        })()}
                      </div>
                    )
                  })()}
                </div>

                {/* Preço de mercado (referência mostrada ao cliente como âncora) */}
                <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: '10px', marginTop: '4px' }}>
                  <label style={labelStyle}>
                    Preço de mercado (€ s/IVA) <span style={{ fontWeight: 400, color: C.textMuted, textTransform: 'none' }}>— opcional, comparado em base s/IVA (B2B PT)</span>
                  </label>
                  <input
                    style={{ ...inputStyle, maxWidth: '180px' }}
                    type="number" min="0" step="0.01"
                    value={prodForm.preco_mercado}
                    onChange={e => handleProdChange('preco_mercado', e.target.value)}
                    placeholder="Ex: 95.00"
                  />
                  {prodForm.preco_mercado !== '' && prodForm.preco !== '' && Number(prodForm.preco_mercado) > 0 && Number(prodForm.preco) > 0 && (() => {
                    const mercado = Number(prodForm.preco_mercado)
                    const nosso = Number(prodForm.preco)
                    const poupanca = mercado - nosso
                    const pct = (poupanca / mercado) * 100
                    if (poupanca <= 0) {
                      return <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.danger, fontWeight: '700' }}>⚠️ O nosso preço s/IVA ({fmt2(nosso)} €) é igual ou superior ao de mercado. <strong>O cliente NÃO verá poupança</strong> — atualiza o preço de mercado ou esconde-o.</p>
                    }
                    if (pct < 10) {
                      return <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.warning, fontWeight: '700' }}>⚠️ Cliente vê: poupa apenas {fmt2(poupanca)} € ({pct.toFixed(1)}%). Pouco impacto comercial — atualiza o preço de mercado se ficou desatualizado.</p>
                    }
                    return <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.success, fontWeight: '600' }}>💚 Cliente vê: poupa {fmt2(poupanca)} € ({pct.toFixed(0)}%) em base s/IVA</p>
                  })()}
                </div>
              </div>

              {/* Stock geri-se em Gestão de Stock (sincronizado com LOGNOW); aqui não há campo */}

              {/* Quantidade mínima de compra — só admin */}
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.primary}33`, borderRadius: '3px', padding: '10px 12px' }}>
                <label style={{ ...labelStyle, color: C.primary }}>Quantidade Mínima de Compra <span style={{ fontWeight: 400, color: C.textMuted, textTransform: 'none' }}>— por defeito 10 un. (só admin)</span></label>
                <input style={inputStyle} type="number" min="1" value={prodForm.qtd_minima_compra}
                  onChange={e => handleProdChange('qtd_minima_compra', e.target.value)} />
              </div>

              {/* Vendas Mensais + Lead Time + Objetivo Cobertura */}
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dados de Planeamento</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={labelStyle}>
                      Vendas Mensais{' '}
                      <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700', backgroundColor: prodForm.vendas_mensais_manual ? C.warningSoft : C.successSoft, color: prodForm.vendas_mensais_manual ? C.warning : C.success, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        {prodForm.vendas_mensais_manual ? '🔒 Manual forçado' : '🔄 Auto'}
                      </span>
                    </label>
                    <input style={inputStyle} type="number" min="0" value={prodForm.Vendas_Mensais}
                      onChange={e => handleProdChange('Vendas_Mensais', e.target.value)} />
                    {!prodForm.vendas_mensais_manual && (
                      <>
                        <p style={{ margin: '4px 0 0', fontSize: '10px', color: C.textMuted }}>Quando houver dados reais (≥14 dias), o sistema substitui este valor automaticamente.</p>
                        <button type="button"
                          onClick={() => {
                            const ok = window.confirm(
                              '⚠️ Forçar valor manual?\n\n' +
                              'Mesmo quando o sistema detectar vendas reais a partir das snapshots diárias, o valor manual será MANTIDO e não será atualizado automaticamente.\n\n' +
                              'Recomendado deixar em "Auto" — o cálculo automático reflete vendas verdadeiras, não estimativas.\n\n' +
                              'Tem a certeza que quer forçar manual?'
                            )
                            if (ok) handleProdChange('vendas_mensais_manual', true)
                          }}
                          style={{ marginTop: '4px', height: '22px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.surface, color: C.textMuted, fontSize: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          🔒 Forçar manual
                        </button>
                      </>
                    )}
                    {prodForm.vendas_mensais_manual && (
                      <>
                        <p style={{ margin: '4px 0 0', padding: '5px 8px', fontSize: '10px', color: C.warning, backgroundColor: C.warningSoft, border: `1px solid ${C.warning}`, borderRadius: '3px', lineHeight: '1.3' }}>
                          ⚠️ Manual ativo: dados reais não substituirão este valor.
                        </p>
                        <button type="button"
                          onClick={() => handleProdChange('vendas_mensais_manual', false)}
                          style={{ marginTop: '4px', height: '22px', padding: '0 8px', border: `1px solid ${C.success}`, borderRadius: '2px', background: C.surface, color: C.success, fontSize: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                          🔄 Voltar a automático
                        </button>
                      </>
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}>Lead Time (dias)</label>
                    <input style={inputStyle} type="number" min="1" value={prodForm.Lead_Time}
                      onChange={e => handleProdChange('Lead_Time', e.target.value)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Obj. Cobertura (dias)</label>
                    <input style={inputStyle} type="number" min="1" value={prodForm.Objetivo_Cobertura}
                      onChange={e => handleProdChange('Objetivo_Cobertura', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Trânsitos */}
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Encomendas em Trânsito <span style={{ fontWeight: 400, textTransform: 'none' }}>(opcional)</span></p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {SLOTS_TRANSITO.map(n => (
                    <Fragment key={n}>
                      <div>
                        <label style={labelStyle}>Trânsito {n} — Qtd</label>
                        <input style={inputStyle} type="number" min="0" value={prodForm[`Transito${n}_Qtd`]}
                          onChange={e => handleProdChange(`Transito${n}_Qtd`, e.target.value)} />
                      </div>
                      <div>
                        <label style={labelStyle}>Trânsito {n} — Data chegada</label>
                        <input style={inputStyle} type="date" value={prodForm[`Transito${n}_Data`]}
                          onChange={e => handleProdChange(`Transito${n}_Data`, e.target.value)} />
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>

              {/* Foto do produto */}
              <div>
                <label style={labelStyle}>Foto do Produto</label>
                {prodForm.foto_url && (
                  <div style={{ marginBottom: '8px', borderRadius: '3px', overflow: 'hidden', height: '120px', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${C.border}` }}>
                    <img src={prodForm.foto_url} alt="preview" style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }}
                      onError={e => { e.target.style.display = 'none' }} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Link size={12} style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
                    <input style={{ ...inputStyle, paddingLeft: '28px' }} type="url" value={prodForm.foto_url} placeholder="https://... ou cole URL da imagem"
                      onChange={e => handleProdChange('foto_url', e.target.value)} />
                  </div>
                  <label title="Fazer upload de ficheiro" style={{ flexShrink: 0, height: '34px', width: '36px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>
                    <Upload size={14} />
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async e => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const ext = file.name.split('.').pop()
                      const path = `produtos/${Date.now()}.${ext}`
                      const { error: upErr } = await supabase.storage.from('produto-fotos').upload(path, file, { upsert: true })
                      if (upErr) { alert('Erro upload: ' + upErr.message); return }
                      const { data: urlData } = supabase.storage.from('produto-fotos').getPublicUrl(path)
                      handleProdChange('foto_url', urlData.publicUrl)
                    }} />
                  </label>
                  {prodForm.foto_url && (
                    <button onClick={() => handleProdChange('foto_url', '')} title="Remover foto"
                      style={{ flexShrink: 0, height: '34px', width: '34px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.danger }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* ─── Onde este produto aparece ───────────────────────────────
                  Eram dois campos independentes — `visivel_catalogo` e
                  `Email_Cliente` — cuja combinacao ninguem conseguia adivinhar.
                  A caixa dizia "Arquivado" a descrever o estado actual e nao o
                  que fazia, e o email do cliente, quando preenchido, mandava
                  sobre ela sem o dizer em lado nenhum.

                  Sao tres estados e passam a ler-se como tres. E a combinacao
                  real que a consulta do catalogo ja fazia:
                    Email_Cliente preenchido            -> so esse cliente ve
                    vazio + visivel_catalogo true       -> toda a gente ve
                    vazio + visivel_catalogo false      -> ninguem ve            */}
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.primary}33`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Onde este produto aparece</p>
                {(() => {
                  const temCliente = (prodForm.Email_Cliente || '').trim() !== ''
                  const estado = temCliente ? 'cliente' : (prodForm.visivel_catalogo !== false ? 'publicado' : 'oculto')
                  const opcoes = [
                    { key: 'publicado', label: 'Publicado',   nota: 'Aparece no catálogo de todos os clientes.',                     cor: C.success, Icone: Eye },
                    { key: 'oculto',    label: 'Não publicado', nota: 'Não aparece a ninguém. Fica à espera de decisão.',             cor: C.textMuted, Icone: EyeOff },
                    { key: 'cliente',   label: 'Associado a cliente', nota: 'Só o cliente escolhido o vê — nem no catálogo público.', cor: C.primary, Icone: Eye },
                  ]
                  const escolher = (k) => {
                    // Clicar no que ja esta escolhido nao faz nada: senao um
                    // clique distraido num produto ja associado mexia-lhe nos
                    // campos sem mudar nada visivel.
                    if (k === estado) return
                    // Cada estado grava a combinacao inteira, para nao ficarem
                    // campos meio-preenchidos a contradizer a escolha.
                    if (k === 'publicado') { handleProdChange('Email_Cliente', ''); handleProdChange('visivel_catalogo', true) }
                    if (k === 'oculto')    { handleProdChange('Email_Cliente', ''); handleProdChange('visivel_catalogo', false) }
                    if (k === 'cliente')   { handleProdChange('visivel_catalogo', false) }
                  }
                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                        {opcoes.map(o => {
                          const activa = estado === o.key
                          return (
                            <button key={o.key} onClick={() => escolher(o.key)}
                              style={{ padding: '9px 8px', border: `2px solid ${activa ? o.cor : C.border}`, borderRadius: '4px',
                                       background: activa ? `${o.cor}14` : C.surface, cursor: 'pointer', textAlign: 'left',
                                       display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '700', color: activa ? o.cor : C.text }}>
                                <o.Icone size={13} /> {o.label}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                      <p style={{ margin: 0, fontSize: '11px', color: C.textMuted, lineHeight: 1.45 }}>
                        {opcoes.find(o => o.key === estado)?.nota}
                      </p>
                      {estado === 'cliente' && (
                        <div>
                          <label style={labelStyle}>Cliente</label>
                          <select style={inputStyle} value={prodForm.Email_Cliente}
                            onChange={e => handleProdChange('Email_Cliente', e.target.value)}>
                            <option value=''>— Escolhe o cliente —</option>
                            {clientes.map(c => (
                              <option key={c.id} value={c.email}>{c.nome} · {c.email}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {/* Sem cliente escolhido, "associado" nao significa nada e o
                          produto acaba invisivel sem ninguem perceber porque. */}
                      {estado === 'cliente' && (prodForm.Email_Cliente || '').trim() === '' && (
                        <p style={{ margin: 0, fontSize: '11px', color: C.warning, fontWeight: '600' }}>
                          Escolhe o cliente — sem ele, o produto não aparece a ninguém.
                        </p>
                      )}
                    </>
                  )
                })()}
              </div>

              {/* Condicoes de pagamento — seccao propria e nao dentro da caixa da
                  associacao a cliente, que e onde estava: uma condicao comercial
                  enfiada em "Associacao a Cliente" ninguem a encontra.
                  Ver supabase/sql/sinal_flexivel_produtos.sql */}
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.warning}55`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Condições de Pagamento</p>
                <div style={{ background: prodForm.sinal_flexivel ? '#fff8e1' : C.surface, border: `1px solid ${prodForm.sinal_flexivel ? `${C.warning}66` : C.border}`, borderRadius: '4px', padding: '12px 14px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: prodForm.sinal_flexivel ? '#92400e' : C.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CreditCard size={14} /> Sinal flexível (acordo fornecedor)
                      </div>
                      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', lineHeight: 1.45 }}>
                        {/* Num fornecedor de pagamento total nao ha 50/50 nenhum
                            para descrever, e o sinal flexivel nao se aplica.
                            Dizer "condicoes normais: 50% + 50%" era prometer uma
                            condicao que o checkout nem chega a oferecer. */}
                        {(() => {
                          const f = fornecedores.find(x => x.id === prodForm.fornecedor_id)
                          if (f?.exige_pagamento_total) {
                            return `Não se aplica: ${f.nome || 'este fornecedor'} exige pagamento a 100% na encomenda.`
                          }
                          return prodForm.sinal_flexivel
                            ? 'Clientes com o acordo podem escolher quanto pagam à cabeça neste produto. Só se aplica se TODOS os artigos do carrinho estiverem marcados — basta um de fora para a encomenda voltar a 50% + 50%.'
                            : 'Condições normais: 50% na encomenda + 50% antes do envio.'
                        })()}
                      </div>
                    </div>
                    <input type="checkbox" checked={prodForm.sinal_flexivel === true}
                      onChange={e => handleProdChange('sinal_flexivel', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }} />
                  </label>
                </div>
              </div>

              {/* Notas Internas do Admin (só admin vê) */}
              {isAdmin && (
                <div style={{ background: '#fffbeb', border: `1px solid ${C.warning}55`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.warning, textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔒 Notas Internas Admin <span style={{ fontWeight: 400, textTransform: 'none', color: C.textMuted }}>(só admin vê)</span></p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: '10px', alignItems: 'flex-start' }}>
                    <div>
                      <label style={labelStyle}>Nota interna sobre o produto</label>
                      <textarea
                        value={prodForm.notas_admin || ''}
                        onChange={e => handleProdChange('notas_admin', e.target.value)}
                        placeholder='Ex: "Fornecedor só dá 6% de desconto. Preço subiu 4% para manter margem."'
                        rows={3}
                        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', minHeight: '64px' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>% Desconto fornecedor</label>
                      <input type="number" min="0" max="100" step="0.5"
                        value={prodForm.desconto_supplier_pct || ''}
                        onChange={e => handleProdChange('desconto_supplier_pct', e.target.value)}
                        placeholder="10"
                        style={inputStyle}
                      />
                      <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '4px', lineHeight: '1.4' }}>
                        {Number(prodForm.desconto_supplier_pct) === 0 ? 'Este fornecedor não dá desconto — o custo é o preço de tabela.' : 'Vazio = 10% (default). Mete 5 ou 6 se o desconto deste fornecedor é menor, ou 0 se não houver.'}
                      </div>
                    </div>
                  </div>
                  {prodForm.notas_admin && prodForm.notas_admin.trim() && (
                    <div style={{ fontSize: '11px', color: '#92400e', display: 'flex', alignItems: 'flex-start', gap: '6px', background: '#fef3c7', padding: '8px 10px', borderRadius: '3px' }}>
                      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span style={{ whiteSpace: 'pre-wrap' }}>{prodForm.notas_admin.trim()}</span>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '4px', flexWrap: 'wrap' }}>
                <button onClick={() => setProdModal(null)}
                  style={{ flex: 1, height: '38px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.surface, cursor: 'pointer', fontSize: '13px', color: C.textMuted }}>Cancelar</button>
                {prodModal?.id && (
                  <button
                    onClick={() => duplicarProduto(prodModal)}
                    title="Cria uma cópia deste produto (sem EAN, stock 0) para ajustares"
                    style={{ height: '38px', padding: '0 14px', border: `1px solid ${C.primary}`, borderRadius: '2px', background: C.surface, color: C.primary, cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CopyIcon size={13} /> Duplicar
                  </button>
                )}
                <button onClick={guardarProduto} disabled={guardando}
                  style={{ flex: 2, height: '38px', border: 'none', borderRadius: '2px', background: C.success, color: '#fff', cursor: guardando ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', opacity: guardando ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {guardando ? <><Loader size={13} /> A guardar...</> : <><Save size={13} /> {prodModal?.id ? 'Guardar Alterações' : 'Criar Produto'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL CARRINHO (cliente) ===== */}
      {showCarrinho && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '600px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}>

            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.sidebar, position: 'sticky', top: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Carrinho de Compras</p>
                  <h2 style={{ color: '#fff', margin: '3px 0 0', fontSize: '17px', fontWeight: '700' }}>{carrinho.length} {carrinho.length === 1 ? 'artigo' : 'artigos'}</h2>
                </div>
              </div>
              <button onClick={fecharCarrinho} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '2px', cursor: 'pointer', color: '#fff', padding: '6px', display: 'flex' }}><X size={16} /></button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {resultMsg.texto && (
                <div style={{ padding: '12px 14px', backgroundColor: resultMsg.tipo === 'ok' ? C.successSoft : C.dangerSoft, borderLeft: `4px solid ${resultMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: resultMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '13px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  {resultMsg.tipo === 'ok' ? <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} /> : <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />}
                  {resultMsg.texto}
                </div>
              )}

              {resultMsg.tipo !== 'ok' && (
                <>
                  {/* Lista de itens do carrinho */}
                  {carrinho.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: C.textMuted }}>
                      <ShoppingCart size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
                      <p style={{ margin: 0 }}>O carrinho está vazio.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {carrinho.map((item, idx) => (
                        <div key={item.Nome} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: C.bg, borderRadius: '3px', border: `1px solid ${C.border}` }}>
                          {item.foto_url ? (
                            <img src={item.foto_url} alt={item.Nome} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '2px' }}
                              onError={e => {
                                e.target.style.display = 'none'
                                const fb = e.target.nextElementSibling
                                if (fb) fb.style.display = 'flex'
                              }} />
                          ) : null}
                          <div style={{
                            display: item.foto_url ? 'none' : 'flex',
                            width: '50px', height: '50px', borderRadius: '2px',
                            background: C.bg, border: `1px solid ${C.border}`,
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>
                            <Package size={20} style={{ color: '#d1d1d1' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '600', color: C.text }}>{item.Nome}</p>
                            <p style={{ margin: 0, fontSize: '11px', color: C.textMuted }}>{fmt2(item.preco_unitario)} € / un. s/ IVA</p>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <button onClick={() => atualizarQuantidadeCarrinho(item.Nome, item.quantidade - 1)}
                              style={{ width: '28px', height: '28px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.surface, cursor: 'pointer', fontSize: '14px', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                            <span style={{ fontSize: '13px', fontWeight: '600', minWidth: '30px', textAlign: 'center' }}>{item.quantidade}</span>
                            <button onClick={() => atualizarQuantidadeCarrinho(item.Nome, item.quantidade + 1)}
                              style={{ width: '28px', height: '28px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.surface, cursor: 'pointer', fontSize: '14px', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: '70px' }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.primary }}>{fmt2(item.total_sem_iva)} €</p>
                            <p style={{ margin: 0, fontSize: '10px', color: C.textMuted }}>s/ IVA</p>
                          </div>
                          <button onClick={() => removerDoCarrinho(item.Nome)} style={{ width: '28px', height: '28px', border: 'none', borderRadius: '2px', background: 'transparent', cursor: 'pointer', color: C.danger, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Banner Incentivo Volume — so para Standard B2B sem desconto base */}
                  {carrinho.length > 0 && incentivoElegivel && (() => {
                    const faltaParaThreshold = Math.max(0, incentivoThreshold - carrinhoTotalSemIva)
                    const progressoPct = Math.min(100, (carrinhoTotalSemIva / incentivoThreshold) * 100)
                    // Aparece desde o primeiro artigo. Esperava pelos 80% do
                    // limiar, e com 2.500 no Plus Skill isso queria dizer que o
                    // cliente so sabia do desconto aos 2.000 — tarde de mais
                    // para ele decidir comprar mais por causa dele.
                    //
                    // Ate meio caminho e uma linha discreta: "faltam 2.350" num
                    // quadro grande e amarelo le-se como inalcancavel.
                    if (!incentivoAtivo && progressoPct < 50) {
                      return (
                        <div style={{ fontSize: '11.5px', color: C.textMuted, marginBottom: '12px',
                          display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '13px' }}>💡</span>
                          <span>
                            <strong style={{ color: C.text }}>{incentivoPct}% de desconto</strong> em compras acima de{' '}
                            <strong style={{ color: C.text }}>{fmt2(incentivoThreshold)} €</strong> s/ IVA
                            {' '}— faltam {fmt2(faltaParaThreshold)} €
                          </span>
                        </div>
                      )
                    }
                    if (incentivoAtivo) {
                      return (
                        <div style={{ background: '#dcfce7', border: '2px solid #16a34a', borderRadius: '6px', padding: '12px 14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '22px' }}>🎉</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: C.success }}>
                              Desconto Volume {incentivoPct}% activo
                            </div>
                            <div style={{ fontSize: '12px', color: C.text, marginTop: '2px' }}>
                              Estás a poupar <strong>{fmt2(valorDescontoSemIva * (1 + IVA))} €</strong> nesta encomenda.
                            </div>
                          </div>
                        </div>
                      )
                    }
                    return (
                      <div style={{ background: '#fef3c7', border: '1px solid #d97706', borderRadius: '6px', padding: '12px 14px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '18px' }}>💡</span>
                          <div style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: C.warning }}>
                            Faltam <strong style={{ color: C.text }}>{fmt2(faltaParaThreshold)} €</strong> para ganhar {incentivoPct}% de desconto
                          </div>
                        </div>
                        <div style={{ background: '#fff', borderRadius: '3px', height: '6px', overflow: 'hidden' }}>
                          <div style={{ background: C.warning, height: '100%', width: `${progressoPct}%`, transition: 'width 0.3s' }} />
                        </div>
                        <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '4px', textAlign: 'right' }}>
                          {fmt2(carrinhoTotalSemIva)} € / {fmt2(incentivoThreshold)} € s/ IVA
                        </div>
                      </div>
                    )
                  })()}

                  {/* Resumo total — estrutura fiscal (CIVA art.16): desconto antes do IVA */}
                  {carrinho.length > 0 && (() => {
                    const totalSemIvaResumo = carrinhoTotalSemIva
                    const descontoSemIvaResumo = valorDescontoSemIva
                    const baseTributavelResumo = baseTributavel
                    const ivaResumo = ivaValor
                    const totalFinalResumo = carrinhoFinal

                    return (
                      <div style={{ backgroundColor: C.successSoft, border: `1px solid ${C.success}`, borderRadius: '3px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {perfil?.cliente_interno && (
                          <div style={{ background: '#f3f0ff', border: '1px solid #d1cef0', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: C.purple, fontWeight: '600', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ fontSize: '13px', lineHeight: '1' }}>ℹ️</span>
                            <span>Cliente interno — ao submeter, o total final será recalculado a <strong>preço de custo + portes</strong>. Os valores apresentados aqui são apenas indicativos.</span>
                          </div>
                        )}
                        {!isParticular && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: C.text }}>
                            <span>Subtotal s/ IVA</span>
                            <span style={{ fontWeight: '600' }}>{fmt2(totalSemIvaResumo)} €</span>
                          </div>
                        )}
                        {!isParticular && descontoCliente > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: C.warning, fontWeight: '600' }}>
                            <span>{incentivoAtivo ? `Desconto Volume (${descontoCliente}%)` : `Desconto (${descontoCliente}%)`}</span>
                            <span>−{fmt2(descontoSemIvaResumo)} €</span>
                          </div>
                        )}
                        {!isParticular && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: C.text, borderTop: `1px dashed ${C.success}55`, paddingTop: '6px' }}>
                              <span style={{ fontWeight: '700' }}>{modoEncomendarInterno ? 'Total (sem IVA)' : 'Base tributável'}</span>
                              <span style={{ fontWeight: '700' }}>{fmt2(baseTributavelResumo)} €</span>
                            </div>
                            {modoEncomendarInterno ? (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>
                                <span>Reverse Charge · IVA 0%</span>
                                <span>+0,00 €</span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: C.textMuted }}>
                                <span>IVA (23%)</span>
                                <span>+{fmt2(ivaResumo)} €</span>
                              </div>
                            )}
                          </>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${C.success}`, paddingTop: '8px' }}>
                          <span style={{ fontSize: '15px', fontWeight: '700', color: C.success }}>Total a Pagar</span>
                          <span style={{ fontSize: '20px', fontWeight: '700', color: C.success }}>{fmt2(totalFinalResumo)} €</span>
                        </div>
                      </div>
                    )
                  })()}

                  {/* Condições Comerciais */}
                  {carrinho.length > 0 && (
                    <div style={{ background: '#fffbf0', border: `1px solid #f0d070`, borderRadius: '3px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: C.warning, display: 'flex', alignItems: 'center', gap: '5px' }}><Info size={13} /> Condições de Compra</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.text }}>
                          <Truck size={13} style={{ color: C.primary, flexShrink: 0 }} />
                          <span>Prazo médio de entrega: <strong>{prazoEntregaDias} dias úteis</strong> após confirmação de pagamento</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: C.text }}>
                          <CreditCard size={13} style={{ color: C.primary, flexShrink: 0, marginTop: '1px' }} />
                          {/* O 50/50 estava cravado. Num fornecedor de pagamento
                              total, o cliente lia aqui uma condicao que o proprio
                              checkout, tres linhas abaixo, nao lhe oferecia. */}
                          {(isParticular || fornecedorExigeTotal)
                            ? <span>Pagamento: <strong>100% à confirmação da encomenda</strong></span>
                            : (() => {
                                // O 50/50 vem da ficha do fornecedor e nao sabe do
                                // acordo de sinal reduzido. Num cliente com esse
                                // acordo, esta linha prometia 50 por cento e o
                                // checkout, tres linhas abaixo, oferecia 8,3 — as
                                // duas coisas a contradizerem-se a vista, no
                                // momento em que ele esta a decidir pagar.
                                const valor = sinalPorUnidades ? sinalUnidadesTotal : sinalValor
                                const pct = sinalPorUnidades ? sinalUnidadesPct : sinalPctNum
                                if (sinalFlexivel && valor > 0 && carrinhoFinal > valor) {
                                  return (
                                    <span>
                                      Pagamento: <strong>{fmtPctSinal(pct)}% na compra</strong> ({fmt2(valor)} €)
                                      {' + '}<strong>o resto antes do envio</strong> ({fmt2(carrinhoFinal - valor)} €)
                                    </span>
                                  )
                                }
                                return <span>Pagamento: <strong>50% na compra</strong> + <strong>50% antes do envio</strong></span>
                              })()
                          }
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: C.success, fontWeight: '700' }}>
                          <Package size={13} style={{ color: C.success, flexShrink: 0, marginTop: '1px' }} />
                          <span>✓ <strong>PORTES DE ENVIO GRÁTIS</strong> — sem custos adicionais na expedição</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dados de Entrega */}
                  {carrinho.length > 0 && (() => {
                    const isLognow = moradaEscolhida === 'lognow'
                    const lockedInputStyle = { ...inputStyle, backgroundColor: '#f9f8f7', color: C.textMuted, cursor: 'not-allowed' }
                    const fieldStyle = isLognow ? lockedInputStyle : inputStyle
                    // Borda vermelha quando admin tentou submeter mas campo está vazio (e é obrigatório)
                    const errStyle = (vazio, obrigatorio) =>
                      (tentouSubmeter && !isLognow && obrigatorio && vazio)
                        ? { ...fieldStyle, borderColor: C.danger, borderWidth: '2px' }
                        : fieldStyle
                    return (
                    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Local de Entrega</p>

                      {/* Selector de morada */}
                      <div>
                        <label style={labelStyle}>Escolher destino</label>
                        <select
                          value={moradaEscolhida}
                          onChange={e => escolherMorada(e.target.value)}
                          style={{ ...inputStyle, height: '34px', cursor: 'pointer' }}
                        >
                          <option value="lognow">🏭 Armazéns LOGNOW (default — Amorim)</option>
                          {moradasGuardadas.map(m => (
                            <option key={m.id} value={m.id}>📦 {m.etiqueta} — {m.morada}</option>
                          ))}
                          <option value="manual">✏️ Outra morada (preencher manualmente)</option>
                        </select>
                        {isLognow && (
                          <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Info size={11} /> Quase todas as encomendas são entregues nos armazéns LOGNOW. Para outro destino, escolhe acima.
                          </p>
                        )}
                      </div>

                      {/* Nome + NIF */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={labelStyle}>Nome / Empresa *</label>
                          <input style={errStyle(!entrega.nome.trim(), true)} type="text" value={entrega.nome} placeholder="Nome completo ou empresa"
                            disabled={isLognow}
                            onChange={e => updEntrega('nome', e.target.value)} />
                        </div>
                        <div>
                          <label style={labelStyle}>NIF{isLognow ? '' : ' *'}</label>
                          <input style={errStyle(!entrega.nif.trim(), !isLognow)} type="text" value={entrega.nif} placeholder="Ex: 123456789" maxLength={9}
                            disabled={isLognow}
                            onChange={e => updEntrega('nif', e.target.value)} />
                        </div>
                      </div>

                      {/* Morada */}
                      <div>
                        <label style={labelStyle}>Morada *</label>
                        <input style={errStyle(!entrega.morada.trim(), true)} type="text" value={entrega.morada} placeholder="Rua, n.º, andar…"
                          disabled={isLognow}
                          onChange={e => updEntrega('morada', e.target.value)} />
                      </div>

                      {/* Código Postal + País */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={labelStyle}>Código Postal *</label>
                          <input style={errStyle(!entrega.cod_postal.trim(), true)} type="text" value={entrega.cod_postal} placeholder="Ex: 1000-001"
                            disabled={isLognow}
                            onChange={e => updEntrega('cod_postal', e.target.value)} />
                        </div>
                        <div>
                          <label style={labelStyle}>País *</label>
                          <input style={errStyle(!entrega.pais.trim(), true)} type="text" value={entrega.pais} placeholder="Portugal"
                            disabled={isLognow}
                            onChange={e => updEntrega('pais', e.target.value)} />
                        </div>
                      </div>

                      {/* Telefone */}
                      <div>
                        <label style={labelStyle}>Telefone{isLognow ? '' : ' *'}</label>
                        <input style={errStyle(!entrega.telefone.trim(), !isLognow)} type="tel" value={entrega.telefone} placeholder="Ex: +351 912 345 678"
                          disabled={isLognow}
                          onChange={e => updEntrega('telefone', e.target.value)} />
                      </div>
                    </div>
                    )
                  })()}

                  {/* Notas */}
                  {carrinho.length > 0 && (
                    <div>
                      <label style={labelStyle}>Notas / Observações <span style={{ fontWeight: 400, textTransform: 'none' }}>(opcional)</span></label>
                      <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} placeholder="Informações adicionais para a encomenda…"
                        style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  )}

                  {/* Lote do acordo — só admin. Independente do pagamento: uma conta
                      interna entra toda paga e pode na mesma pertencer ao acordo.
                      Ver supabase/sql/acordo_fixo.sql */}
                  {isAdmin && carrinho.length > 0 && (
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer',
                      background: acordoFixo ? '#fff8e1' : C.surfaceAlt,
                      border: `1px solid ${acordoFixo ? `${C.warning}66` : C.border}`,
                      borderRadius: '4px', padding: '10px 12px' }}>
                      <input type="checkbox" checked={acordoFixo}
                        onChange={e => setAcordoFixo(e.target.checked)}
                        style={{ accentColor: C.primary, marginTop: '1px', flexShrink: 0 }} />
                      <span>
                        <span style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: acordoFixo ? '#92400e' : C.text }}>
                          Encomenda do acordo (lote fixo)
                        </span>
                        <span style={{ display: 'block', fontSize: '11px', color: C.textMuted, lineHeight: 1.5, marginTop: '2px' }}>
                          Marca esta encomenda como parte do compromisso mensal com o fornecedor.
                          Serve para a distinguir de encomendas avulsas do mesmo artigo — sobretudo
                          em contas internas, onde o pagamento não as diferencia.
                        </span>
                        {/* Valor de REFERENCIA. Numa conta interna a encomenda entra toda
                            paga, portanto nao ha sinal nenhum a cobrar — mas o admin precisa
                            de saber quanto e que aquela fatia vale, porque ao fornecedor tem
                            de pagar na mesma. So se mostra; NAO e gravado em sinal_escolhido,
                            senao o Financeiro passava a dizer que o cliente pagou 8% quando
                            pagou tudo. */}
                        {acordoFixo && !sinalFlexivel && sinalMinimoPct > 0 && (
                          <span style={{ display: 'block', marginTop: '8px', paddingTop: '8px', borderTop: `1px dashed ${C.warning}66`, fontSize: '11.5px', color: C.text, lineHeight: 1.6 }}>
                            Referência: <strong>{fmtPctSinal(sinalMinimoPct)}%</strong> deste total ={' '}
                            <strong style={{ fontSize: '13px' }}>{fmt2(carrinhoFinal * sinalMinimoPct / 100)} €</strong>
                            <span style={{ display: 'block', color: C.textMuted, fontSize: '10.5px', marginTop: '2px' }}>
                              Só para teres o número — esta encomenda não cobra sinal.
                              {perfilEfectivo?.cliente_interno ? ' O total está a preço de custo.' : ''}
                            </span>
                          </span>
                        )}
                      </span>
                    </label>
                  )}

                  {/* Selector tipo de pagamento (apenas B2B — particulares pagam sempre 100%) */}
                  {carrinho.length > 0 && !isParticular && (
                    <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px' }}>
                      <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CreditCard size={13} color={C.primary} /> {fornecedorExigeTotal ? 'Pagamento' : 'Quanto queres pagar agora?'}
                      </p>
                      {/* Cliente TEM o acordo mas o carrinho tem artigos de fora.
                          Sem esta explicacao ele so ve o campo desaparecer e conclui
                          que avariou — em vez de perceber que basta separar a encomenda. */}
                      {/* Num fornecedor de pagamento total nao ha 50/50 nenhum
                          para o cliente perder, portanto explicar-lhe que "esta
                          encomenda segue as condicoes normais (50% + 50%)" so o
                          confunde — ainda por cima logo acima da caixa que lhe
                          diz que paga 100%. Duas explicacoes a dizer o
                          contrario uma da outra. */}
                      {clienteTemAcordo && !carrinhoTodoElegivel && !fornecedorExigeTotal && (
                        <div style={{ background: '#fff8e1', border: `1px solid ${C.warning}66`, borderRadius: '4px', padding: '9px 11px', marginBottom: '8px' }}>
                          <p style={{ margin: '0 0 4px', fontSize: '11.5px', color: '#92400e', fontWeight: '700' }}>
                            Condição especial não aplicável a este carrinho
                          </p>
                          <p style={{ margin: 0, fontSize: '11px', color: C.text, lineHeight: 1.5 }}>
                            {artigosForaDoAcordo.length === 1
                              ? <><strong>{artigosForaDoAcordo[0].Nome}</strong> não está abrangido.</>
                              : <><strong>{artigosForaDoAcordo.length} artigos</strong> não estão abrangidos: {artigosForaDoAcordo.map(i => i.Nome).join(', ')}.</>}
                            {' '}Esta encomenda segue as condições normais (50% + 50%). Para usares a condição especial, faz uma encomenda separada só com os produtos abrangidos.
                          </p>
                        </div>
                      )}
                      {/* Fornecedor que tem de ser pago por inteiro: a opcao dos
                          50% nem chega a aparecer. Explica-se porque — uma opcao
                          que desaparece sem explicacao parece uma avaria. */}
                      {/* Nao ha caixa a explicar o pagamento total: a lista de
                          Condicoes de Compra, no mesmo ecra logo acima, ja diz
                          "Pagamento: 100% a confirmacao da encomenda" e o prazo
                          de 2 dias. Repeti-lo aqui era dizer duas vezes a mesma
                          coisa, e a segunda com ar de aviso. */}
                      <div style={{ display: 'grid', gridTemplateColumns: fornecedorExigeTotal ? '1fr' : '1fr 1fr', gap: '8px' }}>
                        <label style={{ cursor: 'pointer', padding: '10px 12px', border: `2px solid ${tipoPagamento === '50' ? C.primary : C.border}`, borderRadius: '4px', background: tipoPagamento === '50' ? C.surface : C.surface, display: fornecedorExigeTotal ? 'none' : 'flex', flexDirection: 'column', gap: '4px', boxShadow: tipoPagamento === '50' ? '0 0 0 2px rgba(10,10,10,0.06)' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="tipoPagamento" value="50" checked={tipoPagamento === '50'} onChange={() => setTipoPagamento('50')} style={{ accentColor: C.primary }} />
                            <span style={{ fontSize: '12px', fontWeight: '700', color: C.text }}>{sinalFlexivel ? 'Pagar agora' : '50% agora'}</span>
                          </div>
                          {sinalPorUnidades ? (
                            <>
                              {/* Sem campo de percentagem: o valor e imposto pelo numero
                                  inteiro de artigos, senao nao havia o que facturar. */}
                              <div style={{ fontSize: '16px', fontWeight: '800', color: C.success, fontVariantNumeric: 'tabular-nums' }}>
                                {fmt2(sinalUnidadesTotal)} €
                              </div>
                              <div style={{ fontSize: '10px', color: C.textMuted, lineHeight: 1.5 }}>
                                {linhasSinalUnidades.map((l, i) => (
                                  <div key={i}><strong style={{ color: C.text }}>{l.unidades} un</strong> · {l.nome}</div>
                                ))}
                                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: `1px dashed ${C.border}` }}>
                                  {fmtPctSinal(sinalUnidadesPct)}% do total · artigos inteiros, para poder ser facturado
                                </div>
                                <div style={{ marginTop: '3px' }}>
                                  2.ª prestação antes do envio: <strong style={{ color: C.text }}>{fmt2(carrinhoFinal - sinalUnidadesTotal)} €</strong>
                                </div>
                              </div>
                            </>
                          ) : sinalFlexivel ? (
                            <>
                              {/* O cliente escreve a %, mas o que conta e o valor em euros
                                  logo por baixo — e esse que ele transfere e esse que fica
                                  gravado. Evita a discussao dos centimos. */}
                              {/* stopPropagation (e nao preventDefault): impede o clique de
                                  chegar ao <label> e activar o radio, mas deixa o input
                                  funcionar normalmente — setas, teclado e selecao. */}
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                                <input
                                  type="number" inputMode="decimal" step="0.5" min={sinalMinimoPct} max="99.99"
                                  value={sinalPct}
                                  onChange={e => { setSinalPct(e.target.value); setTipoPagamento('50') }}
                                  onFocus={() => setTipoPagamento('50')}
                                  placeholder={String(sinalMinimoPct)}
                                  style={{ width: '100%', height: '34px', padding: '0 26px 0 9px', border: `1px solid ${sinalPct !== '' && !sinalPctValida ? C.danger : C.border}`, borderRadius: '3px', fontSize: '15px', fontWeight: '700', color: C.text, outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }} />
                                <span style={{ position: 'absolute', right: '9px', fontSize: '13px', fontWeight: '700', color: C.textMuted, pointerEvents: 'none' }}>%</span>
                              </div>
                              <div style={{ fontSize: '16px', fontWeight: '800', color: sinalPctValida ? C.success : C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                                {sinalPctValida ? `= ${fmt2(sinalValor)} €` : '—'}
                              </div>
                              <div style={{ fontSize: '10px', color: sinalAbaixoDoMinimo ? C.danger : C.textMuted, lineHeight: 1.4, fontWeight: sinalAbaixoDoMinimo ? '700' : '400' }}>
                                {sinalAbaixoDoMinimo
                                  ? <>Mínimo {fmtPctSinal(sinalMinimoPct)}% — podes pagar mais, não menos.</>
                                  : sinalPctValida
                                    ? <>2.ª prestação antes do envio: <strong style={{ color: C.text }}>{fmt2(sinalSegunda)} €</strong></>
                                    : `Mínimo ${fmtPctSinal(sinalMinimoPct)}% — podes pagar mais se quiseres`}
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: '16px', fontWeight: '800', color: C.success, fontVariantNumeric: 'tabular-nums' }}>{fmt2(carrinhoMetade)} €</div>
                              <div style={{ fontSize: '10px', color: C.textMuted, lineHeight: 1.4 }}>1ª prestação · 2ª prestação antes do envio</div>
                            </>
                          )}
                        </label>
                        <label style={{ cursor: 'pointer', padding: '10px 12px', border: `2px solid ${tipoPagamento === '100' ? C.primary : C.border}`, borderRadius: '4px', background: tipoPagamento === '100' ? C.surface : C.surface, display: 'flex', flexDirection: 'column', gap: '4px', boxShadow: tipoPagamento === '100' ? '0 0 0 2px rgba(10,10,10,0.06)' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Com uma opcao unica o radio nao serve para nada:
                                nao ha escolha a fazer, e um circulo por marcar
                                parece uma coisa por fazer. */}
                            {!fornecedorExigeTotal && (
                              <input type="radio" name="tipoPagamento" value="100" checked={tipoPagamento === '100'} onChange={() => setTipoPagamento('100')} style={{ accentColor: C.primary }} />
                            )}
                            <span style={{ fontSize: '12px', fontWeight: '700', color: C.text }}>100% agora</span>
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: C.info, fontVariantNumeric: 'tabular-nums' }}>{fmt2(carrinhoFinal)} €</div>
                          <div style={{ fontSize: '10px', color: C.textMuted, lineHeight: 1.4 }}>Pagamento único · sem 2ª transferência</div>
                        </label>
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>
                        {/* "os dois IBANs" pressupoe duas opcoes. Com pagamento
                            total ha uma so, e nao ha nada sobre que mudar de
                            ideia — a frase deixa de fazer sentido. */}
                        {sinalFlexivel
                          ? <>Transfere <strong>exactamente o valor em euros</strong> mostrado acima. A 2.ª prestação é sempre o resto — pagas antes do envio.</>
                          : fornecedorExigeTotal
                            ? <>Vais receber o <strong>IBAN e o valor</strong> por email.</>
                            : <>Vais receber email com <strong>os dois IBANs/valores</strong> — podes mudar de ideia ao fazer a transferência.</>}
                      </p>
                    </div>
                  )}

                  {/* Quase la — o unico aviso que se interpoe mesmo.
                      Alguem a 2.410 EUR que submete sem saber perde o desconto
                      por causa de noventa euros, e nos perdemos a venda maior.
                      E e este o caso que gera a pergunta a seguir: "afinal
                      porque e que nao tive desconto?". */}
                  {incentivoInfo?.quaseLa && (
                    <div style={{
                      background: '#fef3c7', border: `2px solid ${C.warning}`, borderRadius: '6px',
                      padding: '11px 13px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px',
                    }}>
                      <span style={{ fontSize: '20px' }}>🎯</span>
                      <div style={{ flex: 1, fontSize: '12.5px', color: C.text, lineHeight: 1.45 }}>
                        Faltam <strong>{fmt2(incentivoInfo.falta)} €</strong> para ganhares{' '}
                        <strong>{incentivoInfo.pct}% de desconto</strong> — poupavas{' '}
                        <strong>{fmt2(carrinhoTotalSemIva * incentivoInfo.pct / 100 * (1 + IVA))} €</strong> nesta encomenda.
                      </div>
                    </div>
                  )}

                  {/* Botões */}
                  {carrinho.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={fecharCarrinho} style={{ flex: 1, height: '38px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '13px', color: C.textMuted }}>Cancelar</button>
                      {/* Sem percentagem valida nao da para submeter: senao a encomenda
                          era criada sem sinal e caia no 50/50 sem o cliente perceber. */}
                      {(() => {
                        const faltaSinal = sinalFlexivel && tipoPagamentoEfectivo !== '100' && !sinalPctValida
                        const inativo = enviando || faltaSinal || abaixoDoMinimo
                        return (
                      <button onClick={submeterEncomenda} disabled={inativo}
                        style={{ flex: 2, height: '38px', border: 'none', borderRadius: '2px', backgroundColor: C.success, color: '#fff', cursor: inativo ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', opacity: inativo ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        {enviando ? (
                          <><Loader size={13} /> A enviar...</>
                        ) : abaixoDoMinimo ? (
                          `Mínimo ${fmt2(minEncomenda)} € s/IVA — faltam ${fmt2(minEncomenda - carrinhoTotalSemIva)} €`
                        ) : faltaSinal ? (
                          sinalAbaixoDoMinimo
                            ? `Mínimo ${fmtPctSinal(sinalMinimoPct)}% — ajusta o valor`
                            : 'Escreve a percentagem a pagar agora'
                        ) : (
                          <>
                            <ChevronRight size={14} />
                            {isParticular
                              ? `Finalizar encomenda (${fmt2(carrinhoFinal)} €)`
                              : tipoPagamentoEfectivo === '100'
                                ? `Pagar 100% (${fmt2(carrinhoFinal)} €)`
                                : sinalPorUnidades
                                  ? `Pagar ${linhasSinalUnidades.reduce((s, l) => s + l.unidades, 0)} un (${fmt2(sinalUnidadesTotal)} €)`
                                  : sinalFlexivel
                                    ? `Pagar ${sinalPctNum}% (${fmt2(sinalValor)} €)`
                                    : `Pagar 50% (${fmt2(carrinhoMetade)} €)`}
                          </>
                        )}
                      </button>
                        )
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* ===== ECRÃ PÓS-COMPRA ===== */}
              {encomendaFinalizada && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                  {/* Cabeçalho sucesso */}
                  <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                    <CheckCircle size={40} color={C.success} style={{ marginBottom: '8px' }} />
                    <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '700', color: C.text }}>Encomenda registada!</h3>
                    <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>Receberá email de confirmação em breve.</p>
                    {/* Nº da encomenda — é a referência a pôr na descrição da transferência.
                        Faltava aqui, e é exactamente onde o cliente precisa dela. */}
                    {encomendaFinalizada.numero && (
                      <div style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: C.surfaceAlt, border: `1px solid ${C.primary}44`, borderRadius: '6px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nº Encomenda</span>
                        <span style={{ fontSize: '15px', fontWeight: '800', color: C.primary, fontFamily: 'monospace', letterSpacing: '0.5px' }}>{encomendaFinalizada.numero}</span>
                        <CopyButton text={encomendaFinalizada.numero} size={13} title="Copiar nº da encomenda" />
                      </div>
                    )}
                  </div>

                  {/* Blocos de pagamento - adaptados ao tipoPagamento escolhido */}
                  {encomendaFinalizada.tipoPagamento === '100' ? (
                    /* Pagamento Total 100% */
                    <div style={{ background: C.infoSoft, border: `2px solid ${C.info}`, borderRadius: '6px', padding: '16px 14px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '10px', fontWeight: '700', color: C.info, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pagamento Total (100%)</p>
                      <p style={{ margin: '0 0 2px', fontSize: '28px', fontWeight: '800', color: C.info }}>{fmt2(encomendaFinalizada.total)} €</p>
                      <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.info, fontWeight: '600' }}>Pagamento único · sem 2ª prestação</p>
                      <p style={{ margin: 0, fontSize: '11px', color: C.text, lineHeight: '1.5' }}>
                        Resolve tudo numa só transferência — não precisa de 2ª prestação antes do envio.
                        <br /><span style={{ color: C.textMuted, fontStyle: 'italic' }}>Se preferires pagar {sinalFlexivel ? 'só uma parte' : 'só 50%'}, vais receber email com o IBAN para a 1ª prestação também.</span>
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      {/* 1.ª Prestação */}
                      <div style={{ background: C.successSoft, border: '2px solid #107c10', borderRadius: '6px', padding: '14px 12px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 4px', fontSize: '10px', fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>1.ª Prestação</p>
                        <p style={{ margin: '0 0 2px', fontSize: '24px', fontWeight: '700', color: C.success }}>{fmt2(encomendaFinalizada.metade)} €</p>
                        <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.success, fontWeight: '600' }}>
                          {encomendaFinalizada.sinalPct != null ? `${encomendaFinalizada.sinalPct}% do valor total` : '50% do valor total'}
                        </p>
                        <p style={{ margin: 0, fontSize: '11px', color: C.text, lineHeight: '1.5' }}>
                          Pagar <strong>agora</strong> para confirmar a encomenda.
                          Prazo: <strong>imediato</strong>.
                        </p>
                      </div>
                      {/* 2.ª Prestação */}
                      <div style={{ background: C.warningSoft, border: '2px solid #c07400', borderRadius: '6px', padding: '14px 12px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 4px', fontSize: '10px', fontWeight: '700', color: C.warning, textTransform: 'uppercase', letterSpacing: '0.5px' }}>2.ª Prestação</p>
                        {/* Sempre total menos a 1.ª — nunca "outra metade". Com sinal
                            flexivel a 2.ª e o resto todo, e a soma tem de bater ao centimo. */}
                        <p style={{ margin: '0 0 2px', fontSize: '24px', fontWeight: '700', color: C.warning }}>{fmt2(Math.round((encomendaFinalizada.total - encomendaFinalizada.metade) * 100) / 100)} €</p>
                        <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.warning, fontWeight: '600' }}>
                          {encomendaFinalizada.sinalPct != null ? `${Math.round((100 - encomendaFinalizada.sinalPct) * 100) / 100}% restantes` : '50% restantes'}
                        </p>
                        <p style={{ margin: 0, fontSize: '11px', color: C.text, lineHeight: '1.5' }}>
                          Pagar <strong>antes do envio</strong>.
                          <br />Portes de envio <strong>grátis</strong>.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Dados bancários */}
                  {(iban || nib) && (
                    <div style={{ background: C.surfaceAlt, border: `1px solid #c7e0f4`, borderRadius: '4px', padding: '12px 14px' }}>
                      <p style={{ margin: '0 0 10px', fontSize: '11px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <CreditCard size={12} /> Dados para Transferência Bancária
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {iban && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', minWidth: '60px' }}>IBAN</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, fontFamily: 'monospace', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                              {iban}
                              <CopyButton text={iban} size={13} title="Copiar IBAN" />
                            </span>
                          </div>
                        )}
                        {nib && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', minWidth: '60px' }}>NIB</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                              {nib}
                              <CopyButton text={nib} size={13} title="Copiar NIB" />
                            </span>
                          </div>
                        )}
                        {/* Valor a transferir agora — poupa erros de digitação no home banking */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginTop: '4px', paddingTop: '6px', borderTop: `1px solid ${C.border}` }}>
                          <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', minWidth: '60px' }}>A transferir</span>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            {fmt2(encomendaFinalizada.tipoPagamento === '100' ? encomendaFinalizada.total : encomendaFinalizada.metade)} €
                            <CopyButton
                              text={String((encomendaFinalizada.tipoPagamento === '100' ? encomendaFinalizada.total : encomendaFinalizada.metade).toFixed(2))}
                              size={13}
                              title="Copiar valor"
                            />
                          </span>
                        </div>
                        {encomendaFinalizada.numero && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', minWidth: '60px' }}>Descrição</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                              {encomendaFinalizada.numero}
                              <CopyButton text={encomendaFinalizada.numero} size={13} title="Copiar descrição" />
                            </span>
                          </div>
                        )}
                        {bancoNome && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', minWidth: '60px' }}>Banco</span>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{bancoNome}</span>
                          </div>
                        )}
                        {bancoTitular && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', minWidth: '60px' }}>Titular</span>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{bancoTitular}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Upload comprovativo */}
                  <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Upload size={13} color={C.primary} /> Comprovativo de Pagamento ({encomendaFinalizada.tipoPagamento === '100' ? '100% — pagamento total' : '1.ª prestação'})
                    </p>
                    <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted }}>Opcional mas recomendado — acelera a confirmação da sua encomenda.</p>
                    {/* Mesma mecânica de "Minhas Encomendas": o input nativo fica escondido
                        (mostrava "Choose file / No file chosen", em inglês) e o ficheiro sobe
                        assim que é escolhido — sem segundo botão para carregar. */}
                    <label style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      padding: '12px 14px', border: `2px dashed ${uploadMsg.tipo === 'ok' ? C.success : C.primary}`,
                      borderRadius: '4px', background: uploadMsg.tipo === 'ok' ? C.successSoft : C.surfaceAlt,
                      cursor: uploadando ? 'not-allowed' : 'pointer',
                      color: uploadMsg.tipo === 'ok' ? C.success : C.primary,
                      fontWeight: '700', fontSize: '13px', opacity: uploadando ? 0.6 : 1,
                    }}>
                      {uploadando
                        ? <><Loader size={14} /> A enviar comprovativo…</>
                        : uploadMsg.tipo === 'ok'
                          ? <><CheckCircle size={14} /> Comprovativo enviado — escolher outro</>
                          : <><Upload size={14} /> Anexar comprovativo de pagamento</>}
                      <input type="file" accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        disabled={uploadando}
                        onChange={async (ev) => {
                          const ficheiro = ev.target.files?.[0]
                          ev.target.value = ''   // permite reenviar o mesmo ficheiro
                          if (!ficheiro || !encomendaFinalizada?.id) return
                          setComprovativo(ficheiro)
                          setUploadando(true)
                          setUploadMsg({ tipo: '', texto: '' })
                          try {
                            const isTotal = encomendaFinalizada.tipoPagamento === '100'
                            const prestacaoTipo = isTotal ? 'total' : '1a'
                            const ext = ficheiro.name.split('.').pop().toLowerCase()
                            const path = `${user.id}/${encomendaFinalizada.id}-${prestacaoTipo}.${ext}`
                            const { error: upErr } = await supabase.storage.from('comprovativos').upload(path, ficheiro, { upsert: true, contentType: ficheiro.type })
                            if (upErr) throw new Error(upErr.message)
                            const updatePayload = isTotal
                              ? { comprovativo_1a_url: path, comprovativo_1a_em: new Date().toISOString(), comprovativo_2a_url: path, comprovativo_2a_em: new Date().toISOString() }
                              : { comprovativo_1a_url: path, comprovativo_1a_em: new Date().toISOString() }
                            await supabase.from('encomendas').update(updatePayload).eq('id', encomendaFinalizada.id)
                            // Notificar admin (silencioso em caso de falha)
                            try {
                              const { data: { session } } = await supabase.auth.getSession()
                              await fetch(`${SUPABASE_URL}/functions/v1/notificar-comprovativo`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                                body: JSON.stringify({ encomenda_id: encomendaFinalizada.id, prestacao: prestacaoTipo }),
                              })
                            } catch (_) { /* silent */ }
                            setUploadMsg({ tipo: 'ok', texto: `"${ficheiro.name}" enviado. O administrador foi notificado.` })
                            setComprovativo(null)
                          } catch (err) {
                            setUploadMsg({ tipo: 'erro', texto: 'Erro ao enviar: ' + err.message })
                          }
                          setUploadando(false)
                        }} />
                    </label>
                    {uploadMsg.texto && (
                      <div style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '2px', backgroundColor: uploadMsg.tipo === 'ok' ? C.successSoft : C.dangerSoft, color: uploadMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {uploadMsg.tipo === 'ok' ? <CheckCircle size={12} /> : <AlertCircle size={12} />} {uploadMsg.texto}
                      </div>
                    )}
                  </div>

                  <button onClick={fecharCarrinho}
                    style={{ width: '100%', height: '38px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: C.textMuted }}>
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL SELEÇÃO DE QUANTIDADE ===== */}
      {modalQuantidade && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '400px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.sidebar }}>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plus size={15} /> Selecionar Quantidade
              </h2>
              <button onClick={() => setModalQuantidade(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '2px', cursor: 'pointer', color: '#fff', padding: '6px', display: 'flex' }}><X size={16} /></button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: C.text }}>{modalQuantidade.produto.Nome}</p>
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: C.textMuted }}>Quantidade mínima: {modalQuantidade.qtdMin} unidades</p>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quantidade</label>
                {(() => {
                  const valorNumero = Number(quantidadeSelecionada) || 0
                  const abaixoMin = valorNumero > 0 && valorNumero < modalQuantidade.qtdMin
                  const vazio = !quantidadeSelecionada || valorNumero <= 0
                  return (
                    <>
                      <input
                        type="number"
                        min={modalQuantidade.qtdMin}
                        value={quantidadeSelecionada}
                        onChange={e => setQuantidadeSelecionada(e.target.value)}
                        onBlur={e => {
                          // No blur, garantir mínimo (mas permitir edição livre durante typing)
                          const v = Number(e.target.value) || 0
                          if (v < modalQuantidade.qtdMin) setQuantidadeSelecionada(modalQuantidade.qtdMin)
                        }}
                        style={{ width: '100%', height: '40px', padding: '0 12px', border: `1px solid ${(abaixoMin || vazio) ? C.danger : C.border}`, borderRadius: '2px', fontSize: '16px', fontWeight: '600', color: C.text, outline: 'none', boxSizing: 'border-box' }}
                      />
                      {abaixoMin && (
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.danger, fontWeight: '600' }}>
                          ⚠️ Mínimo: {modalQuantidade.qtdMin} un. — será ajustado para {modalQuantidade.qtdMin}.
                        </p>
                      )}
                      {vazio && (
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.danger, fontWeight: '600' }}>
                          ⚠️ Quantidade obrigatória (mínimo {modalQuantidade.qtdMin} un.).
                        </p>
                      )}
                    </>
                  )
                })()}
                {(() => {
                  // Aplicar diferencial de portes do cliente actual
                  const precoBase = isParticular
                    ? Number(modalQuantidade.produto.preco_particular || 0)
                    : Number(modalQuantidade.produto.preco) + portesDiff
                  const precoTotalSemIva = precoBase * quantidadeSelecionada
                  const precoTotalComIva = precoTotalSemIva * (1 + IVA)
                  return (
                    <div style={{ marginTop: '12px', padding: '10px 12px', background: C.surfaceAlt, borderRadius: '3px', border: `1px solid ${C.primary}33` }}>
                      <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '4px' }}>Cálculo:</div>
                      <div style={{ fontSize: '13px', color: C.text, marginBottom: '2px' }}>
                        {quantidadeSelecionada} × {fmt2(precoBase)} € = {fmt2(precoTotalSemIva)} € s/ IVA
                      </div>
                      <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
                        Total c/ IVA: {fmt2(precoTotalComIva)} €
                      </div>
                    </div>
                  )
                })()}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModalQuantidade(null)}
                  style={{ flex: 1, height: '38px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: C.textMuted }}>
                  Cancelar
                </button>
                <button onClick={() => {
                  const qty = Math.max(modalQuantidade.qtdMin, Number(quantidadeSelecionada) || 0)
                  adicionarAoCarrinho(modalQuantidade.produto, qty)
                  setModalQuantidade(null)
                }}
                  style={{ flex: 1, height: '38px', border: 'none', borderRadius: '2px', backgroundColor: C.primary, cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#fff' }}>
                  Adicionar ao Carrinho
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {novidadesAbertas && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 760, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
             onClick={() => setNovidadesAbertas(false)}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: C.surface, borderRadius: '14px', width: '100%', maxWidth: '420px', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ padding: '24px 28px', background: C.sidebar, color: C.sidebarText, textAlign: 'center' }}>
              <div style={{ width: '56px', height: '56px', margin: '0 auto 12px', borderRadius: '50%', background: C.success, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles size={28} />
              </div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                {novoIds.size} artigo{novoIds.size === 1 ? '' : 's'} novo{novoIds.size === 1 ? '' : 's'}
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: '13px', opacity: 0.85 }}>
                {ultimaVisita
                  ? `Desde a tua ultima visita, a ${new Date(ultimaVisita).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}.`
                  : 'Entraram no catalogo nos ultimos dias.'}
              </p>
            </div>
            <div style={{ padding: '18px 28px 22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => { setAbaCategoria('novos'); setSubFiltro(''); setSearch(''); setQuantosMostrar(LOTE_CARTOES); setNovidadesAbertas(false) }}
                      style={{ width: '100%', height: '42px', border: 'none', borderRadius: '8px', background: C.primary, color: C.primaryText, cursor: 'pointer', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                Ver so os novos <ChevronRight size={15} />
              </button>
              <button onClick={() => setNovidadesAbertas(false)}
                      style={{ width: '100%', height: '36px', border: 'none', borderRadius: '8px', background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                Agora nao
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edição em massa de preços (admin) */}
      {isAdmin && (
        <EdicaoMassaPrecos
          open={showEdicaoMassa}
          onClose={() => setShowEdicaoMassa(false)}
          onUpdated={() => loadCatalogo()}
        />
      )}
    </div>
  )
}
